// frontend/dispatcher-client/src/components/maps/LiveTransportsMap.js

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { flushSync } from "react-dom";
import { useLocation } from "react-router-dom";

import maplibregl from "maplibre-gl";


import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
} from "@dnd-kit/core";


import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";

 
import {
  ensureRoadReferenceOverlay,
} from "./roadReferenceOverlay";

import {
  AlertCircle,
  AlertTriangle,
  Box,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Container,
  FileText,
  MapPin,
  RefreshCw,
  Route,
  List,
  SlidersHorizontal,
  Search,
  Truck,
  User,
  X,
} from "lucide-react";

import { useTranslation } from "react-i18next";

import api from "../../axiosConfig";

import {
  getSocket,
} from "../../lib/socket";

import {
  getStoredToken,
  getStoredUser,
} from "../../lib/authStorage";

import DriverRoutePlannerMap from "./DriverRoutePlannerMap";
import DriverSelectorModal from "../DriverSelectorModal";
import TruckSelectorModal from "../TruckSelectorModal";
import TrailerSelectorModal from "../TrailerSelectorModal";
import StatusSelectorModal from "../StatusSelectorModal";
import CMRLoadModal from "../CMRLoadModal";
import ModalPortal from "../ModalPortal";

import MapReorderScheduleConflict, {
  getMapScheduleConflict,
  getMapScheduleSignature,
} from "./MapReorderScheduleConflict";
import pickupPinImage from "../../assets/cargaazul_optimized_92.webp";
import deliveryPinImage from "../../assets/descargavermelha_optimized_92.webp";

import "maplibre-gl/dist/maplibre-gl.css";
import "../../styles/MapBase.css";
import "../../styles/LiveTransportsMap.css";

// Antecipar carregamento e descodificação dos pins
// e dos ícones otimizados de camião e reboque.
if (typeof window !== "undefined") {
  [
    pickupPinImage,
    deliveryPinImage,
    "/icons/truck-optimized.webp",
    "/icons/trailer-optimized.webp",
  ].forEach((src) => {
    const img = new window.Image();

    img.decoding = "async";
    img.src = src;

    if (typeof img.decode === "function") {
      img.decode().catch(() => {});
    }
  });
}

const MAP_STYLE =
  "/maps/styles/loadevo-road.json?v=5";

const DEFAULT_CENTER = [
  -8.61099,
  41.14961,
];

const DEFAULT_ZOOM = 5;

const ROUTES_SOURCE_ID =
  "live-transports-routes";

const ROUTES_CASING_LAYER_ID =
  "live-transports-routes-casing";

const ROUTES_LINE_LAYER_ID =
  "live-transports-routes-line";

const POINTS_SOURCE_ID =
  "live-transports-points";

const POINTS_CLUSTER_LAYER_ID =
  "live-transports-points-cluster";

const POINTS_CLUSTER_COUNT_LAYER_ID =
  "live-transports-points-cluster-count";

const POINTS_HALO_LAYER_ID =
  "live-transports-points-halo";

const POINTS_CIRCLE_LAYER_ID =
  "live-transports-points-circle";

const POINTS_LABEL_LAYER_ID =
  "live-transports-points-label";

const FINAL_STATUSES = new Set([
  "COMPLETED",
  "CANCELLED",
]);
 
const LIVE_SESSIONS_CACHE_PREFIX =
  "loadevo.map.live-sessions.v4.";

const LIVE_SESSIONS_CACHE_SOFT_TTL_MS =
  10 * 60 * 1000;

const LIVE_SESSIONS_CACHE_HARD_TTL_MS =
  24 * 60 * 60 * 1000;

function decodeLiveMapJwt(
  token
) {
  try {
    const payloadPart =
      String(
        token || ""
      ).split(".")[1];

    if (!payloadPart) {
      return null;
    }

    const normalized =
      payloadPart
        .replace(
          /-/g,
          "+"
        )
        .replace(
          /_/g,
          "/"
        );

    const padded =
      normalized +
      "=".repeat(
        (
          4 -
          (
            normalized.length %
            4
          )
        ) %
          4
      );

    return JSON.parse(
      window.atob(
        padded
      )
    );
  } catch {
    return null;
  }
}

function resolveLiveMapCompanyId() {
  try {
    const token =
      getStoredToken() ||
      window.localStorage.getItem(
        "authToken"
      ) ||
      "";

    const payload =
      token
        ? decodeLiveMapJwt(
            token
          )
        : null;

    const fromJwt =
      payload?.company_id ??
      payload?.companyId ??
      null;

    if (
      Number.isFinite(
        Number(
          fromJwt
        )
      ) &&
      Number(
        fromJwt
      ) > 0
    ) {
      return Number(
        fromJwt
      );
    }

    const direct =
      window.sessionStorage.getItem(
        "companyId"
      ) ||
      window.sessionStorage.getItem(
        "company_id"
      ) ||
      window.localStorage.getItem(
        "companyId"
      ) ||
      window.localStorage.getItem(
        "company_id"
      ) ||
      null;

    if (
      Number.isFinite(
        Number(
          direct
        )
      ) &&
      Number(
        direct
      ) > 0
    ) {
      return Number(
        direct
      );
    }

    const storedUser =
      getStoredUser();

    const fromUser =
      storedUser?.company_id ??
      storedUser?.companyId ??
      null;

    if (
      Number.isFinite(
        Number(
          fromUser
        )
      ) &&
      Number(
        fromUser
      ) > 0
    ) {
      return Number(
        fromUser
      );
    }
  } catch {
    // Sem company context.
  }

  return null;
}

function getLiveSessionsCacheKey() {
  const companyId =
    resolveLiveMapCompanyId();

  if (!companyId) {
    return null;
  }

  return (
    LIVE_SESSIONS_CACHE_PREFIX +
    `company:${companyId}`
  );
}

function readLiveSessionsCache(
  cacheKey
) {
  if (!cacheKey) {
    return null;
  }

  try {
    const raw =
      window.localStorage.getItem(
        cacheKey
      );

    if (!raw) {
      return null;
    }

    const parsed =
      JSON.parse(raw);

    const savedAt =
      Number(
        parsed?.savedAt
      );

    if (
      !Number.isFinite(
        savedAt
      ) ||
      !Array.isArray(
        parsed?.sessions
      )
    ) {
      window.localStorage.removeItem(
        cacheKey
      );

      return null;
    }

    const age =
      Date.now() -
      savedAt;

    /*
     * SWR:
     * Até 10 min = fresh.
     * 10 min → 24 h = mostramos imediatamente,
     * mas validamos em background.
     * > 24 h = demasiado antigo para mostrar.
     */
    if (
      age >
      LIVE_SESSIONS_CACHE_HARD_TTL_MS
    ) {
      window.localStorage.removeItem(
        cacheKey
      );

      return null;
    }

    return {
      sessions:
        parsed.sessions,

      lastUpdated:
        parsed.lastUpdated ||
        new Date(
          savedAt
        ).toISOString(),

      loadsRev:
        Number.isFinite(
          Number(
            parsed.loadsRev
          )
        )
          ? Number(
              parsed.loadsRev
            )
          : null,

      stale:
        age >
        LIVE_SESSIONS_CACHE_SOFT_TTL_MS,
    };
  } catch (
    error
  ) {
    console.warn(
      "[LiveTransportsMap] Invalid cache:",
      error
    );

    return null;
  }
}

function writeLiveSessionsCache(
  cacheKey,
  {
    sessions,
    lastUpdated,
    loadsRev = null,
  }
) {
  if (
    !cacheKey ||
    !Array.isArray(
      sessions
    )
  ) {
    return;
  }

  try {
    window.localStorage.setItem(
      cacheKey,
      JSON.stringify({
        version:
          4,

        savedAt:
          Date.now(),

        lastUpdated:
          lastUpdated instanceof Date
            ? lastUpdated.toISOString()
            : lastUpdated ||
              new Date().toISOString(),

        loadsRev:
          Number.isFinite(
            Number(
              loadsRev
            )
          )
            ? Number(
                loadsRev
              )
            : null,

        sessions,
      })
    );
  } catch (
    error
  ) {
    console.warn(
      "[LiveTransportsMap] Cache write failed:",
      error?.message
    );
  }
}

function getArrayPayload(data) {
  if (Array.isArray(data)) {
    return data;
  }

  const candidates = [
    data?.items,
    data?.rows,
    data?.loads,
    data?.sessions,
    data?.pickupDeliveries,
    data?.data,
  ];

  const found =
    candidates.find(Array.isArray);

  if (found) {
    return found;
  }

  if (
    data &&
    typeof data === "object"
  ) {
    const nestedArrays =
      Object.values(data).filter(
        Array.isArray
      );

    if (nestedArrays.length) {
      return nestedArrays.flat();
    }
  }

  return [];
}

function parseMaybeJson(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return null;
  }

  if (typeof value === "object") {
    return value;
  }

  if (typeof value !== "string") {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function toFiniteNumberOrNull(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  const number = Number(
    String(value).replace(",", ".")
  );

  return Number.isFinite(number)
    ? number
    : null;
}

function getTransportSessionId(row) {
  const rawId =
    row?.transportsessionId ??
    row?.transportSessionId ??
    row?.transportsession_id ??
    row?.transport_session_id ??
    row?.transportSession?.id ??
    null;

  const id = Number(rawId);

  return Number.isInteger(id) &&
    id > 0
    ? id
    : null;
}

function normalizeLatLngPair(
  firstValue,
  secondValue
) {
  const first =
    toFiniteNumberOrNull(
      firstValue
    );

  const second =
    toFiniteNumberOrNull(
      secondValue
    );

  if (
    first === null ||
    second === null
  ) {
    return null;
  }

  /*
   * Formato habitual da aplicação:
   * latitude, longitude
   *
   * Exemplo:
   * 41.14961, -8.61099
   */
  if (
    Math.abs(first) <= 90 &&
    Math.abs(second) <= 180
  ) {
    return {
      lat: first,
      lng: second,
    };
  }

  /*
   * Fallback para GeoJSON:
   * longitude, latitude
   */
  if (
    Math.abs(first) <= 180 &&
    Math.abs(second) <= 90
  ) {
    return {
      lat: second,
      lng: first,
    };
  }

  return null;
}

function normalizeCoordinates(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  /*
   * Quando existem campos com nomes
   * explícitos, são sempre prioritários.
   */
  if (
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
  ) {
    const directLat =
      value.lat ??
      value.latitude ??
      value.placeLatitude ??
      value.latestStatusEvent
        ?.placeLatitude ??
      null;

    const directLng =
      value.lng ??
      value.lon ??
      value.longitude ??
      value.placeLongitude ??
      value.latestStatusEvent
        ?.placeLongitude ??
      null;

    const directCoordinates =
      normalizeLatLngPair(
        directLat,
        directLng
      );

    if (directCoordinates) {
      return directCoordinates;
    }
  }

  const parsed =
    parseMaybeJson(value);

  if (
    parsed &&
    parsed !== value
  ) {
    return normalizeCoordinates(
      parsed
    );
  }

  if (
    Array.isArray(value) &&
    value.length >= 2
  ) {
    return normalizeLatLngPair(
      value[0],
      value[1]
    );
  }

  if (
    value &&
    typeof value === "object"
  ) {
    return normalizeLatLngPair(
      value.lat ??
        value.latitude ??
        value[0],

      value.lng ??
        value.lon ??
        value.longitude ??
        value[1]
    );
  }

  const matches =
    String(value)
      .trim()
      .match(
        /-?\d+(?:\.\d+)?/g
      );

  if (
    !matches ||
    matches.length < 2
  ) {
    return null;
  }

  return normalizeLatLngPair(
    matches[0],
    matches[1]
  );
}

function normalizePoint(row, index) {
  const coordinates =
    normalizeCoordinates(
      row?.coordinates
    ) ||
    normalizeCoordinates(row);

  if (!coordinates) {
    return null;
  }

  const type = String(
    row?.type ||
    row?.pointType ||
    "STRATEGICPOINT"
  )
    .trim()
    .toUpperCase();

  return {
    id:
      row?.id ??
      `point-${index}`,

    transportsessionId:
      getTransportSessionId(row),

    sequence:
      Number(row?.sequence) ||
      index + 1,

    type,

    status:
      String(row?.status || "")
        .trim()
        .toUpperCase(),

    name:
      row?.location ||
      row?.name ||
      row?.city ||
      null,

    address:
      row?.address ||
      null,

    city:
      row?.city ||
      row?.placeCity ||
      null,

    country:
      row?.country ||
      row?.countryName ||
      row?.placeCountry ||
      null,

    countryCode:
      row?.countryCode ||
      row?.country_code ||
      row?.placeCountryCode ||
      null,

    postalCode:
      row?.postalCode ||
      row?.postcode ||
      row?.zipCode ||
      row?.zip_code ||
      row?.placePostalCode ||
      null,

    date:
      row?.date ||
      null,

    hour:
      formatPointHour(row) ||
      null,

    actualArrivalAt:
      row?.actualArrivalAt ||
      null,

    forecast:
      parseMaybeJson(row?.forecast),

    operationMinutes:
      row?.operationMinutes ??
      row?.serviceDurationMinutes ??
      row?.estimatedOperationMinutes ??
      row?.loadingDurationMinutes ??
      row?.unloadingDurationMinutes ??
      null,

    substatus:
      row?.substatus ||
      row?.latestStatusEvent?.newStatus ||
      null,


    truckPlate:
      row?.truckPlate ||
      row?.truck?.plate ||
      row?.truck?.truckPlate ||
      row?.truckArrivalPlate ||
      row?.truckDeparturePlate ||
      null,

    trailerPlate:
      row?.trailerPlate ||
      row?.trailer?.trailerPlate ||
      row?.trailer?.plate ||
      row?.trailerArrivalPlate ||
      row?.trailerDeparturePlate ||
      null,

    driverId:
      row?.driverId ??
      row?.driver?.id ??
      null,

    driverName:
      row?.driverName ||
      row?.driver?.name ||
      row?.driverNameArrival ||
      row?.driverNameDeparture ||
      null,

    driver2Id:
      row?.driver2Id ??
      row?.driver2?.id ??
      null,

    driver2Name:
      row?.driver2Name ||
      row?.driver2?.name ||
      row?.driver2NameArrival ||
      row?.driver2NameDeparture ||
      null,

    loadId:
      row?.loadId ??
      row?.load?.id ??
      null,

    isFrozen:
      row?.isFrozen === true,

    lat:
      coordinates.lat,

    lng:
      coordinates.lng,
  };
}

function normalizeSnapshotPoint(
  point,
  index
) {
  const coordinates =
    normalizeCoordinates({
      lat:
        point?.lat ??
        point?.latitude,

      lng:
        point?.lng ??
        point?.lon ??
        point?.longitude,
    });

  if (!coordinates) {
    return null;
  }

  return {
    id:
      point?.templatePointId ??
      point?.id ??
      `snapshot-${index}`,

    sequence:
      Number(point?.sequence) ||
      index + 1,

    type:
      String(
        point?.type || "VIA"
      ).toUpperCase(),

    status: "",

    name:
      point?.name ||
      point?.city ||
      null,

    address:
      point?.address ||
      null,

    city:
      point?.city ||
      null,

    country:
      point?.country ||
      point?.countryName ||
      null,

    countryCode:
      point?.countryCode ||
      point?.country_code ||
      null,

    postalCode:
      point?.postalCode ||
      point?.postcode ||
      point?.zipCode ||
      point?.zip_code ||
      null,

    date: null,
    hour: null,
    substatus: null,
    truckPlate: null,
    trailerPlate: null,
    driverName: null,

    lat:
      coordinates.lat,

    lng:
      coordinates.lng,
  };
}

function getSessionTimelinePoints(session) {
  if (!session) {
    return [];
  }

  const operationalCardPoints =
    session.operationalPoints?.length
      ? session.operationalPoints
      : session.points || [];

  const rawRoutePoints =
    Array.isArray(session.routeSnapshot?.points)
      ? session.routeSnapshot.points
      : [];

  const routeCardPoints =
    rawRoutePoints
      .map((point, index) => {
        return (
          normalizeSnapshotPoint(
            point,
            index
          ) || {
            ...point,
            id:
              point?.templatePointId ??
              point?.id ??
              `route-${index}`,
            sequence:
              Number(point?.sequence) ||
              index + 1,
            status: "",
            lat: null,
            lng: null,
          }
        );
      })
      .sort(
        (a, b) =>
          a.sequence - b.sequence
      );

  const usedOperationalIndices =
    new Set();

  const timelineRoutePoints =
    routeCardPoints.map(
      (routePoint) => {
        let matchedIndex = -1;
        let closestDistance = Infinity;

        operationalCardPoints.forEach(
          (operationalPoint, index) => {
            if (
              usedOperationalIndices.has(index)
            ) {
              return;
            }

            if (
              routePoint.lat == null ||
              routePoint.lng == null ||
              operationalPoint.lat == null ||
              operationalPoint.lng == null
            ) {
              return;
            }

            const lat1 = Number(routePoint.lat);
            const lng1 = Number(routePoint.lng);
            const lat2 = Number(operationalPoint.lat);
            const lng2 = Number(operationalPoint.lng);

            if (
              ![
                lat1,
                lng1,
                lat2,
                lng2,
              ].every(Number.isFinite)
            ) {
              return;
            }

            const latitudeDifference =
              (lat1 - lat2) * 111320;

            const longitudeDifference =
              (lng1 - lng2) *
              111320 *
              Math.cos(
                (lat1 * Math.PI) / 180
              );

            const distance =
              Math.hypot(
                latitudeDifference,
                longitudeDifference
              );

            if (
              distance <= 50 &&
              distance < closestDistance
            ) {
              closestDistance = distance;
              matchedIndex = index;
            }
          }
        );

        if (matchedIndex === -1) {
          return routePoint;
        }

        usedOperationalIndices.add(
          matchedIndex
        );

        const operationalPoint =
          operationalCardPoints[matchedIndex];

        return {
          ...routePoint,
          type:
            operationalPoint.type ||
            routePoint.type,
          status:
            operationalPoint.status || "",
          substatus:
            operationalPoint.substatus || null,
          date:
            operationalPoint.date || null,
          hour:
            operationalPoint.hour || null,

          actualArrivalAt:
            operationalPoint.actualArrivalAt || null,

          forecast:
            operationalPoint.forecast || null,

          operationMinutes:
            operationalPoint.operationMinutes ??
            routePoint.operationMinutes ??
            null,

          truckPlate:
            operationalPoint.truckPlate ||
            routePoint.truckPlate ||
            null,
          trailerPlate:
            operationalPoint.trailerPlate ||
            routePoint.trailerPlate ||
            null,
          driverName:
            operationalPoint.driverName ||
            routePoint.driverName ||
            null,
          driver2Name:
            operationalPoint.driver2Name ||
            routePoint.driver2Name ||
            null,
        };
      }
    );

  const apiTimelinePoints =
    session.timelinePoints || [];

  const longestLength = Math.max(
    timelineRoutePoints.length,
    apiTimelinePoints.length,
    operationalCardPoints.length
  );

  if (
    operationalCardPoints.length > 0 &&
    operationalCardPoints.length === longestLength
  ) {
    return operationalCardPoints;
  }

  if (
    apiTimelinePoints.length > 0 &&
    apiTimelinePoints.length === longestLength
  ) {
    return apiTimelinePoints;
  }

  return timelineRoutePoints;
}

function normalizeGeometry(value) {
  const parsed =
    parseMaybeJson(value) ||
    value;

  if (
    parsed?.type === "Feature" &&
    parsed.geometry?.type ===
      "LineString" &&
    Array.isArray(
      parsed.geometry.coordinates
    )
  ) {
    return parsed;
  }

  if (
    parsed?.type === "LineString" &&
    Array.isArray(
      parsed.coordinates
    )
  ) {
    return {
      type: "Feature",

      geometry: parsed,

      properties: {},
    };
  }

  return null;
}

function normalizeRouteResponse(data) {
  const routes =
    Array.isArray(data?.routes)
      ? data.routes
      : [];

  const route =
    routes[0] ||
    data?.route ||
    null;

  return normalizeGeometry(
    route?.geometry
  );
}

function getRouteTotalsFromSummary(source) {
  const value = parseMaybeJson(source) || source;
  const summary = value?.summary || value;

  if (!summary || typeof summary !== "object") {
    return null;
  }

  // Unidades já usadas no cálculo dos legs:
  // length/distanceKm = km
  // distance/distanceMeters = metros
  // durationMinutes = minutos
  // time/durationSeconds = segundos

  const kmValue = toFiniteNumberOrNull(
    summary.distanceKm ??
    summary.distance_km ??
    summary.length
  );

  const metersValue = toFiniteNumberOrNull(
    summary.distanceMeters ??
    summary.distance_m ??
    summary.distance
  );

  const minutesValue = toFiniteNumberOrNull(
    summary.durationMinutes
  );

  const secondsValue = toFiniteNumberOrNull(
    summary.time ??
    summary.durationSeconds ??
    summary.duration
  );

  const km = kmValue !== null
    ? kmValue
    : metersValue !== null
      ? metersValue / 1000
      : null;

  const minutes = minutesValue !== null
    ? minutesValue
    : secondsValue !== null
      ? secondsValue / 60
      : null;

  if (
    (km === null || km < 0) &&
    (minutes === null || minutes < 0)
  ) {
    return null;
  }

  return {
    km: km !== null && km >= 0 ? km : null,
    minutes: minutes !== null && minutes >= 0 ? minutes : null,
  };
}

function getPointLabel(point) {
  if (point.type === "PICKUP") {
    return "C";
  }

  if (point.type === "DELIVERY") {
    return "D";
  }

  if (point.type === "ORIGIN") {
    return "A";
  }

  if (point.type === "DESTINATION") {
    return "B";
  }

  if (point.type === "FUEL") {
    return "F";
  }

  return String(
    point.sequence || ""
  );
}

function getSessionDisplayNumber(
  session
) {
  const number =
    session?.transportSessionNumber ??
    session?.transportsessionNumber ??
    session?.sessionNumber ??
    session?.id;

  return `TS-${String(
    number || ""
  ).padStart(4, "0")}`;
}

function getSessionStatus(session) {
  return String(
    session?.status || "ACTIVE"
  )
    .trim()
    .toUpperCase();
}

function calculateProgress(points) {
  if (!points?.length) {
    return 0;
  }

  const completed =
    points.filter(
      (point) =>
        String(
          point?.status || ""
        )
          .trim()
          .toUpperCase() === "COMPLETED"
    ).length;

  return Math.round(
    (completed / points.length) * 100
  );
}

function calculateCompletedPointCount(
  points
) {
  return (points || []).filter(
    (point) =>
      String(
        point?.status || ""
      )
        .trim()
        .toUpperCase() === "COMPLETED"
  ).length;
}

function getNextOperationalPoint(
  points
) {
  if (!points.length) {
    return null;
  }

  return (
    points.find(
      (point) =>
        !FINAL_STATUSES.has(
          String(
            point?.status || ""
          ).toUpperCase()
        )
    ) ||
    points[
      points.length - 1
    ] ||
    null
  );
}

function formatPointHour(value) {
  const point =
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
      ? value
      : { hour: value };

  const firstPresent = (...values) =>
    values.find(
      (item) =>
        item !== null &&
        item !== undefined &&
        String(item).trim() !== ""
    );

  const start = firstPresent(
    point.hour,
    point.hourRange,
    point.timeWindow,
    point.timeRange,
    point.startHour,
    point.hourStart,
    point.hourFrom,
    point.timeFrom,
    point.startTime,
    point.timeStart,
    point.hour_start,
    point.start_time,
    point.horaInicio,
    point.hora_inicio
  ) ?? "";

  const end = firstPresent(
    point.hourEnd,
    point.endHour,
    point.hourTo,
    point.timeTo,
    point.timeEnd,
    point.endTime,
    point.hour_end,
    point.end_hour,
    point.end_time,
    point.horaFim,
    point.hora_fim,
    point.hour2
  );

  const timePattern =
    /\b(?:[01]?\d|2[0-3]):[0-5]\d\b/g;

  const startTimes =
    String(start).match(timePattern) || [];

  const endTimes =
    String(end ?? "").match(timePattern) || [];

  const normalizeTime = (value) => {
    const [hours, minutes] = value.split(":");

    return `${hours.padStart(2, "0")}:${minutes}`;
  };

  if (startTimes.length >= 2) {
    return (
      `${normalizeTime(startTimes[0])} – ` +
      `${normalizeTime(startTimes[1])}`
    );
  }

  if (
    startTimes.length === 1 &&
    endTimes.length >= 1
  ) {
    return (
      `${normalizeTime(startTimes[0])} – ` +
      `${normalizeTime(endTimes[0])}`
    );
  }

  if (
    startTimes.length === 1 &&
    /^\d{1,2}:\d{2}(?::\d{2})?$/.test(
      String(start).trim()
    )
  ) {
    return normalizeTime(startTimes[0]);
  }

  return String(start).trim();
}

function formatPointDateTime(
  point,
  locale
) {
  if (!point) {
    return "";
  }

  let dateLabel =
    point.date || "";

  const isoDateMatch =
    String(dateLabel).match(
      /^(\d{4})-(\d{2})-(\d{2})/
    );

  if (isoDateMatch) {
    const [, year, month, day] =
      isoDateMatch;

    try {
      dateLabel =
        new Date(
          Number(year),
          Number(month) - 1,
          Number(day)
        ).toLocaleDateString(
          locale
        );
    } catch {
      dateLabel =
        point.date || "";
    }
  }

  const hourLabel =
    point.hour
      ? formatPointHour(point.hour)
      : "";

  return [
    dateLabel,
    hourLabel,
  ]
    .filter(Boolean)
    .join(" · ");
}

// =========================================================
// LOADEVO — OPERATIONAL PLANNING FORECAST
// Estimativas, não eventos GPS confirmados.
// =========================================================

const FORECAST_LOADING_MINUTES = 60;
const FORECAST_UNLOADING_MINUTES = 45;

const FORECAST_BREAK_AFTER_MINUTES = 270;
const FORECAST_BREAK_MINUTES = 45;

const FORECAST_DAY_DRIVING_MINUTES = 540;
const FORECAST_DAILY_REST_MINUTES = 660;

function getPlanningTimeZone(point) {
  const explicit =
    point?.forecast?.timeZone ||
    point?.timeZone;

  if (explicit) {
    try {
      new Intl.DateTimeFormat("en", {
        timeZone: explicit,
      });

      return explicit;
    } catch {
      return null;
    }
  }

  const zones = {
    PT: "Europe/Lisbon",
    ES: "Europe/Madrid",
    FR: "Europe/Paris",
    DE: "Europe/Berlin",
    BE: "Europe/Brussels",
    NL: "Europe/Amsterdam",
    LU: "Europe/Luxembourg",
    IT: "Europe/Rome",
    CH: "Europe/Zurich",
  };

  return zones[getPointCountryCode(point)] || null;
}

function zonedScheduleInstant(date, clock, timeZone) {
  if (!date || !clock || !timeZone) {
    return null;
  }

  const match =
    String(date).match(
      /^(\d{4})-(\d{2})-(\d{2})/
    );

  if (!match) {
    return null;
  }

  const [, y, m, d] = match.map(Number);

  const [h, min] =
    clock.split(":").map(Number);

  if (
    !Number.isInteger(h) ||
    h > 23 ||
    min > 59
  ) {
    return null;
  }

  const expected =
    Date.UTC(y, m - 1, d, h, min);

  const formatter =
    new Intl.DateTimeFormat("en-GB", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });

  let candidate = expected;

  for (let i = 0; i < 4; i += 1) {
    const fields = Object.fromEntries(
      formatter
        .formatToParts(new Date(candidate))
        .filter((part) => part.type !== "literal")
        .map((part) => [
          part.type,
          Number(part.value),
        ])
    );

    const represented = Date.UTC(
      fields.year,
      fields.month - 1,
      fields.day,
      fields.hour,
      fields.minute
    );

    const correction =
      expected - represented;

    candidate += correction;

    if (correction === 0) {
      return candidate;
    }
  }

  return null;
}

function getPlanningWindow(point) {
  const zone =
    getPlanningTimeZone(point);

  const date =
    String(point?.date || "").slice(0, 10);

  const matches =
    String(formatPointHour(point) || "").match(
      /\b(?:[01]?\d|2[0-3]):[0-5]\d\b/g
    ) || [];

  const start =
    zonedScheduleInstant(
      date,
      matches[0],
      zone
    );

  let end = matches[1]
    ? zonedScheduleInstant(
        date,
        matches[1],
        zone
      )
    : null;

  if (
    end !== null &&
    start !== null &&
    end < start
  ) {
    const tomorrow =
      new Date(`${date}T12:00:00Z`);

    if (
      !Number.isFinite(tomorrow.getTime())
    ) {
      return {
        start: null,
        end: null,
        zone,
      };
    }

    tomorrow.setUTCDate(
      tomorrow.getUTCDate() + 1
    );

    end = zonedScheduleInstant(
      tomorrow.toISOString().slice(0, 10),
      matches[1],
      zone
    );
  }

  return { start, end, zone };
}

function getPlannedOperationMinutes(point) {
  const configured =
    toFiniteNumberOrNull(
      point?.operationMinutes ??
      point?.serviceDurationMinutes ??
      point?.estimatedOperationMinutes ??
      point?.loadingDurationMinutes ??
      point?.unloadingDurationMinutes
    );

  if (
    configured !== null &&
    configured >= 0
  ) {
    return configured;
  }

  const type =
    String(point?.type || "").toUpperCase();

  if (
    ["PICKUP", "PICK_UP", "LOADING"]
      .includes(type)
  ) {
    return FORECAST_LOADING_MINUTES;
  }

  if (
    [
      "DELIVERY",
      "DROPOFF",
      "DROP_OFF",
      "UNLOADING",
    ].includes(type)
  ) {
    return FORECAST_UNLOADING_MINUTES;
  }

  return 0;
}

function travelWithPlanningRests(
  minutes,
  counters
) {
  let left = minutes;
  let elapsed = 0;
  let breaks = 0;
  let rests = 0;

  while (left > 0.00001) {
    if (
      counters.today >=
      FORECAST_DAY_DRIVING_MINUTES - 0.00001
    ) {
      elapsed += FORECAST_DAILY_REST_MINUTES;
      rests += FORECAST_DAILY_REST_MINUTES;

      counters.today = 0;
      counters.sinceBreak = 0;

      continue;
    }

    if (
      counters.sinceBreak >=
      FORECAST_BREAK_AFTER_MINUTES - 0.00001
    ) {
      elapsed += FORECAST_BREAK_MINUTES;
      breaks += FORECAST_BREAK_MINUTES;

      counters.sinceBreak = 0;

      continue;
    }

    const step = Math.min(
      left,
      FORECAST_BREAK_AFTER_MINUTES -
        counters.sinceBreak,
      FORECAST_DAY_DRIVING_MINUTES -
        counters.today
    );

    elapsed += step;
    left -= step;

    counters.sinceBreak += step;
    counters.today += step;
  }

  return { elapsed, breaks, rests };
}

function buildSessionOperationalForecast(session) {
  const points =
    getSessionTimelinePoints(session);

  const metrics =
    Array.isArray(session?.routeMetrics)
      ? session.routeMetrics
      : [];

  const byPoint = [];

  const counters = {
    sinceBreak: 0,
    today: 0,
  };

  const firstSchedule =
    getPlanningWindow(points[0]);

  let departure = firstSchedule.start;

  let allSchedules =
    firstSchedule.start !== null;

  let breaks = 0;
  let rests = 0;
  let waiting = 0;
  let operations = 0;

  const findMetric = (point) =>
    metrics.find((metric) => {
      if (
        metric.id != null &&
        point?.id != null &&
        String(metric.id) ===
          String(point.id)
      ) {
        return true;
      }

      const lat =
        toFiniteNumberOrNull(point?.lat);

      const lng =
        toFiniteNumberOrNull(point?.lng);

      return (
        lat !== null &&
        lng !== null &&
        Math.abs(
          lat - Number(metric.lat)
        ) < 0.0004 &&
        Math.abs(
          lng - Number(metric.lng)
        ) < 0.0004
      );
    });

  points.forEach((point, index) => {
    const schedule =
      getPlanningWindow(point);

    const operationMinutes =
      getPlannedOperationMinutes(point);

    const oldDeparture = departure;

    let arrival = null;
    let km = null;
    let driveMinutes = null;

    if (
      index > 0 &&
      Number.isFinite(oldDeparture)
    ) {
      const prevMetric =
        findMetric(points[index - 1]);

      const nextMetric =
        findMetric(point);

      if (prevMetric && nextMetric) {
        const legKm =
          nextMetric.km - prevMetric.km;

        const legMin =
          nextMetric.minutes -
          prevMetric.minutes;

        if (
          legKm >= 0 &&
          legMin >= 0 &&
          Number.isFinite(legMin)
        ) {
          km = legKm;
          driveMinutes = legMin;

          const travel =
            travelWithPlanningRests(
              legMin,
              counters
            );

          breaks += travel.breaks;
          rests += travel.rests;

          arrival =
            oldDeparture +
            travel.elapsed * 60000;
        }
      }
    }

    let start =
      index === 0
        ? schedule.start
        : arrival;

    let wait = null;

    if (
      index > 0 &&
      Number.isFinite(arrival)
    ) {
      if (schedule.start === null) {
        allSchedules = false;
      } else {
        wait = Math.max(
          0,
          (schedule.start - arrival) / 60000
        );

        start = Math.max(
          arrival,
          schedule.start
        );

        waiting += wait;

        if (
          wait >=
          FORECAST_DAILY_REST_MINUTES
        ) {
          // Planeamento: intervalo suficiente
          // para incluir um descanso.
          // Não duplicar a sua duração.
          counters.today = 0;
          counters.sinceBreak = 0;
        }
      }
    }

    if (schedule.start === null) {
      allSchedules = false;
    }

    // Sem agendamento, prevê a operação mais cedo
    // possível. Não inventa tempo de espera.
    const completion =
      Number.isFinite(start)
        ? start + operationMinutes * 60000
        : null;

    const margin =
      Number.isFinite(completion) &&
      schedule.end !== null
        ? (
            schedule.end - completion
          ) / 60000
        : null;

    const delay =
      margin !== null
        ? Math.max(0, -margin)
        : index > 0 &&
            schedule.start !== null &&
            Number.isFinite(arrival)
          ? Math.max(
              0,
              (arrival - schedule.start) / 60000
            )
          : null;

    operations += operationMinutes;

    byPoint.push({
      timeZone: schedule.zone,

      estimatedArrivalAt:
        arrival === null
          ? null
          : new Date(arrival).toISOString(),

      estimatedStartAt:
        start === null
          ? null
          : new Date(start).toISOString(),

      estimatedCompletionAt:
        completion === null
          ? null
          : new Date(completion).toISOString(),

      waitingMinutes: wait,
      operationMinutes,

      windowMarginMinutes: margin,
      delayMinutes: delay,

      routeLegKm: km,
      routeLegMinutes: driveMinutes,
    });

    departure = completion;
  });

  const last =
    byPoint[byPoint.length - 1];

  const lastCompletion =
    last?.estimatedCompletionAt
      ? new Date(
          last.estimatedCompletionAt
        ).getTime()
      : null;

  const elapsed =
    Number.isFinite(firstSchedule.start) &&
    Number.isFinite(lastCompletion) &&
    points.length > 0
      ? Math.max(
          0,
          (
            lastCompletion -
            firstSchedule.start
          ) / 60000
        )
      : null;

  const completeSchedule =
    allSchedules &&
    elapsed !== null;

  return {
    points: byPoint,

    totalElapsedMinutes: elapsed,

    mandatoryBreakMinutes:
      elapsed !== null ? breaks : null,

    dailyRestMinutes:
      elapsed !== null ? rests : null,

    operationsMinutes:
      elapsed !== null ? operations : null,

    // Só calcular espera total quando todos
    // os agendamentos necessários estão presentes.
    waitingMinutes:
      completeSchedule ? waiting : null,

    isCompleteSchedule: completeSchedule,
    isPlanningEstimate: true,
  };
}

function formatForecastInstant(value, locale, timeZone) {
  if (
    !value ||
    !/(Z|[+-]\d{2}:\d{2})$/i.test(String(value))
  ) {
    return "—";
  }

  const date = new Date(value);

  if (!Number.isFinite(date.getTime())) {
    return "—";
  }

  try {
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      ...(timeZone ? { timeZone } : {}),
    }).format(date);
  } catch {
    return "—";
  }
}

function formatForecastMinutes(value) {
  const numeric = toFiniteNumberOrNull(value);

  if (numeric === null || numeric < 0) {
    return "—";
  }

  const total = Math.round(numeric);
  const hours = Math.floor(total / 60);
  const minutes = total % 60;

  return hours
    ? `${hours}h ${String(minutes).padStart(2, "0")}m`
    : `${minutes} min`;
}

function getPointScheduleHighlight(
  point,
  locale
) {
  if (!point) {
    return {
      dateLabel: "—",
      timeLabel: "—",
      primaryLabel: "—",
      secondaryLabel: "—",
      emphasis: "time",
      dayLabel: "—",
      monthLabel: "—",
      yearLabel: "—",
    };
  }

  const rawDate = String(
    point?.date || ""
  ).trim();

  const rawTime = String(
    point?.hour || ""
  ).trim();

  const timeLabel =
    rawTime
      ? formatPointHour(rawTime)
      : "—";

  let dateLabel =
    rawDate || "—";

  let dayLabel = "—";
  let monthLabel = "—";
  let yearLabel = "—";

  let isSameDayAsToday =
    false;

  const isoDateMatch =
    rawDate.match(
      /^(\d{4})-(\d{2})-(\d{2})/
    );

  if (isoDateMatch) {
    const [, year, month, day] =
      isoDateMatch;

    try {
      const pointDate =
        new Date(
          Number(year),
          Number(month) - 1,
          Number(day)
        );

      dateLabel =
        pointDate.toLocaleDateString(
          locale,
          {
            day: "numeric",
            month: "short",
            year: "numeric",
          }
        );

      dayLabel = String(
        pointDate.getDate()
      );

      monthLabel =
        pointDate
          .toLocaleDateString(
            locale,
            {
              month: "short",
            }
          )
          .replace(/\./g, "")
          .toUpperCase();

      yearLabel = String(
        pointDate.getFullYear()
      );

      const now =
        new Date();

      isSameDayAsToday =
        pointDate.getFullYear() ===
          now.getFullYear() &&
        pointDate.getMonth() ===
          now.getMonth() &&
        pointDate.getDate() ===
          now.getDate();
    } catch {
      dateLabel =
        rawDate || "—";
    }
  }

  const emphasis =
    rawDate
      ? isSameDayAsToday
        ? "time"
        : "date"
      : rawTime
        ? "time"
        : "date";

  return {
    dateLabel,
    timeLabel,
    primaryLabel:
      emphasis === "time"
        ? timeLabel
        : dateLabel,
    secondaryLabel:
      emphasis === "time"
        ? dateLabel
        : timeLabel,
    emphasis,
    dayLabel,
    monthLabel,
    yearLabel,
  };
}

function getPointLocationMeta(
  point
) {
  if (!point) {
    return "";
  }

  const city =
    String(
      point.city || ""
    ).trim();

  const country =
    String(
      point.country || ""
    ).trim();

  return (
    [city, country]
      .filter(Boolean)
      .join(", ") ||
    point.address ||
    ""
  );
}

function getPointCountryCode(
  point
) {
  const directCode =
    String(
      point?.countryCode || ""
    )
      .trim()
      .toUpperCase();

  if (
    /^[A-Z]{2}$/.test(
      directCode
    )
  ) {
    return directCode;
  }

  const searchableText =
    [
      point?.country,
      point?.address,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

  const countries = [
    ["portugal", "PT"],
    ["espanha", "ES"],
    ["spain", "ES"],
    ["frança", "FR"],
    ["france", "FR"],
    ["bélgica", "BE"],
    ["belgium", "BE"],
    ["países baixos", "NL"],
    ["netherlands", "NL"],
    ["holanda", "NL"],
    ["alemanha", "DE"],
    ["germany", "DE"],
    ["luxemburgo", "LU"],
    ["luxembourg", "LU"],
    ["suíça", "CH"],
    ["switzerland", "CH"],
    ["itália", "IT"],
    ["italy", "IT"],
  ];

  const match =
    countries.find(
      ([countryName]) =>
        searchableText.includes(
          countryName
        )
    );

  return match?.[1] || "";
}

function getCountryFlag(
  point
) {
  const countryCode =
    getPointCountryCode(
      point
    );

  if (!countryCode) {
    return null;
  }

  return (
    <img
      src={`https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`}
      alt=""
      aria-hidden="true"
      width={19}
      height={19}
      style={{
        display: "inline-block",
        width: "19px",
        height: "19px",
        minWidth: "19px",
        objectFit: "cover",
        objectPosition: "center",
        borderRadius: "50%",
        verticalAlign: "-4px",
      }}
    />
  );
}

function getOperationalStatusText(
  point,
  t
) {
  if (!point) {
    return "";
  }

  const placeName = String(
    point.companyName ||
    point.name ||
    point.location ||
    point.city ||
    ""
  ).trim();

  const city = String(
    point.city || ""
  ).trim();

  const location =
    placeName &&
    city &&
    !placeName
      .toLocaleLowerCase()
      .includes(city.toLocaleLowerCase())
      ? `${placeName}, ${city}`
      : placeName ||
        city ||
        point.address ||
        t(
          "map.live.nextLocation",
          {
            defaultValue:
              "o próximo ponto",
          }
        );


  const statusCandidates = [
    point.status,
    point.substatus,
    point.latestStatusEvent?.newStatus,
  ]
    .map((value) =>
      String(value || "").trim().toUpperCase()
    )
    .filter(Boolean);

  const substatus =
    statusCandidates.find(
      (value) =>
        ![
          "PENDING",
          "NONE",
          "UNKNOWN",
          "ACTIVE",
          "CURRENT",
        ].includes(value)
    ) ||
    statusCandidates[0] ||
    "";



  if (
    [
      "IN_TRANSIT_MOVING_TO_PICKUP_LOCATION",
      "IN_TRANSIT_MOVING_TO_DELIVERY_LOCATION",
      "MOVING_TO_PICKUP_LOCATION",
      "MOVING_TO_DELIVERY_LOCATION",
      "IN_TRANSIT",
    ].includes(substatus)
  ) {
    return `Moving to ${location}`;
  }



  if (
    substatus ===
      "AT_PICKUP_LOCATION" ||
    substatus ===
      "AT_DELIVERY_LOCATION"
  ) {
    return t(
      "map.live.operationalStatus.atLocation",
      {
        location,

        defaultValue:
          "No local: {{location}}",
      }
    );
  }

  if (
    substatus ===
    "WAITING_TO_LOAD"
  ) {
    return t(
      "map.live.operationalStatus.waitingToLoad",
      {
        defaultValue:
          "A aguardar carga",
      }
    );
  }

  if (
    substatus ===
    "LOADING_IN_PROGRESS"
  ) {
    return t(
      "map.live.operationalStatus.loading",
      {
        defaultValue:
          "Carga em curso",
      }
    );
  }

  if (
    substatus ===
    "WAITING_TO_UNLOAD"
  ) {
    return t(
      "map.live.operationalStatus.waitingToUnload",
      {
        defaultValue:
          "A aguardar descarga",
      }
    );
  }

  if (
    substatus ===
    "UNLOADING_MERCHANDISE"
  ) {
    return t(
      "map.live.operationalStatus.unloading",
      {
        defaultValue:
          "Descarga em curso",
      }
    );
  }

  if (
    substatus === "DELAYED"
  ) {
    return t(
      "map.live.operationalStatus.delayed",
      {
        defaultValue:
          "Transporte atrasado",
      }
    );
  }

  if (substatus === "PENDING") {
    return `Pending · ${location}`;
  }

  if (!substatus) {
    return `No operational status · ${location}`;
  }

  return `${
    substatus.replace(/_/g, " ").toLowerCase()
  } · ${location}`;


}

function formatUpdatedAt(
  value,
  locale
) {
  if (!value) return "";

  try {
    return new Date(
      value
    ).toLocaleTimeString(
      locale,
      {
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  } catch {
    return "";
  }
}

function getMapSelectionSessions(
  sessions,
  selectedSessionId,
  selectedTruckPlate
) {
  if (selectedTruckPlate) {
    const wantedPlate = String(
      selectedTruckPlate
    ).trim().toUpperCase();

    return sessions.filter(
      (session) =>
        String(session.truckPlate || "")
          .trim()
          .toUpperCase() === wantedPlate
    );
  }

  return sessions.filter(
    (session) =>
      selectedSessionId != null &&
      String(session.id) ===
        String(selectedSessionId)
  );
}

function buildRoutesFeatureCollection(
  sessions,
  selectedSessionId,
  selectedTruckPlate = null
) {
  const selectedSessions =
    getMapSelectionSessions(
      sessions,
      selectedSessionId,
      selectedTruckPlate
    );

  return {
    type: "FeatureCollection",

    features: selectedSessions
      .filter(
        (session) =>
          session.geometry?.geometry?.type ===
            "LineString" &&
          Array.isArray(
            session.geometry.geometry.coordinates
          ) &&
          session.geometry.geometry.coordinates.length >= 2
      )
      .map((session) => ({
        type: "Feature",

        geometry:
          session.geometry.geometry,

        properties: {
          sessionId: String(session.id),
          truckPlate: String(
            session.truckPlate || ""
          ),
          selected: true,
          status: session.status,
        },
      })),
  };
}

function buildPointsFeatureCollection(
  sessions,
  selectedSessionId,
  selectedTruckPlate = null
) {
  const selectedSessions =
    getMapSelectionSessions(
      sessions,
      selectedSessionId,
      selectedTruckPlate
    );

  const features = selectedSessions
    .flatMap((session) => {
      const points = selectedTruckPlate
        ? getSessionTimelinePoints(session)
        : session.points || [];

      return points.map((point) => {
        if (
          point.lng == null ||
          point.lat == null
        ) {
          return null;
        }

        const lng = Number(point.lng);
        const lat = Number(point.lat);

        if (
          !Number.isFinite(lng) ||
          !Number.isFinite(lat) ||
          Math.abs(lng) > 180 ||
          Math.abs(lat) > 90
        ) {
          return null;
        }

        return {
          type: "Feature",

          geometry: {
            type: "Point",
            coordinates: [lng, lat],
          },

          properties: {
            sessionId: String(session.id),
            truckPlate: String(
              session.truckPlate || ""
            ),
            pointId: String(point.id),
            type: point.type,
            label: getPointLabel(point),
            selected: true,
            showLabel: true,
            name: point.name || "",
          },
        };
      });
    })
    .filter(Boolean);

  return {
    type: "FeatureCollection",
    features,
  };
}

// Movimento físico do próprio cartão, sem clone visual.
// Compensa a escala CSS do painel direito.
const LiveSortableStop = ({
  id,
  disabled,
  dragInProgress,
  dragScale = 1,
  className,
  children,
}) => {
  const {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled,
    animateLayoutChanges: () => false,
    transition: {
      duration: 180,
      easing: "cubic-bezier(0.22, 1, 0.36, 1)",
    },
  });

  return (
    <div
      ref={setNodeRef}
      data-reorder-id={id}
      className={`${className}${
        isDragging ? " is-dnd-dragging" : ""
      }`}
      style={{
        transform:
          dragInProgress && transform
            ? CSS.Translate.toString({
                ...transform,
                x: 0,
                y: transform.y / dragScale,
              })
            : undefined,
        transition:
          dragInProgress && !isDragging
            ? transition
            : "none",
        zIndex: isDragging ? 20 : undefined,
      }}
    >
      <span
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        className="live-transports-map__selection-stop-drag-handle"
        title={
          disabled
            ? undefined
            : "Arrastar para reordenar"
        }
        aria-label={`Reordenar paragem ${id}`}
        tabIndex={disabled ? -1 : 0}
        style={{
          visibility: disabled ? "hidden" : "visible",
          pointerEvents: disabled ? "none" : "auto",
          touchAction: "none",
          cursor: isDragging ? "grabbing" : "grab",
        }}
      />

      {children}
    </div>
  );
};

const LiveTransportsMap = () => {
  const { t, i18n } =
    useTranslation("map");

  const location = useLocation();

  const requestedSessionId = useMemo(() => {
    const rawId = new URLSearchParams(
      location.search
    ).get("sessionId");

    if (!rawId || !/^[1-9]\d*$/.test(rawId)) {
      return null;
    }

    const id = Number(rawId);

    return Number.isSafeInteger(id)
      ? id
      : null;
  }, [location.search]);

  const requestedSessionIdRef =
    useRef(requestedSessionId);

  requestedSessionIdRef.current =
    requestedSessionId;

  const locale =
    i18n.resolvedLanguage ||
    i18n.language ||
    "pt-PT";

  const mapContainerRef =
    useRef(null);

  const mapRef =
    useRef(null);

  const stopsListRef =
    useRef(null);

  const stopsReorderBusyRef =
    useRef(false);

  const [draggingStopId, setDraggingStopId] =
    useState(null);

  const [stopDragScale, setStopDragScale] =
    useState(1);

  const [savingStopOrder, setSavingStopOrder] =
    useState(false);

  const [driverPicker, setDriverPicker] =
    useState(null);

  const [savingDriver, setSavingDriver] =
    useState(false);

  const [vehiclePicker, setVehiclePicker] =
    useState(null);

  const [savingVehicle, setSavingVehicle] =
    useState(false);

  const [selectedStopForStatus, setSelectedStopForStatus] =
    useState(null);

  const [selectedStopForCmr, setSelectedStopForCmr] =
    useState(null);

  const [savingStopStatus, setSavingStopStatus] =
    useState(false);

  const [scheduleConflict, setScheduleConflict] =
    useState(null);

  const stopDndSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 7,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter:
        sortableKeyboardCoordinates,
    })
  );

  const routeGeometryCacheRef =
    useRef(new Map());

  const routeGeometryFailureCacheRef =
    useRef(new Map());

  const liveSessionsCacheKeyRef =
    useRef(
      getLiveSessionsCacheKey()
    );

  const initialSessionsCacheRef =
    useRef(
      readLiveSessionsCache(
        liveSessionsCacheKeyRef
          .current
      )
    );

  const lastLoadsRevRef =
    useRef(
      initialSessionsCacheRef
        .current
        ?.loadsRev ??
      null
    );

  const socketRefreshTimerRef =
    useRef(null);

  const [viewMode, setViewMode] =
    useState("sessions");

  const [sessionPanelMode, setSessionPanelMode] =
    useState("simple");

  const changeSessionPanelMode = (nextMode) => {
    if (nextMode === sessionPanelMode) {
      return;
    }

    const reduceMotion =
      window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

    if (
      reduceMotion ||
      typeof document.startViewTransition !== "function"
    ) {
      setSessionPanelMode(nextMode);
      return;
    }

    document.startViewTransition(() => {
      flushSync(() => {
        setSessionPanelMode(nextMode);
      });
    });
  };


  const [
    selectedTruckPlate,
    setSelectedTruckPlate,
  ] = useState(null);

  const [
    expandedTruckSessionId,
    setExpandedTruckSessionId,
  ] = useState(null);

  const [
    matchedTruckPanelHeight,
    setMatchedTruckPanelHeight,
  ] = useState(null);

  const transportMapMounted =
    viewMode !== "drivers";

  const [mapReady, setMapReady] =
    useState(false);

  const [sessions, setSessions] =
    useState(
      () =>
        initialSessionsCacheRef
          .current
          ?.sessions ||
        []
    );

  const [
    selectedSessionId,
    setSelectedSessionId,
  ] = useState(() => requestedSessionId);

  useEffect(() => {
    if (requestedSessionId === null) {
      return;
    }

    setViewMode("sessions");

    setSelectedSessionId(
      requestedSessionId
    );
  }, [requestedSessionId]);

  const [searchTerm, setSearchTerm] =
    useState("");

  const [
    isSidebarCollapsed,
    setIsSidebarCollapsed,
  ] = useState(false);

  const [loading, setLoading] =
    useState(
      () =>
        !initialSessionsCacheRef
          .current
    );

  const [refreshing, setRefreshing] =
    useState(false);

  const [error, setError] =
    useState(null);

  const [lastUpdated, setLastUpdated] =
    useState(
      () => {
        const cached =
          initialSessionsCacheRef
            .current
            ?.lastUpdated;

        return cached
          ? new Date(
              cached
            )
          : null;
      }
    );

  const fetchRouteGeometry =
    useCallback(
      async (
        sessionId,
        points
      ) => {
        if (points.length < 2) {
          return null;
        }

        const signature =
          points
            .map(
              (point) =>
                `${point.lat}:${point.lng}`
            )
            .join("|");

        const cacheKey =
          `${sessionId}:${signature}`;

        if (
          routeGeometryCacheRef.current.has(
            cacheKey
          )
        ) {
          return routeGeometryCacheRef.current.get(
            cacheKey
          );
        }

        const previousFailureAt =
          routeGeometryFailureCacheRef
            .current
            .get(
              cacheKey
            );

        if (
          Number.isFinite(
            Number(
              previousFailureAt
            )
          ) &&
          Date.now() -
            Number(
              previousFailureAt
            ) <
            15 * 1000
        ) {
          return null;
        }

        try {
          const { data } =
            await api.post(
              "/maps/route",
              {
                transportsessionId:
                  sessionId,

                costing:
                  "truck",

                routePreview:
                  true,

                alternatives:
                  0,

                includeTollEstimate:
                  false,

                validateLocations:
                  false,

                locations:
                  points.map(
                    (
                      point,
                      index
                    ) => ({
                      id:
                        point.id ||
                        `live-point-${index}`,

                      type:
                        point.type,

                      sequence:
                        index + 1,

                      lat:
                        point.lat,

                      lon:
                        point.lng,
                    })
                  ),
              }
            );

          const geometry =
            normalizeRouteResponse(
              data
            );

          const route =
            (Array.isArray(data?.routes)
              ? data.routes[0]
              : null) ||
            data?.route ||
            data?.trip ||
            null;

          const legs =
            route?.legs ||
            data?.trip?.legs ||
            [];

          let routeMetrics = [];

          if (
            Array.isArray(legs) &&
            legs.length === points.length - 1
          ) {
            let totalKm = 0;
            let totalMinutes = 0;

            routeMetrics = [
              {
                id: points[0].id,
                lat: points[0].lat,
                lng: points[0].lng,
                km: 0,
                minutes: 0,
              },
            ];

            for (let index = 0; index < legs.length; index += 1) {
              const leg = legs[index];
              const summary = leg?.summary || leg;

              const distanceKm =
                toFiniteNumberOrNull(
                  summary?.length ??
                  summary?.distanceKm ??
                  summary?.distance_km
                );

              const distanceMeters =
                toFiniteNumberOrNull(
                  summary?.distanceMeters ??
                  summary?.distance_m ??
                  summary?.distance
                );

              const durationMinutes =
                toFiniteNumberOrNull(
                  summary?.durationMinutes
                );

              const durationSeconds =
                toFiniteNumberOrNull(
                  summary?.time ??
                  summary?.durationSeconds ??
                  summary?.duration
                );

              const legKm =
                distanceKm !== null
                  ? distanceKm
                  : distanceMeters !== null
                    ? distanceMeters / 1000
                    : null;

              const legMinutes =
                durationMinutes !== null
                  ? durationMinutes
                  : durationSeconds !== null
                    ? durationSeconds / 60
                    : null;

              if (
                legKm === null ||
                legMinutes === null ||
                legKm < 0 ||
                legMinutes < 0
              ) {
                routeMetrics = [];
                break;
              }

              totalKm += legKm;
              totalMinutes += legMinutes;

              const destination =
                points[index + 1];

              routeMetrics.push({
                id: destination.id,
                lat: destination.lat,
                lng: destination.lng,
                km: totalKm,
                minutes: totalMinutes,
              });
            }
          }

          // Alguns providers devolvem resumo total mas não
          // um leg por cada par de paragens.
          const lastMetric =
            routeMetrics.length === points.length
              ? routeMetrics[routeMetrics.length - 1]
              : null;

          const routeTotals = lastMetric
            ? {
                km: lastMetric.km,
                minutes: lastMetric.minutes,
              }
            : getRouteTotalsFromSummary(route) ||
              getRouteTotalsFromSummary(data?.summary) ||
              getRouteTotalsFromSummary(data);

          const result = {
            geometry,
            routeMetrics,
            routeTotals,
          };

          if (geometry || routeTotals) {
            routeGeometryCacheRef.current.set(
              cacheKey,
              result
            );

            routeGeometryFailureCacheRef.current.delete(
              cacheKey
            );
          }

          return geometry || routeTotals ? result : null;

        } catch (routeError) {
          routeGeometryFailureCacheRef.current.set(
            cacheKey,
            Date.now()
          );

          console.warn(
            "[LiveTransportsMap] Route calculation failed:",
            routeError?.message
          );

          return null;
        }
      },
      []
    );

  const refreshSessions =
    useCallback(
      async ({
        silent = false,
      } = {}) => {
        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        setError(null);

        try {
          const { data: loadsData } =
            await api.get(
              "/loads/summary-with-progress",
              {
                params: {
                  view: "current",
                  withPoints: 0,
                  limit: 5000,
                },
              }
            );

          const loads =
            getArrayPayload(
              loadsData
            );

          const sessionIds =
            [
              ...new Set(
                loads
                  .map(
                    getTransportSessionId
                  )
                  .filter(Boolean)
              ),
            ];

          if (!sessionIds.length) {
            const now =
              new Date();

            setSessions(
              []
            );

            setSelectedSessionId(
              null
            );

            setLastUpdated(
              now
            );

            writeLiveSessionsCache(
              liveSessionsCacheKeyRef
                .current,
              {
                sessions:
                  [],

                lastUpdated:
                  now,

                loadsRev:
                  lastLoadsRevRef
                    .current,
              }
            );

            return;
          }

          const [
            pointsResponse,
            sessionResponses,
          ] = await Promise.all([
            api.get(
              "/pickupdeliveries/by-sessions",
              {
                params: {
                  ids:
                    sessionIds.join(
                      ","
                    ),
                },
              }
            ),

            Promise.allSettled(
              sessionIds.map(
                (sessionId) =>
                  api.get(
                    `/transportsessions/${sessionId}`
                  )
              )
            ),
          ]);

          const rawPoints =
            getArrayPayload(
              pointsResponse.data
            );

          const normalizedPoints =
            rawPoints
              .map(normalizePoint)
              .filter(Boolean);

          /*
           * Guardar todos os pontos recebidos,
           * mesmo os que não têm coordenadas.
           *
           * Estes pontos são apenas para a
           * timeline. Não geram marcadores GPS
           * nem alteram a geometria das rotas.
           */
          const timelinePointsBySession =
            new Map();

          rawPoints.forEach((row, index) => {
            const sessionId =
              getTransportSessionId(row);

            if (!sessionId) return;

            const normalized =
              normalizePoint(row, index);

            const point = normalized || {
              id: row?.id ?? `raw-${index}`,
              transportsessionId: sessionId,
              sequence:
                Number(row?.sequence) ||
                index + 1,
              type: String(
                row?.type ||
                row?.pointType ||
                "STRATEGICPOINT"
              ).toUpperCase(),
              status: String(
                row?.status || ""
              ).toUpperCase(),
              substatus:
                row?.substatus ||
                row?.latestStatusEvent?.newStatus ||
                null,

              name:
                row?.location ||
                row?.name ||
                row?.city ||
                null,
              city:
                row?.city ||
                row?.placeCity ||
                null,
              country:
                row?.country ||
                row?.placeCountry ||
                null,

              countryCode:
                row?.countryCode ||
                row?.country_code ||
                null,
              postalCode:
                row?.postalCode ||
                row?.postcode ||
                row?.zipCode ||
                row?.zip_code ||
                row?.placePostalCode ||
                null,
              date: row?.date || null,
              hour: formatPointHour(row) || null,


              actualArrivalAt:
                row?.actualArrivalAt || null,

              forecast:
                parseMaybeJson(row?.forecast),

              operationMinutes:
                row?.operationMinutes ??
                row?.serviceDurationMinutes ??
                row?.estimatedOperationMinutes ??
                row?.loadingDurationMinutes ??
                row?.unloadingDurationMinutes ??
                null,

              driverId:
                row?.driverId ?? null,
              driverName:
                row?.driverName ||
                row?.driverNameArrival ||
                row?.driverNameDeparture ||
                null,

              driver2Id:
                row?.driver2Id ?? null,
              driver2Name:
                row?.driver2Name ||
                row?.driver2NameArrival ||
                row?.driver2NameDeparture ||
                null,

              loadId:
                row?.loadId ?? null,
              isFrozen:
                row?.isFrozen === true,

              lat: null,
              lng: null,
            };

            if (
              !timelinePointsBySession.has(
                sessionId
              )
            ) {
              timelinePointsBySession.set(
                sessionId,
                []
              );
            }

            timelinePointsBySession
              .get(sessionId)
              .push(point);
          });

          for (
            const points of
            timelinePointsBySession.values()
          ) {
            points.sort(
              (a, b) =>
                a.sequence - b.sequence
            );
          }

          const pointsBySession =
            new Map();

          normalizedPoints.forEach(
            (point) => {
              const sessionId =
                point.transportsessionId;

              if (!sessionId) {
                return;
              }

              if (
                !pointsBySession.has(
                  sessionId
                )
              ) {
                pointsBySession.set(
                  sessionId,
                  []
                );
              }

              pointsBySession
                .get(sessionId)
                .push(point);
            }
          );

          const detailsBySession =
            new Map();

          sessionResponses.forEach(
            (
              response,
              index
            ) => {
              if (
                response.status !==
                "fulfilled"
              ) {
                return;
              }

              const raw =
                response.value.data
                  ?.transportSession ??
                response.value.data
                  ?.item ??
                response.value.data;

              detailsBySession.set(
                sessionIds[index],
                raw
              );
            }
          );

          const loadsBySession =
            new Map();

          loads.forEach((load) => {
            const sessionId =
              getTransportSessionId(
                load
              );

            if (!sessionId) {
              return;
            }

            if (
              !loadsBySession.has(
                sessionId
              )
            ) {
              loadsBySession.set(
                sessionId,
                []
              );
            }

            loadsBySession
              .get(sessionId)
              .push(load);
          });

          const builtSessions =
            await Promise.all(
              sessionIds.map(
                async (
                  sessionId
                ) => {
                  const detail =
                    detailsBySession.get(
                      sessionId
                    ) || {};

                  const sessionLoads =
                    loadsBySession.get(
                      sessionId
                    ) || [];

                  const status =
                    getSessionStatus(
                      detail
                    );

                  /*
                   * O endpoint de Loads já está scoped
                   * com view=current.
                   *
                   * Logo, a TS aparece se tiver pelo
                   * menos uma Current Load.
                   *
                   * Não usamos o status da própria TS
                   * como fonte de verdade para esta view.
                   */
                  if (
                    !sessionLoads.length
                  ) {
                    return null;
                  }

                  const routeSnapshot =
                    parseMaybeJson(
                      detail
                        ?.routeSnapshot
                    );

                  const operationalPoints =
                    (
                      pointsBySession.get(
                        sessionId
                      ) || []
                    )
                      .slice()
                      .sort(
                        (a, b) =>
                          a.sequence -
                          b.sequence
                      );

                  const snapshotPoints =
                    (
                      Array.isArray(
                        routeSnapshot
                          ?.points
                      )
                        ? routeSnapshot.points
                        : []
                    )
                      .map(
                        normalizeSnapshotPoint
                      )
                      .filter(Boolean)
                      .sort(
                        (a, b) =>
                          a.sequence -
                          b.sequence
                      );

                  /*
                   * Quando existe uma rota guardada,
                   * os seus pontos definem o trajeto.
                   *
                   * Os pontos operacionais continuam
                   * a ser apresentados no mapa e usados
                   * no cálculo do progresso, mas não
                   * devem substituir a rota guardada.
                   */
                  const routePoints =
                    snapshotPoints.length >= 2
                      ? snapshotPoints
                      : operationalPoints;

                  const displayPoints =
                    operationalPoints.length
                      ? operationalPoints
                      : routePoints;

                  const storedGeometry =
                    normalizeGeometry(
                      routeSnapshot
                        ?.route
                        ?.geometry
                    );

                  let routeResult = null;

                  if (
                    routePoints.length >= 2
                  ) {
                    routeResult =
                      await fetchRouteGeometry(
                        sessionId,
                        routePoints
                      );
                  }

                  const geometry =
                    routeResult?.geometry ||
                    storedGeometry;

                  // Preferir métricas da rota calculada;
                  // caso não existam, ler o resumo guardado.
                  const routeTotals =
                    routeResult?.routeTotals ||
                    getRouteTotalsFromSummary(
                      routeSnapshot?.route
                    ) ||
                    getRouteTotalsFromSummary(
                      routeSnapshot?.summary
                    ) ||
                    null;

                  // Numa rota de dois pontos, o resumo total
                  // permite mostrar a distância por etapa.
                  // Para 3+ pontos não inventamos repartições.
                  const routeMetrics =
                    routeResult?.routeMetrics?.length
                      ? routeResult.routeMetrics
                      : routePoints.length === 2 &&
                          routeTotals?.km != null &&
                          routeTotals?.minutes != null
                        ? [
                            {
                              id: routePoints[0].id,
                              lat: routePoints[0].lat,
                              lng: routePoints[0].lng,
                              km: 0,
                              minutes: 0,
                            },
                            {
                              id: routePoints[1].id,
                              lat: routePoints[1].lat,
                              lng: routePoints[1].lng,
                              km: routeTotals.km,
                              minutes: routeTotals.minutes,
                            },
                          ]
                        : [];

                  const firstTruck =
                    operationalPoints.find(
                      (point) =>
                        point.truckPlate
                    )?.truckPlate ||
                    sessionLoads.find(
                      (load) =>
                        load?.truckPlate
                    )?.truckPlate ||
                    null;

                  const firstTrailer =
                    operationalPoints.find(
                      (point) =>
                        point.trailerPlate
                    )?.trailerPlate ||
                    detail
                      ?.trailerPlate ||
                    detail
                      ?.trailer
                      ?.trailerPlate ||
                    sessionLoads.find(
                      (load) =>
                        load?.trailerPlate
                    )?.trailerPlate ||
                    sessionLoads.find(
                      (load) =>
                        load?.trailer
                          ?.trailerPlate
                    )?.trailer
                      ?.trailerPlate ||
                    null;

                  const firstDriver =
                    operationalPoints.find(
                      (point) =>
                        point.driverName
                    )?.driverName ||
                    sessionLoads.find(
                      (load) =>
                        load?.driverName
                    )?.driverName ||
                    null;

                  return {
                    id:
                      sessionId,

                    transportSessionNumber:
                      detail
                        ?.transportSessionNumber ??
                      detail
                        ?.transportsessionNumber ??
                      sessionLoads[0]
                        ?.transportSessionNumber ??
                      null,

                    status,

                    routeName:
                      routeSnapshot
                        ?.name ||
                      null,

                    routeNumber:
                      routeSnapshot
                        ?.routeNumber ||
                      null,

                    routeSnapshot,

                    forecast:
                      parseMaybeJson(detail?.forecast),

                    routeTemplateId:
                      detail
                        ?.routeTemplateId ??
                      routeSnapshot
                        ?.templateId ??
                      null,

                    routeTemplateVersion:
                      detail
                        ?.routeTemplateVersion ??
                      routeSnapshot
                        ?.templateVersion ??
                      null,

                    truckPlate:
                      firstTruck,

                    trailerPlate:
                      firstTrailer,

                    driverName:
                      firstDriver,

                    points:
                      displayPoints,

                    operationalPoints,

                    timelinePoints:
                      timelinePointsBySession.get(
                        sessionId
                      ) || [],

                    loads:
                      sessionLoads,

                    progress:
                      calculateProgress(
                        operationalPoints
                      ),

                    geometry,
                    routeMetrics,
                    routeTotals,

                    updatedAt:

                      detail
                        ?.updatedAt ||
                      sessionLoads[0]
                        ?.updatedAt ||
                      null,
                  };
                }
              )
            );

          const activeSessions =
            builtSessions
              .filter(Boolean)
              .sort((a, b) => {


                const numberA =
                  Number(
                    a.transportSessionNumber ||
                    a.id
                  );

                const numberB =
                  Number(
                    b.transportSessionNumber ||
                    b.id
                  );

                return (
                  numberB -
                  numberA
                );
              });

          const now =
            new Date();

          setSessions(
            activeSessions
          );

          setSelectedSessionId(
            (current) => {
              // 1. Prioridade absoluta à sessão recebida
              // através do botão "Ver mapa".
              const requestedId =
                requestedSessionIdRef.current;

              if (requestedId !== null) {
                const requestedSession =
                  activeSessions.find(
                    (session) =>
                      String(session.id) ===
                      String(requestedId)
                  );

                return requestedSession?.id ?? requestedId;
              }

              // 2. Sem pedido específico, preservar
              // a sessão que o utilizador selecionou.
              const stillExists =
                activeSessions.some(
                  (session) =>
                    String(session.id) ===
                    String(current)
                );

              if (stillExists) {
                return current;
              }

              // 3. Só selecionar a primeira sessão
              // quando não existe outra seleção válida.
              return activeSessions[0]?.id ?? null;
            }
          );

          setLastUpdated(
            now
          );

          writeLiveSessionsCache(
            liveSessionsCacheKeyRef
              .current,
            {
              sessions:
                activeSessions,

              lastUpdated:
                now,

              loadsRev:
                lastLoadsRevRef
                  .current,
            }
          );
        } catch (loadError) {
          console.error(
            "[LiveTransportsMap] Failed to load sessions:",
            loadError
          );

          setError(
            loadError?.response?.data
              ?.error ||
            t(
              "map.live.errors.load",
              {
                defaultValue:
                  "Não foi possível carregar os transportes atuais.",
              }
            )
          );
        } finally {
          setLoading(false);
          setRefreshing(false);
        }
      },
      [
        fetchRouteGeometry,
        t,
      ]
    );

    const focusSessionOnMap =
    useCallback(
      (sessionId, selectedPlate = null) => {
        if (
          !mapReady ||
          !mapRef.current
        ) {
          return;
        }

        const targetSessions = selectedPlate
          ? getMapSelectionSessions(
              sessions,
              null,
              selectedPlate
            )
          : getMapSelectionSessions(
              sessions,
              sessionId,
              null
            );

        if (!targetSessions.length) {
          return;
        }

        const map = mapRef.current;

        const bounds =
          new maplibregl.LngLatBounds();

        let coordinateCount = 0;

        const extend = (lngValue, latValue) => {
          if (
            lngValue == null ||
            latValue == null
          ) {
            return;
          }

          const lng = Number(lngValue);
          const lat = Number(latValue);

          if (
            !Number.isFinite(lng) ||
            !Number.isFinite(lat) ||
            Math.abs(lng) > 180 ||
            Math.abs(lat) > 90
          ) {
            return;
          }

          bounds.extend([lng, lat]);
          coordinateCount += 1;
        };

        targetSessions.forEach((session) => {
          const geometryCoordinates =
            session.geometry
              ?.geometry
              ?.coordinates;

          // Incluir toda a geometria do trajeto.
          if (Array.isArray(geometryCoordinates)) {
            geometryCoordinates.forEach((coordinate) => {
              extend(
                coordinate?.[0],
                coordinate?.[1]
              );
            });
          }

          // Incluir sempre os locais de carga,
          // descarga e restantes paragens.
          // Mesmo que a geometria não passe
          // exatamente pelas coordenadas dos locais.
          [
            ...(session.points || []),
            ...(session.operationalPoints || []),
            ...(session.timelinePoints || []),
          ].forEach((point) => {
            extend(
              point?.lng,
              point?.lat
            );
          });
        });


        if (!coordinateCount) {
          return;
        }

        try {
          map.stop();
          map.resize();
        } catch {}

        const mapBounds =
          map.getContainer().getBoundingClientRect();

        const mapRoot =
          mapContainerRef.current
            ?.closest(".live-transports-map");

        const sidebarElement =
          mapRoot?.querySelector(
            ".live-transports-map__sidebar"
          );

        const detailsElement =
          mapRoot?.querySelector(
            ".live-transports-map__selection"
          );

        const sidebarBounds =
          sidebarElement?.getBoundingClientRect();

        const detailsBounds =
          detailsElement?.getBoundingClientRect();

        const mapWidth =
          Math.max(1, mapBounds.width);

        // Verificar se cada painel ocupa
        // realmente uma parte visível do mapa.
        const overlapsMap = (rect) =>
          Boolean(
            rect &&
            rect.width > 0 &&
            rect.height > 0 &&
            rect.right > mapBounds.left &&
            rect.left < mapBounds.right &&
            rect.bottom > mapBounds.top &&
            rect.top < mapBounds.bottom
          );

        // Espaço ocupado pelo painel esquerdo.
        const sidebarOverlap =
          !isSidebarCollapsed && overlapsMap(sidebarBounds)
            ? Math.max(
                0,
                Math.min(
                  mapBounds.right,
                  sidebarBounds.right
                ) - mapBounds.left
              )
            : 0;

        // Espaço ocupado pelo painel direito.
        // Funciona com o Simplificado e o Completo,
        // independentemente do estado do painel esquerdo.
        const detailsOverlap =
          overlapsMap(detailsBounds)
            ? Math.max(
                0,
                mapBounds.right -
                  Math.max(
                    mapBounds.left,
                    detailsBounds.left
                  )
              )
            : 0;

        // Margens de segurança adicionais.
        // Reservar espaço para pins e balões informativos.
        let leftPadding = Math.max(
          130,
          Math.ceil(sidebarOverlap + 120)
        );

        let rightPadding = Math.max(
          100,
          Math.ceil(detailsOverlap + 120)
        );

        // Evitar padding inválido em ecrãs estreitos.
        // Preservar um mínimo de espaço central.
        const maximumPadding =
          Math.max(0, mapWidth - 240);

        const totalPadding =
          leftPadding + rightPadding;

        if (totalPadding > maximumPadding) {
          const ratio =
            maximumPadding / totalPadding;

          leftPadding = Math.floor(
            leftPadding * ratio
          );

          rightPadding = Math.floor(
            rightPadding * ratio
          );
        }



        if (coordinateCount === 1) {
          map.easeTo({
            center:
              bounds.getCenter(),

            zoom:
              11,

            offset: [
              Math.round(
                (leftPadding - rightPadding) / 2
              ),
              0,
            ],

            duration:
              350,
          });

          return;
        }

        map.fitBounds(
          bounds,
          {
            padding: {
              top: 140,
              right: rightPadding,
              bottom: 120,
              left: leftPadding,
            },

            maxZoom:
              12,

            duration:
              650,
          }
        );

      },
      [
        mapReady,
        sessions,
        isSidebarCollapsed,
      ]
    );

  const lastFocusedPanelModeRef =
    useRef(sessionPanelMode);

  useEffect(() => {
    if (
      !mapReady ||
      viewMode === "drivers"
    ) {
      return undefined;
    }

    const modeChanged =
      lastFocusedPanelModeRef.current !==
      sessionPanelMode;

    lastFocusedPanelModeRef.current =
      sessionPanelMode;

    let frame = null;

    const focusCurrentRoute = () => {
      frame = window.requestAnimationFrame(() => {
        if (viewMode === "trucks") {
          if (selectedTruckPlate) {
            focusSessionOnMap(
              null,
              selectedTruckPlate
            );
          }
        } else if (selectedSessionId) {
          focusSessionOnMap(
            selectedSessionId
          );
        }
      });
    };

    // Quando a largura do painel muda,
    // esperar pelo fim da transição visual.
    // Nas restantes situações, focar imediatamente.
    const timer = modeChanged
      ? window.setTimeout(
          focusCurrentRoute,
          280
        )
      : null;

    if (!modeChanged) {
      focusCurrentRoute();
    }

    return () => {
      if (timer !== null) {
        window.clearTimeout(timer);
      }

      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [
    focusSessionOnMap,
    mapReady,
    selectedSessionId,
    selectedTruckPlate,
    sessionPanelMode,
    viewMode,
  ]);


  useEffect(() => {
    if (!transportMapMounted) {
      return undefined;
    }

    const hasCache =
      Boolean(
        initialSessionsCacheRef
          .current
      );

    refreshSessions({
      silent:
        hasCache,
    });

    return undefined;
  }, [
    refreshSessions,
    transportMapMounted,
  ]);

  const liveSessionIdsKey =
    useMemo(
      () =>
        sessions
          .map(
            (
              session
            ) =>
              Number(
                session.id
              )
          )
          .filter(
            (
              sessionId
            ) =>
              Number.isInteger(
                sessionId
              ) &&
              sessionId > 0
          )
          .sort(
            (
              first,
              second
            ) =>
              first -
              second
          )
          .join(
            ","
          ),
      [
        sessions,
      ]
    );

  useEffect(() => {
    const socket =
      getSocket();

    const sessionIds =
      liveSessionIdsKey
        ? liveSessionIdsKey
            .split(",")
            .map(
              Number
            )
            .filter(
              Number.isInteger
            )
        : [];

    const scheduleRefresh =
      () => {
        if (
          socketRefreshTimerRef
            .current
        ) {
          window.clearTimeout(
            socketRefreshTimerRef
              .current
          );
        }

        /*
         * Um único update no backend pode
         * disparar vários eventos quase
         * ao mesmo tempo.
         *
         * Agrupamos tudo num único refresh.
         */
        socketRefreshTimerRef.current =
          window.setTimeout(
            () => {
              socketRefreshTimerRef.current =
                null;

              refreshSessions({
                silent:
                  true,
              });
            },
            120
          );
      };

    const subscribe =
      () => {
        if (
          !socket.connected
        ) {
          return;
        }

        socket.emit(
          "subscribe:loads"
        );

        if (
          sessionIds.length
        ) {
          socket.emit(
            "subscribe:sessions",
            {
              ids:
                sessionIds,
            }
          );
        }
      };

    const handleConnect =
      () => {
        subscribe();

        /*
         * Podemos ter perdido eventos
         * enquanto o socket esteve offline.
         */
        scheduleRefresh();
      };

    const handleLoadsRev =
      (
        payload
      ) => {
        const nextRev =
          Number(
            payload?.loadsRev
          );

        if (
          Number.isFinite(
            nextRev
          )
        ) {
          const previousRev =
            Number(
              lastLoadsRevRef
                .current
            );

          if (
            Number.isFinite(
              previousRev
            ) &&
            nextRev <=
              previousRev
          ) {
            return;
          }

          lastLoadsRevRef.current =
            nextRev;
        }

        scheduleRefresh();
      };

    const handleSessionProgress =
      (
        payload
      ) => {
        const sessionId =
          Number(
            payload?.sessionId ??
            payload?.transportsessionId
          );

        const pct =
          Number(
            payload?.pct
          );

        if (
          Number.isInteger(
            sessionId
          ) &&
          Number.isFinite(
            pct
          )
        ) {
          setSessions(
            (
              current
            ) =>
              current.map(
                (
                  session
                ) =>
                  Number(
                    session.id
                  ) ===
                  sessionId
                    ? {
                        ...session,

                        progress:
                          Math.max(
                            0,
                            Math.min(
                              100,
                              Math.round(
                                pct
                              )
                            )
                          ),
                      }
                    : session
              )
          );
        }

        scheduleRefresh();
      };

    const handlePickupDeliveryUpdated =
      (
        payload
      ) => {
        const pointId =
          payload?.id ??
          payload?.pickupDeliveryId ??
          null;

        if (
          pointId !==
            null &&
          payload?.status
        ) {
          setSessions(
            (
              current
            ) =>
              current.map(
                (
                  session
                ) => {
                  const updatePoints =
                    (
                      points
                    ) =>
                      (
                        points ||
                        []
                      ).map(
                        (
                          point
                        ) =>
                          String(
                            point.id
                          ) ===
                          String(
                            pointId
                          )
                            ? {
                                ...point,

                                status:
                                  String(
                                    payload.status
                                  )
                                    .trim()
                                    .toUpperCase(),
                              }
                            : point
                      );

                  const nextOperationalPoints =
                    updatePoints(
                      session
                        .operationalPoints
                    );

                  return {
                    ...session,

                    operationalPoints:
                      nextOperationalPoints,

                    points:
                      updatePoints(
                        session.points
                      ),

                    progress:
                      calculateProgress(
                        nextOperationalPoints
                      ),
                  };
                }
              )
          );
        }

        scheduleRefresh();
      };

    const genericEvents = [
      "load:created",
      "load:updated",
      "load:deleted",
      "loads:stale",
      "load:points:stale",
      "session:points",
      "session:points:stale",
      "session:route:updated",
      "pd:created",
      "pd:deleted",
      "pd:bulk-driver",
      "pd:bulk-truck",
      "pd:bulk-trailer",
    ];

    socket.on(
      "connect",
      handleConnect
    );

    socket.on(
      "reconnect",
      handleConnect
    );

    socket.on(
      "loads:rev",
      handleLoadsRev
    );

    socket.on(
      "session:progress",
      handleSessionProgress
    );

    socket.on(
      "pd:updated",
      handlePickupDeliveryUpdated
    );

    genericEvents.forEach(
      (
        eventName
      ) => {
        socket.on(
          eventName,
          scheduleRefresh
        );
      }
    );

    if (
      socket.connected
    ) {
      subscribe();
    } else if (
      typeof socket.connect ===
      "function"
    ) {
      socket.connect();
    }

    const handleOnline =
      () => {
        scheduleRefresh();
      };

    const handleVisibilityChange =
      () => {
        if (
          document.visibilityState ===
          "visible"
        ) {
          scheduleRefresh();
        }
      };

    window.addEventListener(
      "online",
      handleOnline
    );

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    /*
     * Safety net:
     *
     * Com WebSocket ligado:
     * zero polling.
     *
     * Se o socket estiver desligado:
     * valida silenciosamente a cada 5 min.
     */
    const safetyInterval =
      window.setInterval(
        () => {
          if (
            !socket.connected
          ) {
            refreshSessions({
              silent:
                true,
            });
          }
        },
        5 * 60 * 1000
      );

    return () => {
      if (
        socketRefreshTimerRef
          .current
      ) {
        window.clearTimeout(
          socketRefreshTimerRef
            .current
        );

        socketRefreshTimerRef.current =
          null;
      }

      window.clearInterval(
        safetyInterval
      );

      socket.off(
        "connect",
        handleConnect
      );

      socket.off(
        "reconnect",
        handleConnect
      );

      socket.off(
        "loads:rev",
        handleLoadsRev
      );

      socket.off(
        "session:progress",
        handleSessionProgress
      );

      socket.off(
        "pd:updated",
        handlePickupDeliveryUpdated
      );

      genericEvents.forEach(
        (
          eventName
        ) => {
          socket.off(
            eventName,
            scheduleRefresh
          );
        }
      );

      window.removeEventListener(
        "online",
        handleOnline
      );

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, [
    liveSessionIdsKey,
    refreshSessions,
  ]);

  useEffect(() => {
    if (
      !transportMapMounted ||
      !mapContainerRef.current
    ) {
      return undefined;
    }

    let resizeObserver =
      null;

    let resizeFrame =
      null;

    const map =
      new maplibregl.Map({
        container:
          mapContainerRef.current,

        style:
          MAP_STYLE,

        center:
          DEFAULT_CENTER,

        zoom:
          DEFAULT_ZOOM,

        attributionControl:
          true,
      });

    mapRef.current = map;

    map.scrollZoom.enable();
    map.boxZoom.enable();
    map.dragPan.enable();
    map.keyboard.enable();
    map.doubleClickZoom.enable();
    map.touchZoomRotate.enable();

    map.addControl(
      new maplibregl.NavigationControl(),
      "top-right"
    );

    const resizeMap = () => {
      if (resizeFrame) {
        window.cancelAnimationFrame(
          resizeFrame
        );
      }

      resizeFrame =
        window.requestAnimationFrame(
          () => {
            try {
              map.resize();
            } catch {}
          }
        );
    };

    if (
      typeof ResizeObserver !==
      "undefined"
    ) {
      resizeObserver =
        new ResizeObserver(
          resizeMap
        );

      resizeObserver.observe(
        mapContainerRef.current
      );
    }

    window.addEventListener(
      "resize",
      resizeMap
    );

    const handleRouteClick =
      (event) => {
        const sessionId =
          Number(
            event?.features?.[0]
              ?.properties
              ?.sessionId
          );

        if (
          Number.isInteger(sessionId) &&
          sessionId > 0
        ) {
          setSelectedSessionId(sessionId);
          setViewMode("sessions");
        }
      };

    const handlePointClick =
      (event) => {
        const sessionId =
          Number(
            event?.features?.[0]
              ?.properties
              ?.sessionId
          );

        if (
          Number.isInteger(sessionId) &&
          sessionId > 0
        ) {
          setSelectedSessionId(sessionId);
          setViewMode("sessions");
        }
      };
    
    const handleClusterClick =
      async (event) => {
        const feature =
          event?.features?.[0];

        const clusterId =
          feature?.properties
            ?.cluster_id;

        const coordinates =
          feature?.geometry
            ?.coordinates;

        if (
          clusterId === null ||
          clusterId === undefined ||
          !Array.isArray(
            coordinates
          )
        ) {
          return;
        }

        const source =
          map.getSource(
            POINTS_SOURCE_ID
          );

        if (
          !source ||
          typeof source
            .getClusterExpansionZoom !==
            "function"
        ) {
          return;
        }

        try {
          const zoom =
            await source
              .getClusterExpansionZoom(
                clusterId
              );

          map.easeTo({
            center:
              coordinates,

            zoom:
              Math.min(
                Number(zoom) || 12,
                15
              ),

            duration:
              350,
          });
        } catch (clusterError) {
          console.warn(
            "[LiveTransportsMap] Could not expand cluster:",
            clusterError?.message
          );
        }
      };

    const handlePointerEnter =
      () => {
        map.getCanvas().style.cursor =
          "pointer";
      };

    const handlePointerLeave =
      () => {
        map.getCanvas().style.cursor =
          "";
      };

    const handleLoad = () => {
      map.addSource(
        ROUTES_SOURCE_ID,
        {
          type: "geojson",

          data: {
            type:
              "FeatureCollection",

            features: [],
          },
        }
      );

      map.addLayer({
        id:
          ROUTES_CASING_LAYER_ID,

        type:
          "line",

        source:
          ROUTES_SOURCE_ID,

        layout: {
          "line-cap":
            "round",

          "line-join":
            "round",
        },

        paint: {
          "line-color":
            "#ffffff",

          "line-width": [
            "case",

            [
              "boolean",
              ["get", "selected"],
              false,
            ],

            10,
            7,
          ],

          "line-opacity":
            0.94,
        },
      });

      map.addLayer({
        id:
          ROUTES_LINE_LAYER_ID,

        type:
          "line",

        source:
          ROUTES_SOURCE_ID,

        layout: {
          "line-cap":
            "round",

          "line-join":
            "round",
        },

        paint: {
          "line-color": [
            "case",

            [
              "boolean",
              ["get", "selected"],
              false,
            ],

            "#2563eb",
            "#64748b",
          ],

          "line-width": [
            "case",

            [
              "boolean",
              ["get", "selected"],
              false,
            ],

            6,
            3.5,
          ],

          "line-opacity": [
            "case",

            [
              "boolean",
              ["get", "selected"],
              false,
            ],

            1,
            0.55,
          ],
        },
      });

      try {
        ensureRoadReferenceOverlay(
          map
        );
      } catch (
        roadReferenceError
      ) {
        console.warn(
          "[LiveTransportsMap] Could not initialise road references:",
          roadReferenceError
        );
      }

      map.addSource(
        POINTS_SOURCE_ID,
        {
          type: "geojson",

          cluster: true,

          clusterMaxZoom:
            14,

          clusterRadius:
            42,

          data: {
            type:
              "FeatureCollection",

            features: [],
          },
        }
      );

      map.addLayer({
        id:
          POINTS_CLUSTER_LAYER_ID,

        type:
          "circle",

        source:
          POINTS_SOURCE_ID,

        filter: [
          "has",
          "point_count",
        ],

        paint: {
          "circle-radius": [
            "step",

            [
              "get",
              "point_count",
            ],

            18,

            5,
            21,

            10,
            24,

            25,
            28,
          ],

          "circle-color": [
            "step",

            [
              "get",
              "point_count",
            ],

            "#0b8dbd",

            5,
            "#2563eb",

            10,
            "#1d4ed8",

            25,
            "#1e3a8a",
          ],

          "circle-stroke-color":
            "#ffffff",

          "circle-stroke-width":
            3,

          "circle-opacity":
            0.96,
        },
      });

      map.addLayer({
        id:
          POINTS_CLUSTER_COUNT_LAYER_ID,

        type:
          "symbol",

        source:
          POINTS_SOURCE_ID,

        filter: [
          "has",
          "point_count",
        ],

        layout: {
          "text-field": [
            "get",
            "point_count_abbreviated",
          ],

          "text-size":
            12,

          "text-allow-overlap":
            true,

          "text-ignore-placement":
            true,
        },

        paint: {
          "text-color":
            "#ffffff",

          "text-halo-color":
            "rgba(15, 23, 42, 0.12)",

          "text-halo-width":
            0.5,
        },
      });

      map.addLayer({
        id:
          POINTS_HALO_LAYER_ID,

        type:
          "circle",

        source:
          POINTS_SOURCE_ID,

        filter: [
          "!",
          [
            "has",
            "point_count",
          ],
        ],

        paint: {
          "circle-radius": [
            "case",

            [
              "boolean",
              ["get", "selected"],
              false,
            ],

            16,
            9,
          ],

          "circle-color":
            "#ffffff",

          "circle-opacity":
            0.96,

          "circle-stroke-color":
            "rgba(15, 23, 42, 0.14)",

          "circle-stroke-width":
            1,
        },
      });

      map.addLayer({
        id:
          POINTS_CIRCLE_LAYER_ID,

        type:
          "circle",

        source:
          POINTS_SOURCE_ID,

        filter: [
          "!",
          [
            "has",
            "point_count",
          ],
        ],

        paint: {
          "circle-radius": [
            "case",

            [
              "boolean",
              ["get", "selected"],
              false,
            ],

            12,
            6,
          ],

          "circle-color": [
            "match",

            ["get", "type"],

            "PICKUP",
            "#0b8dbd",

            "ORIGIN",
            "#0b8dbd",

            "DELIVERY",
            "#111827",

            "DESTINATION",
            "#111827",

            "FUEL",
            "#f59e0b",

            "STRATEGICPOINT",
            "#9333ea",

            "VIA",
            "#9333ea",

            "#64748b",
          ],

          "circle-stroke-color":
            "#ffffff",

          "circle-stroke-width":
            2,
        },
      });

      map.addLayer({
        id:
          POINTS_LABEL_LAYER_ID,

        type:
          "symbol",

        source:
          POINTS_SOURCE_ID,

        filter: [
          "all",

          [
            "!",
            [
              "has",
              "point_count",
            ],
          ],

          [
            "==",
            [
              "get",
              "showLabel",
            ],
            true,
          ],
        ],

        layout: {
          "text-field":
            ["get", "label"],

          "text-size":
            10,

          "text-allow-overlap":
            true,

          "text-ignore-placement":
            true,
        },

        paint: {
          "text-color":
            "#ffffff",

          "text-halo-color":
            "rgba(15, 23, 42, 0.08)",

          "text-halo-width":
            0.4,
        },
      });

      map.on(
        "click",
        ROUTES_LINE_LAYER_ID,
        handleRouteClick
      );

      map.on(
        "click",
        POINTS_CLUSTER_LAYER_ID,
        handleClusterClick
      );

      map.on(
        "click",
        POINTS_CIRCLE_LAYER_ID,
        handlePointClick
      );

      map.on(
        "mouseenter",
        ROUTES_LINE_LAYER_ID,
        handlePointerEnter
      );

      map.on(
        "mouseleave",
        ROUTES_LINE_LAYER_ID,
        handlePointerLeave
      );

      map.on(
        "mouseenter",
        POINTS_CLUSTER_LAYER_ID,
        handlePointerEnter
      );

      map.on(
        "mouseleave",
        POINTS_CLUSTER_LAYER_ID,
        handlePointerLeave
      );

      map.on(
        "mouseenter",
        POINTS_CIRCLE_LAYER_ID,
        handlePointerEnter
      );

      map.on(
        "mouseleave",
        POINTS_CIRCLE_LAYER_ID,
        handlePointerLeave
      );

      resizeMap();

      window.setTimeout(
        resizeMap,
        100
      );

      setMapReady(true);
    };

    map.on(
      "load",
      handleLoad
    );

    return () => {
      setMapReady(false);

      resizeObserver?.disconnect();

      window.removeEventListener(
        "resize",
        resizeMap
      );

      if (resizeFrame) {
        window.cancelAnimationFrame(
          resizeFrame
        );
      }

      try {
        map.off(
          "load",
          handleLoad
        );

        if (
          map.getLayer(
            ROUTES_LINE_LAYER_ID
          )
        ) {
          map.off(
            "click",
            ROUTES_LINE_LAYER_ID,
            handleRouteClick
          );

          map.off(
            "mouseenter",
            ROUTES_LINE_LAYER_ID,
            handlePointerEnter
          );

          map.off(
            "mouseleave",
            ROUTES_LINE_LAYER_ID,
            handlePointerLeave
          );
        }

        if (
          map.getLayer(
            POINTS_CLUSTER_LAYER_ID
          )
        ) {
          map.off(
            "click",
            POINTS_CLUSTER_LAYER_ID,
            handleClusterClick
          );

          map.off(
            "mouseenter",
            POINTS_CLUSTER_LAYER_ID,
            handlePointerEnter
          );

          map.off(
            "mouseleave",
            POINTS_CLUSTER_LAYER_ID,
            handlePointerLeave
          );
        }

        if (
          map.getLayer(
            POINTS_CIRCLE_LAYER_ID
          )
        ) {
          map.off(
            "click",
            POINTS_CIRCLE_LAYER_ID,
            handlePointClick
          );

          map.off(
            "mouseenter",
            POINTS_CIRCLE_LAYER_ID,
            handlePointerEnter
          );

          map.off(
            "mouseleave",
            POINTS_CIRCLE_LAYER_ID,
            handlePointerLeave
          );
        }

        map.remove();
      } catch {}

      if (
        mapRef.current === map
      ) {
        mapRef.current = null;
      }
    };
  }, [
    transportMapMounted,
  ]);


  useEffect(() => {
    if (viewMode === "drivers" || !mapReady || !mapRef.current) {
      return;
    }

    const map = mapRef.current;
    const truckMode = viewMode === "trucks";
    const markers = [];
    const popups = [];
    const automaticPopups = [];
    let selectedPopup = null;


    map.getSource(ROUTES_SOURCE_ID)?.setData?.(
      buildRoutesFeatureCollection(
        sessions,
        truckMode ? null : selectedSessionId,
        truckMode ? selectedTruckPlate : null
      )
    );

    // Remover os círculos e letras antigos, incluindo "D".
    map.getSource(POINTS_SOURCE_ID)?.setData?.({
      type: "FeatureCollection",
      features: [],
    });

    const stops = getMapSelectionSessions(
      sessions,
      truckMode ? null : selectedSessionId,
      truckMode ? selectedTruckPlate : null
    ).flatMap((session) =>
      (truckMode ? getSessionTimelinePoints(session) : session.points || [])
        .filter((point) =>
          point?.lat != null &&
          point?.lng != null &&
          Number.isFinite(Number(point.lat)) &&
          Number.isFinite(Number(point.lng)) &&
          Math.abs(Number(point.lat)) <= 90 &&
          Math.abs(Number(point.lng)) <= 180
        )
        .map((point, index) => ({ session, point, index }))
    );

    const getKind = (point) => {
      const type = String(point.type || "").toUpperCase();
      if (["PICKUP", "ORIGIN"].includes(type)) return "pickup";
      if (["DELIVERY", "DESTINATION"].includes(type)) return "delivery";
      return "other";
    };

    // Mostrar automaticamente apenas a primeira carga
    // e a última descarga, evitando excesso de cartões.
    const firstPickup = stops.find(({ point }) => getKind(point) === "pickup");
    const lastDelivery = [...stops].reverse().find(
      ({ point }) => getKind(point) === "delivery"
    );

    stops.forEach((stop) => {
      const { session, point, index } = stop;
      const kind = getKind(point);
      const label = kind === "pickup" ? "Carga"
        : kind === "delivery" ? "Descarga" : "Paragem";
      const coords = [Number(point.lng), Number(point.lat)];

      const pin = document.createElement("button");
      pin.type = "button";
      pin.className = `ld-map-stop-pin is-${kind}`;
      pin.title = `${label}: ${point.name || point.city || "Localização"}`;
      pin.setAttribute("aria-label", pin.title);

      if (kind === "other") {
        pin.textContent = String(point.sequence || index + 1);
      } else {
        const image = document.createElement("img");
        image.src = kind === "pickup" ? pickupPinImage : deliveryPinImage;
        image.alt = "";
        image.draggable = false;
        pin.appendChild(image);
      }

      const marker = new maplibregl.Marker({ element: pin, anchor: "bottom" })
        .setLngLat(coords)
        .addTo(map);
      markers.push(marker);

      const card = document.createElement("div");
      card.className = `ld-map-stop-card is-${kind}`;

      const header = document.createElement("div");
      header.className = "ld-map-stop-card__header";
      const symbol = document.createElement("span");
      symbol.className = "ld-map-stop-card__symbol";
      symbol.textContent = kind === "pickup" ? "↑"
        : kind === "delivery" ? "↓" : String(point.sequence || index + 1);
      const heading = document.createElement("strong");
      heading.textContent = label;
      header.append(symbol, heading);

      const place = document.createElement("div");
      place.className = "ld-map-stop-card__place";

      const placeName = document.createElement("strong");
      placeName.textContent =
        point.name || point.city || point.address || "Localização sem nome";

      place.appendChild(placeName);

      const locationText = [point.city, point.country]
        .filter((value) =>
          value &&
          String(value).trim().toLowerCase() !==
            String(placeName.textContent).trim().toLowerCase()
        )
        .filter((value, index, values) =>
          values.findIndex((item) =>
            String(item).trim().toLowerCase() ===
            String(value).trim().toLowerCase()
          ) === index
        )
        .join(", ");

      const subtitle =
        locationText ||
        (point.address !== placeName.textContent ? point.address : "");

      if (subtitle) {
        const placeLocation = document.createElement("small");
        placeLocation.textContent = subtitle;
        place.appendChild(placeLocation);
      }

      const time = document.createElement("div");
      time.className = "ld-map-stop-card__time";
      time.textContent = formatPointDateTime(point, locale) || "Horário não definido";

      card.append(header, place, time);

      const status = String(point.status || "").toUpperCase();
      const delayed = String(point.substatus || "").toUpperCase() === "DELAYED";
      const statusText = delayed ? "Atrasada"
        : status === "COMPLETED" ? "Concluída"
        : status === "IN_PROGRESS" ? "Em curso"
        : status === "CANCELLED" ? "Cancelada"
        : ["PENDING", "PLANNED", "SCHEDULED"].includes(status) ? "Programada"
        : "";

      if (statusText) {
        const statusElement = document.createElement("span");
        statusElement.className = `ld-map-stop-card__status${delayed ? " is-delayed" : ""}`;
        statusElement.textContent = statusText;
        card.appendChild(statusElement);
      }

      const popup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        focusAfterOpen: false,
        offset: 35,
        maxWidth: "280px",
        className: "ld-map-stop-popup",
      })
        .setLngLat(coords)
        .setDOMContent(card);
      popups.push(popup);

      pin.addEventListener("click", (event) => {
        event.stopPropagation();

        if (truckMode) {
          setSelectedSessionId(session.id);
          setViewMode("sessions");
          return;
        }

        const alreadySelected =
          selectedPopup === popup && popup.isOpen();

        selectedPopup = alreadySelected ? null : popup;

        // Nunca manter dois balões abertos
        // quando o utilizador seleciona um ponto.
        popups.forEach((item) => item.remove());

        if (selectedPopup) {
          selectedPopup.addTo(map);

          selectedPopup.getElement()
            ?.style.removeProperty("visibility");

          revealSelectedPopup(selectedPopup);
        } else {
          refreshAutomaticPopups();
        }
      });

      if (
        !truckMode &&
        (stop === firstPickup || stop === lastDelivery)
      ) {
        automaticPopups.push(popup);
      }
    });

    // Área realmente disponível entre os painéis.
    const getFreeMapArea = () => {
      const mapBounds =
        map.getContainer().getBoundingClientRect();

      const root = map.getContainer().closest(
        ".live-transports-map"
      );

      const sidebarBounds = root
        ?.querySelector(".live-transports-map__sidebar")
        ?.getBoundingClientRect();

      const detailsBounds = root
        ?.querySelector(".live-transports-map__selection")
        ?.getBoundingClientRect();

      const overlapsMap = (rect) =>
        Boolean(
          rect &&
          rect.width > 0 &&
          rect.height > 0 &&
          rect.right > mapBounds.left &&
          rect.left < mapBounds.right &&
          rect.bottom > mapBounds.top &&
          rect.top < mapBounds.bottom
        );

      const margin = 18;

      return {
        left:
          !isSidebarCollapsed && overlapsMap(sidebarBounds)
            ? Math.max(
                mapBounds.left + margin,
                sidebarBounds.right + margin
              )
            : mapBounds.left + margin,

        right: overlapsMap(detailsBounds)
          ? Math.min(
              mapBounds.right - margin,
              detailsBounds.left - margin
            )
          : mapBounds.right - margin,

        top: mapBounds.top + margin,
        bottom: mapBounds.bottom - margin,
      };
    };

    // Se o utilizador abrir um balão junto
    // a um painel, deslocar suavemente o mapa
    // para que o conteúdo fique visível.
    const revealSelectedPopup = (popup) => {
      const rect = popup.getElement()
        ?.getBoundingClientRect();

      if (!rect) return;

      const area = getFreeMapArea();

      // Não tentar compensar quando o espaço
      // disponível é menor do que o balão.
      if (
        area.right - area.left < rect.width ||
        area.bottom - area.top < rect.height
      ) {
        return;
      }

      let shiftX = 0;
      let shiftY = 0;

      if (rect.left < area.left) {
        shiftX = area.left - rect.left;
      } else if (rect.right > area.right) {
        shiftX = area.right - rect.right;
      }

      if (rect.top < area.top) {
        shiftY = area.top - rect.top;
      } else if (rect.bottom > area.bottom) {
        shiftY = area.bottom - rect.bottom;
      }

      if (
        Math.abs(shiftX) > 2 ||
        Math.abs(shiftY) > 2
      ) {
        map.panBy(
          [-shiftX, -shiftY],
          {
            duration: 350,
            essential: true,
          }
        );
      }
    };


    // Avaliar cada balão individualmente.
    // Mostrar tantos quantos couberem sem sobreposição.
    const refreshAutomaticPopups = () => {
      if (truckMode || selectedPopup) {
        return;
      }

      popups.forEach((popup) => popup.remove());

      const area = getFreeMapArea();
      const safetyGap = 16;

      if (
        area.right <= area.left ||
        area.bottom <= area.top
      ) {
        return;
      }

      const centerX = (area.left + area.right) / 2;
      const centerY = (area.top + area.bottom) / 2;

      const mapRect = map
        .getContainer()
        .getBoundingClientRect();

      // Fazer o MapLibre considerar os painéis
      // ao escolher automaticamente a posição dos balões.
      const popupPadding = {
        left: Math.max(
          0,
          Math.ceil(area.left - mapRect.left + 8)
        ),
        right: Math.max(
          0,
          Math.ceil(mapRect.right - area.right + 8)
        ),
        top: Math.max(
          0,
          Math.ceil(area.top - mapRect.top)
        ),
        bottom: Math.max(
          0,
          Math.ceil(mapRect.bottom - area.bottom)
        ),
      };

      // Tolerância para diferenças de arredondamento.
      const tolerance = 6;

      // Medir cada balão depois de o MapLibre
      // ajustar a sua posição à área disponível.
      const candidates = [
        ...new Set(automaticPopups),
      ]
        .map((popup) => {
          popup.addTo(map);

          // O MapLibre adapta a âncora do balão
          // às margens livres entre os painéis.
          popup.setPadding(popupPadding);

          const element = popup.getElement();

          if (!element) {
            popup.remove();
            return null;
          }

          element.style.visibility = "hidden";

          const rect = element.getBoundingClientRect();

          const projected = map.project(
            popup.getLngLat()
          );

          const pinX = mapRect.left + projected.x;
          const pinY = mapRect.top + projected.y;

          const fits =
            rect.width > 0 &&
            rect.height > 0 &&
            rect.left >= area.left - tolerance &&
            rect.right <= area.right + tolerance &&
            rect.top >= area.top - tolerance &&
            rect.bottom <= area.bottom + tolerance;

          if (!fits) {
            popup.remove();
            return null;
          }

          return {
            popup,
            element,
            rect,
            distance: Math.hypot(
              pinX - centerX,
              pinY - centerY
            ),
          };
        })
        .filter(Boolean);


      // Primeiro avaliar o ponto mais central.
      candidates.sort(
        (a, b) => a.distance - b.distance
      );

      const visible = [];

      candidates.forEach((candidate) => {
        const rect = candidate.rect;

        // Verificar se colide com algum
        // balão já escolhido.
        const overlaps = visible.some((shown) => {
          const other = shown.rect;

          return !(
            rect.right + safetyGap <= other.left ||
            other.right + safetyGap <= rect.left ||
            rect.bottom + safetyGap <= other.top ||
            other.bottom + safetyGap <= rect.top
          );
        });

        if (overlaps) {
          candidate.popup.remove();
          return;
        }

        visible.push(candidate);
      });

      // Mostrar apenas os balões aprovados.
      visible.forEach(({ element }) => {
        element.style.visibility = "";
      });
    };


    // Durante movimentos, esconder apenas
    // os balões automáticos.
    const handleMapMoveStart = () => {
      if (!selectedPopup) {
        automaticPopups.forEach(
          (popup) => popup.remove()
        );
      }
    };

    // Um novo zoom devolve o controlo
    // ao sistema de apresentação automática.
    const handleZoomStart = () => {
      selectedPopup = null;

      popups.forEach((popup) => {
        popup.remove();
      });
    };

    // Reavaliar cada paragem após o movimento.
    const handleMapMoveEnd = () => {
      if (!selectedPopup) {
        refreshAutomaticPopups();
      }
    };

    map.on("movestart", handleMapMoveStart);
    map.on("zoomstart", handleZoomStart);
    map.on("moveend", handleMapMoveEnd);
    map.on("resize", handleMapMoveEnd);

    // Avaliação inicial.
    refreshAutomaticPopups();

    return () => {
      map.off("movestart", handleMapMoveStart);
      map.off("zoomstart", handleZoomStart);
      map.off("moveend", handleMapMoveEnd);
      map.off("resize", handleMapMoveEnd);

      popups.forEach((popup) => popup.remove());
      markers.forEach((marker) => marker.remove());
    };


  }, [
    mapReady,
    selectedSessionId,
    selectedTruckPlate,
    sessions,
    viewMode,
    locale,
    isSidebarCollapsed,
  ]);



  useEffect(() => {
    let secondFrame = null;

    const resizeMap = () => {
      try {
        mapRef.current?.resize();
      } catch {}
    };

    const firstFrame =
      window.requestAnimationFrame(
        () => {
          resizeMap();

          secondFrame =
            window.requestAnimationFrame(
              resizeMap
            );
        }
      );

    const timer =
      window.setTimeout(
        resizeMap,
        60
      );

    return () => {
      window.cancelAnimationFrame(
        firstFrame
      );

      if (secondFrame) {
        window.cancelAnimationFrame(
          secondFrame
        );
      }

      window.clearTimeout(
        timer
      );
    };
  }, [isSidebarCollapsed]);

  const [sessionFilter, setSessionFilter] =
    useState("all");

  const pendingRequestedSessionRef =
    useRef(requestedSessionId);

  useEffect(() => {
    pendingRequestedSessionRef.current =
      requestedSessionId;

    if (requestedSessionId === null) {
      return;
    }

    setViewMode("sessions");
    setSessionFilter("all");
    setSearchTerm("");
    setSelectedSessionId(requestedSessionId);
  }, [requestedSessionId, location.key]);

  const classifySession =
    (session) => {
      const status = getSessionStatus(session);

      const operationalPoints =
        session.operationalPoints || [];

      const delayed =
        operationalPoints.some(
          (point) =>
            String(
              point.substatus || ""
            ).toUpperCase() === "DELAYED"
        );

      if (delayed) {
        return "delayed";
      }

      if (
        ["PENDING", "PLANNED", "SCHEDULED"]
          .includes(status)
      ) {
        return "planned";
      }

      if (
        ["COMPLETED", "CANCELLED"]
          .includes(status)
      ) {
        return "other";
      }

      return "active";
    };

  const sessionCounts =
    useMemo(() => {
      const counts = {
        all: sessions.length,
        active: 0,
        delayed: 0,
        planned: 0,
      };

      sessions.forEach((session) => {
        const category =
          classifySession(session);

        if (
          Object.prototype.hasOwnProperty.call(
            counts,
            category
          )
        ) {
          counts[category] += 1;
        }
      });

      return counts;
    }, [sessions]);

  const filteredSessions =
    useMemo(() => {
      const cleanSearch =
        searchTerm
          .trim()
          .toLowerCase();

      return sessions.filter((session) => {
        if (
          sessionFilter !== "all" &&
          classifySession(session) !==
            sessionFilter
        ) {
          return false;
        }

        if (!cleanSearch) {
          return true;
        }

        const values = [
          getSessionDisplayNumber(session),
          session.routeName,
          session.truckPlate,
          session.trailerPlate,
          session.driverName,
          session.status,

          ...(session.points || []).map(
            (point) =>
              [
                point.name,
                point.city,
                point.country,
                point.address,
              ]
                .filter(Boolean)
                .join(" ")
          ),
        ];

        return values.some(
          (value) =>
            String(value || "")
              .toLowerCase()
              .includes(cleanSearch)
        );
      });
    }, [
      searchTerm,
      sessionFilter,
      sessions,
    ]);

  /*
   * Camiões associados às sessões atuais.
   * Uma matrícula aparece apenas uma vez.
   * Não depende do filtro das sessões.
   */
  const assignedTrucks = useMemo(() => {
    const trucksByPlate = new Map();

    sessions.forEach((session) => {
      const plate = String(
        session.truckPlate || ""
      ).trim();

      if (!plate) return;

      const key = plate.toUpperCase();

      const existing = trucksByPlate.get(key);

      if (!existing) {
        trucksByPlate.set(key, {
          plate,
          session,
          sessions: [session],
          sessionCount: 1,
        });

        return;
      }

      existing.sessions.push(session);
      existing.sessionCount += 1;

      const existingIsActive =
        [
          "ACTIVE",
          "IN_PROGRESS",
          "ONGOING",
        ].includes(
          String(
            existing.session.status || ""
          ).toUpperCase()
        );

      const currentIsActive =
        [
          "ACTIVE",
          "IN_PROGRESS",
          "ONGOING",
        ].includes(
          String(
            session.status || ""
          ).toUpperCase()
        );

      if (
        currentIsActive &&
        !existingIsActive
      ) {
        existing.session = session;
      }
    });

    return Array.from(
      trucksByPlate.values()
    ).sort((a, b) =>
      a.plate.localeCompare(
        b.plate,
        "pt"
      )
    );
  }, [sessions]);

  const filteredTrucks = useMemo(() => {
    const query = searchTerm
      .trim()
      .toLowerCase();

    if (!query) {
      return assignedTrucks;
    }

    return assignedTrucks.filter(
      ({ plate, session }) => {
        const searchable = [
          plate,
          session.driverName,
          session.routeName,
          getSessionDisplayNumber(session),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchable.includes(query);
      }
    );
  }, [
    assignedTrucks,
    searchTerm,
  ]);

  const selectedTruck = useMemo(() => {
    if (!selectedTruckPlate) {
      return null;
    }

    return assignedTrucks.find(
      (truck) =>
        truck.plate.toUpperCase() ===
        selectedTruckPlate.toUpperCase()
    ) || null;
  }, [
    assignedTrucks,
    selectedTruckPlate,
  ]);

  const selectedTruckGpsMock =
    selectedTruck?.plate?.toUpperCase() ===
    "07-UP-51";

  const selectedTruckProgress = useMemo(() => {
    const totals = (
      selectedTruck?.sessions || []
    ).reduce(
      (acc, session) => {
        const points =
          session.operationalPoints?.length
            ? session.operationalPoints
            : session.points || [];

        acc.total += points.length;

        acc.completed +=
          calculateCompletedPointCount(
            points
          );

        return acc;
      },
      {
        total: 0,
        completed: 0,
      }
    );

    return {
      ...totals,

      percent: totals.total
        ? Math.round(
            (totals.completed /
              totals.total) * 100
          )
        : 0,
    };
  }, [
    selectedTruck,
  ]);

  const selectedSession =
    useMemo(() => {
      return sessions.find(
        (session) =>
          String(session.id) ===
          String(
            selectedSessionId
          )
      ) || null;
    }, [
      selectedSessionId,
      sessions,
    ]);

  const planningForecast = useMemo(
    () =>
      selectedSession
        ? buildSessionOperationalForecast(
            selectedSession
          )
        : null,
    [selectedSession]
  );

  const focusStopOnMap = (point) => {
    const map = mapRef.current;

    if (!map || !mapReady || !point) {
      return;
    }

    // Encontrar as coordenadas reais da paragem.
    const candidates = [
      point,
      ...(selectedSession?.points || []),
      ...(selectedSession?.operationalPoints || []),
      ...(selectedSession?.timelinePoints || []),
    ];

    const target = candidates.find((candidate) => {
      if (String(candidate?.id) !== String(point.id)) {
        return false;
      }

      if (candidate?.lat == null || candidate?.lng == null) {
        return false;
      }

      const lat = Number(candidate.lat);
      const lng = Number(candidate.lng);

      return (
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        Math.abs(lat) <= 90 &&
        Math.abs(lng) <= 180
      );
    });

    if (!target) {
      window.alert(
        "Esta paragem não tem coordenadas definidas no mapa."
      );
      return;
    }

    const lat = Number(target.lat);
    const lng = Number(target.lng);

    // Calcular o espaço visível entre os painéis.
    const mapBounds =
      map.getContainer().getBoundingClientRect();

    const mapRoot = mapContainerRef.current?.closest(
      ".live-transports-map"
    );

    const sidebarBounds = mapRoot
      ?.querySelector(".live-transports-map__sidebar")
      ?.getBoundingClientRect();

    const panelBounds = mapRoot
      ?.querySelector(".live-transports-map__selection")
      ?.getBoundingClientRect();

    const leftOccupied =
      !isSidebarCollapsed && sidebarBounds
        ? Math.max(0, sidebarBounds.right - mapBounds.left)
        : 0;

    const rightOccupied = panelBounds
      ? Math.max(0, mapBounds.right - panelBounds.left)
      : 0;

    const maxOffset = Math.min(mapBounds.width * 0.25, 220);

    const offsetX = Math.max(
      -maxOffset,
      Math.min(
        maxOffset,
        (leftOccupied - rightOccupied) / 2
      )
    );

    // Animação suave até ao ponto selecionado.
    map.stop();

    map.flyTo({
      center: [lng, lat],
      zoom: Math.max(map.getZoom(), 14),
      offset: [Math.round(offsetX), 0],
      duration: 650,
      essential: true,
    });
  };

  const handleVehicleSelect = async (resource) => {
    if (!vehiclePicker || savingVehicle) return;


    const kind = vehiclePicker.kind;
    const pointId = vehiclePicker.point.id;

    const resourceId = resource?.id ?? null;

    const rawPlate =
      kind === "truck"
        ? resource?.plate ??
          resource?.truckPlate ??
          resource?.manualPlate
        : resource?.trailerPlate ??
          resource?.trailerNumber ??
          resource?.plate ??
          resource?.manualPlate;

    const plate = String(rawPlate || "")
      .trim()
      .toUpperCase() || null;

    if (resourceId == null && !plate) return;

    setSavingVehicle(true);

    try {
      const response = await api.get(
        `/pickupdeliveries/by-id/${pointId}`
      );

      const freshPoint =
        response.data?.pickupDelivery ??
        response.data?.pd ??
        response.data?.row ??
        response.data;

      if (
        !freshPoint ||
        String(freshPoint.id) !== String(pointId)
      ) {
        throw new Error(
          "Não foi possível confirmar a paragem."
        );
      }

      const freshSessionId =
        getTransportSessionId(freshPoint);

      if (
        freshSessionId != null &&
        String(freshSessionId) !==
          String(vehiclePicker.sessionId)
      ) {
        throw new Error(
          "A paragem já não pertence a esta sessão."
        );
      }

      if (
        FINAL_STATUSES.has(
          String(freshPoint.status || "").toUpperCase()
        ) ||
        freshPoint.isFrozen === true
      ) {
        throw new Error(
          "Não é possível alterar recursos numa paragem bloqueada."
        );
      }

      const type = String(
        freshPoint.type || ""
      ).toUpperCase();

      if (!["PICKUP", "DELIVERY"].includes(type)) {
        throw new Error(
          "Este ponto não permite esta atribuição."
        );
      }

      if (kind === "truck") {
        await api.put(
          `/pickupdeliveries/${pointId}/assign-truck`,
          {
            truckId: resourceId,
            truckPlate:
              resourceId == null ? plate : null,
            type: type.toLowerCase(),
          }
        );
      } else if (kind === "trailer") {
        await api.put(
          `/pickupdeliveries/${pointId}/assign-trailer`,
          {
            trailerId: resourceId,
            trailerPlate:
              resourceId == null ? plate : null,
          }
        );
      }

      await refreshSessions({ silent: true });

      setVehiclePicker(null);
    } catch (error) {
      console.error(
        "[LiveTransportsMap] Vehicle assignment:",
        error
      );

      window.alert(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Não foi possível atribuir o recurso."
      );
    } finally {
      setSavingVehicle(false);
    }
  };

  const reorderablePoints =
    useMemo(() => {
      if (!selectedSession) {
        return null;
      }

      const visiblePoints =
        getSessionTimelinePoints(selectedSession);

      const operationalPoints =
        selectedSession.timelinePoints?.length
          ? selectedSession.timelinePoints
          : selectedSession.operationalPoints || [];

      if (
        visiblePoints.length < 2 ||
        visiblePoints.length !== operationalPoints.length
      ) {
        return null;
      }

      const visibleIds =
        visiblePoints.map((point) =>
          String(point?.id ?? "")
        );

      const operationalIds =
        operationalPoints.map((point) =>
          String(point?.id ?? "")
        );

      if (
        visibleIds.some(
          (id) =>
            !Number.isInteger(Number(id)) ||
            Number(id) <= 0
        ) ||
        new Set(visibleIds).size !== visibleIds.length ||
        new Set(operationalIds).size !== operationalIds.length ||
        visibleIds.some(
          (id) => !operationalIds.includes(id)
        )
      ) {
        return null;
      }

      return visiblePoints;
    }, [selectedSession]);

  const liveStopCollisionDetection = useCallback(
    (args) => {
      const ordered = reorderablePoints || [];

      const activeIndex = ordered.findIndex(
        (point) =>
          String(point.id) === String(args.active.id)
      );

      if (activeIndex < 0) {
        return closestCenter(args);
      }

      const isLocked = (point) =>
        FINAL_STATUSES.has(
          String(point?.status || "")
            .trim()
            .toUpperCase()
        ) || point?.isFrozen === true;

      const eligible = args.droppableContainers.filter(
        (container) => {
          const targetIndex = ordered.findIndex(
            (point) =>
              String(point.id) === String(container.id)
          );

          if (targetIndex < 0) {
            return false;
          }

          return !ordered
            .slice(
              Math.min(activeIndex, targetIndex),
              Math.max(activeIndex, targetIndex) + 1
            )
            .some(isLocked);
        }
      );

      const eligibleArgs = {
        ...args,
        droppableContainers: eligible,
      };

      const pointerCollisions = pointerWithin(eligibleArgs);

      return pointerCollisions.length
        ? pointerCollisions
        : closestCenter(eligibleArgs);
    },
    [reorderablePoints]
  );


  const fetchMapReorderSchedules = useCallback(
    async (sessionId) => {
      const { data } = await api.get(
        `/pickupdeliveries/${sessionId}/light`,
        {
          params: {
            sort: "seq",
            t: Date.now(),
          },
        }
      );

      if (!Array.isArray(data)) {
        throw new Error(
          "Não foi possível consultar os horários da sessão."
        );
      }

      const rows = await Promise.all(
        data.map(async (row) => {
          let scheduleType = String(
            row?.scheduleType || ""
          ).toUpperCase();

          let hourEnd = row?.hourEnd ?? null;
          let hour = row?.hour ?? null;
          let date = row?.date ?? null;

          if (
            !["FIXED", "WINDOW"].includes(scheduleType) ||
            (scheduleType === "WINDOW" && !hourEnd)
          ) {
            const response = await api.get(
              `/pickupdeliveries/by-id/${row.id}`
            );

            const full =
              response.data?.pickupDelivery ??
              response.data?.pd ??
              response.data?.row ??
              response.data;

            scheduleType = String(
              full?.scheduleType || ""
            ).toUpperCase();

            hourEnd = full?.hourEnd ?? null;
            hour = full?.hour ?? hour;
            date = full?.date ?? date;
          }

          if (
            !["FIXED", "WINDOW"].includes(scheduleType) ||
            (scheduleType === "WINDOW" && !hourEnd)
          ) {
            throw new Error(
              `Horário incompleto no ponto ${row.id}.`
            );
          }

          return {
            ...row,
            date,
            hour: hour
              ? String(hour).slice(0, 5)
              : null,
            hourEnd: hourEnd
              ? String(hourEnd).slice(0, 5)
              : null,
            scheduleType,
          };
        })
      );

      return rows.sort(
        (a, b) => Number(a.sequence) - Number(b.sequence)
      );
    },
    []
  );

  const saveReorderedStops = useCallback(
    async (activeId, overId) => {
      if (
        stopsReorderBusyRef.current ||
        !selectedSession ||
        !reorderablePoints
      ) {
        return;
      }

      const sessionId = selectedSession.id;

      const originalPoints = [
        ...(
          selectedSession.timelinePoints?.length
            ? selectedSession.timelinePoints
            : selectedSession.operationalPoints || []
        ),
      ].sort(
        (a, b) =>
          Number(a.sequence) - Number(b.sequence)
      );

      const originalIds = originalPoints.map(
        (point) => String(point.id)
      );

      const from = originalIds.indexOf(
        String(activeId)
      );

      const to = originalIds.indexOf(
        String(overId)
      );

      if (
        from < 0 ||
        to < 0 ||
        from === to
      ) {
        return;
      }

      const orderedIds = arrayMove(
        originalIds,
        from,
        to
      );

      const originalById = new Map(
        originalPoints.map((point) => [
          String(point.id),
          point,
        ])
      );

      const isLocked = (point) =>
        FINAL_STATUSES.has(
          String(point?.status || "")
            .trim()
            .toUpperCase()
        ) || point?.isFrozen === true;

      // Nunca alterar a posição de uma paragem finalizada.
      if (
        originalPoints.some(
          (point, index) =>
            isLocked(point) &&
            orderedIds[index] !== String(point.id)
        )
      ) {
        return;
      }

      let serverPoints;

      try {
        serverPoints = await fetchMapReorderSchedules(sessionId);

        const serverIds = serverPoints.map(
          (point) => String(point.id)
        );

        if (
          serverIds.length !== originalIds.length ||
          serverIds.some((id, index) => id !== originalIds[index])
        ) {
          throw new Error(
            "A ordem da sessão foi alterada entretanto. Atualiza os dados e volta a tentar."
          );
        }
      } catch (error) {
        window.alert(
          error?.response?.data?.error ||
          error?.message ||
          "Não foi possível validar os horários."
        );
        return "validation-failed";
      }

      const serverById = new Map(
        serverPoints.map((point) => [
          String(point.id),
          point,
        ])
      );


      const proposedPoints = orderedIds.map((id) => ({
        ...serverById.get(id),
        isFrozen:
          originalById.get(id)?.isFrozen === true,
      }));

      const conflict =
        getMapScheduleConflict(proposedPoints);

      const scheduleSnapshot =
        getMapScheduleSignature(serverPoints);

      if (conflict) {
        const editableIndices = [
          conflict.blockingIndex,
          conflict.index,
        ];

        const hasEditablePoint = editableIndices.some((index) => {
          const point = proposedPoints[index];

          return point &&
            !FINAL_STATUSES.has(
              String(point.status || "").toUpperCase()
            ) &&
            point.isFrozen !== true;
        });

        if (!hasEditablePoint) {
          window.alert(
            "Os pontos em conflito estão bloqueados. Não é possível alterar os seus horários."
          );
          return;
        }

        setScheduleConflict({
          sessionId,
          movedId: String(activeId),
          points: proposedPoints,
          snapshot: scheduleSnapshot,
          error: "",
        });

        return "conflict";
      }

      // Snapshot para proteger contra alterações concorrentes.

      const snapshot = (points) =>
        [...points]
          .sort(
            (a, b) =>
              Number(a.sequence) - Number(b.sequence)
          )
          .map((point) =>
            [
              String(point.id),
              Number(point.sequence),
              String(
                point.status || ""
              ).toUpperCase(),
              String(
                point.date || ""
              ).slice(0, 10),
              String(
                point.hour || ""
              ).slice(0, 5),
            ].join(":")
          )
          .join("|");

      const originalSnapshot = snapshot(
        originalPoints
      );

      // Atualização otimista para evitar saltos visuais.
      const updateView = (items) => {
        if (
          !Array.isArray(items) ||
          items.length !== orderedIds.length
        ) {
          return items;
        }

        const byId = new Map(
          items.map((item) => [
            String(item.id),
            item,
          ])
        );

        if (
          orderedIds.some(
            (id) => !byId.has(id)
          )
        ) {
          return items;
        }

        return orderedIds.map(
          (id, index) => ({
            ...byId.get(id),
            sequence: index + 1,
          })
        );
      };

      // Restaurar a ordem sem perder outros dados atualizados.
      const restoreView = (items) => {
        if (
          !Array.isArray(items) ||
          items.length !== originalIds.length
        ) {
          return items;
        }

        const byId = new Map(
          items.map((item) => [
            String(item.id),
            item,
          ])
        );

        if (
          originalIds.some(
            (id) => !byId.has(id)
          )
        ) {
          return items;
        }

        return originalIds.map(
          (id, index) => ({
            ...byId.get(id),
            sequence: index + 1,
          })
        );
      };

      const changeSessionView = (update) => {
        setSessions((current) =>
          current.map((session) =>
            String(session.id) === String(sessionId)
              ? {
                  ...session,
                  operationalPoints: update(
                    session.operationalPoints
                  ),
                  timelinePoints: update(
                    session.timelinePoints
                  ),
                  points: update(
                    session.points
                  ),
                }
              : session
          )
        );
      };

      stopsReorderBusyRef.current = true;
      setSavingStopOrder(true);

      // Mostrar imediatamente a nova posição.
      changeSessionView(updateView);

      try {
        const { data: liveData } = await api.get(
          `/pickupdeliveries/${sessionId}/light`,
          {
            params: {
              sort: "seq",
              t: Date.now(),
            },
          }
        );

        const currentSchedules =
          await fetchMapReorderSchedules(sessionId);

        if (
          !Array.isArray(liveData) ||
          snapshot(liveData) !== originalSnapshot ||
          getMapScheduleSignature(currentSchedules) !==
            scheduleSnapshot
        ) {
          throw new Error(
            "A sessão foi alterada entretanto. Atualiza os dados e volta a tentar."
          );
        }

        await api.put(
          "/pickupdeliveries/reorder",
          {
            transportsessionId: sessionId,
            updatedSequence: orderedIds.map(
              (id, index) => ({
                id: originalById.get(id).id,
                newSequence: index + 1,
              })
            ),
          }
        );

        await refreshSessions({
          silent: true,
        });
        return "saved";
      } catch (error) {
        changeSessionView(restoreView);

        console.error(
          "[LiveTransportsMap] Reorder failed:",
          error
        );


        window.alert(
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Não foi possível guardar a nova ordem."
        );

        await refreshSessions({
          silent: true,
        });
        return "handled-failure";
      } finally {
        stopsReorderBusyRef.current = false;
        setSavingStopOrder(false);
      }
    },
    [
      selectedSession,
      reorderablePoints,
      refreshSessions,
      fetchMapReorderSchedules,
    ]
  );


  // Medir a escala no painel onde começou o arrasto.
  const handleStopDragStart = ({ active, activatorEvent }) => {
    const panel = activatorEvent?.target?.closest?.(
      ".live-transports-map__selection"
    );

    const layoutWidth = panel?.offsetWidth || 0;
    const visualWidth =
      panel?.getBoundingClientRect().width || 0;

    const measuredScale = layoutWidth > 0
      ? visualWidth / layoutWidth
      : 1;

    setStopDragScale(
      Number.isFinite(measuredScale) && measuredScale > 0
        ? measuredScale
        : 1
    );

    setDraggingStopId(String(active.id));
  };

  const stopDropPendingRef = useRef(false);

  // Atualizar a ordem visual antes da resposta HTTP.
  const handleStopDragEnd = ({ active, over }) => {
    setDraggingStopId(null);

    if (
      !over ||
      !selectedSession ||
      !reorderablePoints ||
      stopDropPendingRef.current ||
      savingStopOrder
    ) {
      return;
    }

    const original = [...reorderablePoints].sort(
      (a, b) => Number(a.sequence) - Number(b.sequence)
    );

    const originalIds = original.map(
      (point) => String(point.id)
    );

    const from = originalIds.indexOf(String(active.id));
    const to = originalIds.indexOf(String(over.id));

    if (from < 0 || to < 0 || from === to) {
      return;
    }

    const orderedIds = arrayMove(originalIds, from, to);

    const isLocked = (point) =>
      FINAL_STATUSES.has(
        String(point?.status || "").trim().toUpperCase()
      ) || point?.isFrozen === true;

    if (
      original.some(
        (point, index) =>
          isLocked(point) &&
          orderedIds[index] !== String(point.id)
      )
    ) {
      return;
    }

    const applyIds = (ids) => {
      const reorder = (items) => {
        if (
          !Array.isArray(items) ||
          items.length !== ids.length
        ) {
          return items;
        }

        const byId = new Map(
          items.map((point) => [
            String(point.id),
            point,
          ])
        );

        if (ids.some((id) => !byId.has(id))) {
          return items;
        }

        return ids.map((id, index) => ({
          ...byId.get(id),
          sequence: index + 1,
        }));
      };

      setSessions((current) =>
        current.map((session) =>
          String(session.id) === String(selectedSession.id)
            ? {
                ...session,
                operationalPoints: reorder(
                  session.operationalPoints
                ),
                timelinePoints: reorder(
                  session.timelinePoints
                ),
                points: reorder(session.points),
              }
            : session
        )
      );
    };

    stopDropPendingRef.current = true;

    // A troca aparece imediatamente.
    flushSync(() => {
      applyIds(orderedIds);
    });

    // O backend continua a validar a alteração.
    void saveReorderedStops(active.id, over.id)
      .then((result) => {
        if (
          result !== "saved" &&
          result !== "handled-failure"
        ) {
          applyIds(originalIds);
        }
      })
      .catch((error) => {
        applyIds(originalIds);

        console.error(
          "[LiveTransportsMap] Reorder failed:",
          error
        );
      })
      .finally(() => {
        stopDropPendingRef.current = false;
      });
  };

  const saveResolvedStopOrder = useCallback(
    async (editedPoints) => {

      if (
        !scheduleConflict ||
        stopsReorderBusyRef.current
      ) {
        return;
      }

      const pending = scheduleConflict;

      if (getMapScheduleConflict(editedPoints)) {
        setScheduleConflict((previous) => ({
          ...previous,
          error: "Ainda existem horários em conflito.",
        }));
        return;
      }

      if (
        !window.confirm(
          "Esta alteração modifica horários acordados com o cliente. Queres guardar os novos horários e a nova ordem?"
        )
      ) {
        return;
      }

      stopsReorderBusyRef.current = true;
      setSavingStopOrder(true);

      try {
        const current = await fetchMapReorderSchedules(
          pending.sessionId
        );

        if (
          getMapScheduleSignature(current) !==
          pending.snapshot
        ) {
          throw new Error(
            "A sessão foi alterada por outro utilizador. Atualiza os dados antes de tentar novamente."
          );
        }

        const originalById = new Map(
          current.map((point) => [
            String(point.id),
            point,
          ])
        );

        if (
          editedPoints.length !== current.length ||
          editedPoints.some(
            (point) => !originalById.has(String(point.id))
          )
        ) {
          throw new Error(
            "Os pontos da sessão foram alterados entretanto."
          );
        }

        const updatedSequence = editedPoints.map(
          (point, index) => {
            const original = originalById.get(
              String(point.id)
            );

            const oldDate = String(
              original.date || ""
            ).slice(0, 10);

            const newDate = String(
              point.date || ""
            ).slice(0, 10);

            const oldHour = String(
              original.hour || ""
            ).slice(0, 5);

            const newHour = String(
              point.hour || ""
            ).slice(0, 5);

            const oldMode = String(
              original.scheduleType || "FIXED"
            ).toUpperCase();

            const newMode = String(
              point.scheduleType || "FIXED"
            ).toUpperCase();

            const oldEnd = String(
              original.hourEnd || ""
            ).slice(0, 5);

            const newEnd = String(
              point.hourEnd || ""
            ).slice(0, 5);

            const scheduleChanged =
              oldDate !== newDate ||
              oldHour !== newHour ||
              oldMode !== newMode ||
              oldEnd !== newEnd;

            if (
              scheduleChanged &&
              (
                FINAL_STATUSES.has(
                  String(original.status || "").toUpperCase()
                ) ||
                point.isFrozen === true
              )
            ) {
              throw new Error(
                "Não é permitido alterar o horário de uma paragem bloqueada."
              );
            }

            const update = {
              id: original.id,
              newSequence: index + 1,
            };

            if (oldDate !== newDate) {
              update.date = newDate || null;
            }

            if (oldHour !== newHour) {
              update.hour = newHour || null;
            }

            if (oldMode !== newMode) {
              update.scheduleType = newMode;
            }

            if (oldEnd !== newEnd) {
              update.hourEnd = newEnd || null;
            }

            return update;
          }
        );

        await api.put(
          "/pickupdeliveries/reorder",
          {
            transportsessionId: pending.sessionId,
            updatedSequence,
          }
        );

        await refreshSessions({
          silent: true,
        });

        setScheduleConflict(null);
      } catch (error) {
        console.error(
          "[LiveTransportsMap] Conflict resolution failed:",
          error
        );

        setScheduleConflict((previous) =>
          previous
            ? {
                ...previous,
                error:
                  error?.response?.data?.message ||
                  error?.response?.data?.error ||
                  error?.message ||
                  "Não foi possível guardar a nova ordem.",
              }
            : previous
        );
      } finally {
        stopsReorderBusyRef.current = false;
        setSavingStopOrder(false);
      }
    },
    [
      scheduleConflict,
      fetchMapReorderSchedules,
      refreshSessions,
    ]
  );

  const getStatusLabel =
    (status) => {
      return t(
        `map.live.statuses.${status}`,
        {
          defaultValue:
            status,
        }
      );
    };

  const getStopStatusLabel = (point) => {
    const status = String(
      point?.status || ""
    )
      .trim()
      .toUpperCase();

    const substatus = String(
      point?.substatus || ""
    )
      .trim()
      .toUpperCase();

    const type = String(
      point?.type || ""
    )
      .trim()
      .toUpperCase();

    if (status === "COMPLETED") {
      return "Concluído";
    }

    if (status === "CANCELLED") {
      return "Cancelado";
    }

    if (
      status === "DELAYED" ||
      substatus === "DELAYED"
    ) {
      return "Atrasado";
    }

    if (
      !status &&
      ["VIA", "STRATEGICPOINT"].includes(type)
    ) {
      return "Passagem";
    }

    const labels = {
      PENDING: "Pendente",
      PLANNED: "Planeado",
      SCHEDULED: "Agendado",
      ACTIVE: "Em curso",
      IN_PROGRESS: "Em curso",
      ONGOING: "Em curso",
      STARTED: "Iniciado",
      IN_TRANSIT: "Em trânsito",
      EN_ROUTE: "Em trânsito",
      ARRIVED: "Chegou ao local",
      AT_LOCATION: "No local",
      WAITING: "Em espera",
      WAITING_TO_LOAD: "A aguardar carga",
      LOADING: "Em carga",
      LOADING_IN_PROGRESS: "Em carga",
      WAITING_TO_UNLOAD: "A aguardar descarga",
      UNLOADING: "Em descarga",
      UNLOADING_MERCHANDISE: "Em descarga",
      AT_PICKUP_LOCATION: "No local de carga",
      AT_DELIVERY_LOCATION: "No local de descarga",
      IN_TRANSIT_MOVING_TO_PICKUP_LOCATION:
        "A caminho da carga",
      IN_TRANSIT_MOVING_TO_DELIVERY_LOCATION:
        "A caminho da descarga",
    };

    const hasMeaningfulSubstatus =
      substatus &&
      !["PENDING", "NONE", "UNKNOWN"].includes(
        substatus
      );

    const effectiveStatus =
      hasMeaningfulSubstatus
        ? substatus
        : status;

    if (labels[effectiveStatus]) {
      return labels[effectiveStatus];
    }

    if (!effectiveStatus) {
      return "Sem estado";
    }

    return effectiveStatus
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/^./, (letter) =>
        letter.toUpperCase()
      );
  };

  const getFreshOperationalStop = async (point) => {
    const id = Number(point?.id);

    if (
      !Number.isSafeInteger(id) ||
      id <= 0 ||
      !selectedSession
    ) {
      throw new Error(
        "Esta paragem não tem um identificador operacional válido."
      );
    }

    const operationalPoints = [
      ...(selectedSession.timelinePoints || []),
      ...(selectedSession.operationalPoints || []),
    ];

    const belongsToSession = operationalPoints.some(
      (item) => String(item?.id) === String(id)
    );

    if (!belongsToSession) {
      throw new Error(
        "Este ponto pertence à rota, mas não foi encontrado entre as paragens operacionais."
      );
    }

    const { data } = await api.get(
      `/pickupdeliveries/by-id/${id}`,
      {
        params: { _: Date.now() },
      }
    );

    const freshPoint =
      data?.pickupDelivery ??
      data?.pd ??
      data?.row ??
      data;

    if (
      !freshPoint ||
      String(freshPoint.id) !== String(id)
    ) {
      throw new Error(
        "Não foi possível confirmar a paragem."
      );
    }

    const sessionId =
      getTransportSessionId(freshPoint);

    if (
      sessionId != null &&
      String(sessionId) !==
        String(selectedSession.id)
    ) {
      throw new Error(
        "Esta paragem já não pertence à sessão selecionada."
      );
    }

    const pointType = String(
      freshPoint.type || ""
    ).toUpperCase();

    if (
      !["PICKUP", "DELIVERY", "STRATEGICPOINT"].includes(
        pointType
      )
    ) {
      throw new Error(
        "Este ponto não permite abrir os modais operacionais."
      );
    }

    return freshPoint;
  };

  const openStopPhotos = async (point) => {
    try {
      const freshPoint =
        await getFreshOperationalStop(point);

      const pointType = String(
        freshPoint.type || ""
      ).toUpperCase();

      if (
        !["PICKUP", "DELIVERY"].includes(pointType)
      ) {
        return;
      }

      setSelectedStopForCmr(freshPoint.id);
    } catch (error) {
      console.error(
        "[LiveTransportsMap] Open CMR:",
        error
      );

      window.alert(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Não foi possível abrir os documentos desta paragem."
      );
    }
  };

  const openStopStatus = async (point) => {
    if (savingStopStatus) return;

    try {
      const freshPoint =
        await getFreshOperationalStop(point);

      if (
        freshPoint.isFrozen === true ||
        freshPoint.isStatusLocked === true ||
        freshPoint.isLocked === true
      ) {
        throw new Error(
          "O estado desta paragem está bloqueado."
        );
      }

      const type = String(
        freshPoint.type || ""
      ).toUpperCase();

      const status = String(
        freshPoint.status || ""
      ).toUpperCase();

      if (
        type !== "STRATEGICPOINT" &&
        status === "PENDING"
      ) {
        const hasTruck =
          freshPoint.truckId != null ||
          Boolean(
            String(freshPoint.truckPlate || "").trim()
          );

        const hasDriver =
          freshPoint.driverId != null ||
          Boolean(
            String(freshPoint.driverName || "").trim()
          );

        if (!hasTruck || !hasDriver) {
          throw new Error(
            "É necessário atribuir um camião e um motorista antes de alterar o estado."
          );
        }
      }

      setSelectedStopForStatus(freshPoint);
    } catch (error) {
      console.error(
        "[LiveTransportsMap] Open status:",
        error
      );

      window.alert(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Não foi possível abrir o estado da paragem."
      );
    }
  };

  return (
    <>
      <nav
        className="loadevo-shared-live-switch"
        data-view={viewMode}
        data-collapsed={
          isSidebarCollapsed &&
          viewMode !== "drivers"
            ? "true"
            : "false"
        }
        aria-label="Vista do mapa de transportes"
      >
        <span
          className="loadevo-shared-live-switch__slider"
          aria-hidden="true"
        />

        <button
          type="button"
          className={
            viewMode === "sessions"
              ? "is-active"
              : ""
          }
          aria-pressed={viewMode === "sessions"}
          onClick={() =>
            setViewMode("sessions")
          }
        >
          <Route size={13} />
          <span>Sessões</span>

          <small>{sessions.length}</small>
        </button>

        <button
          type="button"
          className={
            viewMode === "trucks"
              ? "is-active"
              : ""
          }
          aria-pressed={viewMode === "trucks"}
          onClick={() => {
            if (viewMode === "sessions") {
              const sessionPanel =
                document.querySelector(
                  ".live-transports-map .live-transports-map__selection:not(.live-transports-map__truck-route-panel)"
                );

              setMatchedTruckPanelHeight(
                sessionPanel?.offsetHeight ?? null
              );
            }

            setViewMode("trucks");

            setSelectedTruckPlate(
              (current) =>
                current ||
                assignedTrucks[0]?.plate ||
                null
            );

            setExpandedTruckSessionId(null);
          }}
        >
          <Truck size={13} />
          <span>Camiões</span>

          <small>{assignedTrucks.length}</small>
        </button>

        <button
          type="button"
          className={
            viewMode === "drivers"
              ? "is-active"
              : ""
          }
          aria-pressed={viewMode === "drivers"}
          onClick={() =>
            setViewMode("drivers")
          }
        >
          <User size={13} />
          <span>Motoristas</span>
        </button>
      </nav>

      {viewMode === "drivers" ? (
        <DriverRoutePlannerMap
          currentSessions={sessions}
          currentSessionsLoading={loading}
          onRouteSaved={() =>
            refreshSessions({
              silent: true,
            })
          }
          onShowSessions={() =>
            setViewMode("sessions")
          }
        />
      ) : (
        <div
          className={[
            "map-view",
            "live-transports-map",
            isSidebarCollapsed
              ? "is-sidebar-collapsed"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
      <aside className="map-view__sidebar live-transports-map__sidebar">
        <div className="live-transports-map__header">
          <div className="live-transports-map__heading">
            <span className="live-transports-map__eyebrow">
              LOADEVO CONTROL CENTER
            </span>

            <h2 className="map-view__sidebar-title">
              {viewMode === "trucks"
                ? "Rotas dos camiões"
                : t("map.live.title", {
                    defaultValue:
                      "Transportes atuais",
                  })}
            </h2>

            <span className="live-transports-map__heading-count">
              {viewMode === "trucks"
                ? `${assignedTrucks.length} camiões associados`
                : `${sessions.length} sessões`}
            </span>
          </div>

          <button
            type="button"
            className="live-transports-map__refresh"
            onClick={() =>
              refreshSessions({
                silent: true,
              })
            }
            disabled={refreshing || loading}
            aria-label={t("map.live.refresh", {
              defaultValue:
                "Atualizar transportes",
            })}
          >
            <RefreshCw
              size={17}
              className={
                refreshing
                  ? "is-spinning"
                  : ""
              }
            />
          </button>
        </div>

        <label className="live-transports-map__search">
          <Search
            size={15}
            aria-hidden="true"
          />

          <input
            type="search"
            value={searchTerm}
            onChange={(event) =>
              setSearchTerm(
                event.target.value
              )
            }
            placeholder={
              viewMode === "trucks"
                ? "Pesquisar camiões..."
                : t(
                    "map.live.searchPlaceholder",
                    {
                      defaultValue:
                        "Pesquisar transportes...",
                    }
                  )
            }
            aria-label={
              viewMode === "trucks"
                ? "Pesquisar camiões"
                : "Pesquisar transportes"
            }
          />
        </label> 

        <div
          className="loadevo-shared-live-switch__slot"
          aria-hidden="true"
        />

        {viewMode === "sessions" && (
          <div
            className="live-transports-map__filters"
            role="group"
            aria-label="Filtrar sessões"
          >
            {[
              {
                id: "all",
                label: "Todas",
              },
              {
                id: "active",
                label: "Ativas",
              },
              {
                id: "delayed",
                label: "Atrasadas",
              },
            ].map((filter) => (
              <button
                key={filter.id}
                type="button"
                className={[
                  "live-transports-map__filter",
                  `is-${filter.id}`,
                  sessionFilter === filter.id
                    ? "is-selected"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() =>
                  setSessionFilter(filter.id)
                }
                aria-pressed={
                  sessionFilter === filter.id
                }
              >
                <span className="live-transports-map__filter-dot" />

                <span>{filter.label}</span>

                <small>
                  {sessionCounts[filter.id]}
                </small>
              </button>
            ))}
          </div>
        )}

        {lastUpdated && (
          <div className="live-transports-map__updated">
            <Clock3
              size={14}
              aria-hidden="true"
            />

            <span> 
              {t(
                "map.live.updatedAt",
                {
                  time:
                    formatUpdatedAt(
                      lastUpdated,
                      locale
                    ),

                  defaultValue:
                    "Atualizado às {{time}}",
                }
              )}
            </span>
          </div>
        )}

        {error && (
          <div className="live-transports-map__error">
            <AlertCircle
              size={18}
              aria-hidden="true"
            />

            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="live-transports-map__loading">
            <RefreshCw
              size={21}
              className="is-spinning"
            />

            <span>
              {t(
                "map.live.loading",
                {
                  defaultValue:
                    "A carregar transportes...",
                }
              )}
            </span>
          </div>
        ) : viewMode === "trucks" ? (
          filteredTrucks.length ? (
            <div className="live-transports-map__list live-transports-map__trucks-list">
              {filteredTrucks.map(
                ({
                  plate,
                  session,
                }) => {
                  const selected =
                    selectedTruckPlate?.toUpperCase() ===
                    plate.toUpperCase();

                  // MOCK GPS — apenas 07-UP-51.
                  // Substituir futuramente por telemetria FMC650.
                  const mockGps =
                    plate.trim().toUpperCase() === "07-UP-51"
                      ? {
                          status: "Em andamento",
                          road: "A1",
                          city: "Leiria",
                          postalCode: "2400-000",
                          country: "Portugal",
                          flag: "🇵🇹",
                          lastUpdatedRelative: "há 24 s",
                          lastUpdatedTime: "15:28:06",
                          driverPhoto:
                            "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&h=80&fit=crop&crop=faces",
                        }
                      : null;

                  return (
                    <button
                      key={plate}
                      type="button"
                      className={[
                        "live-transports-map__truck-card",
                        selected
                          ? "is-selected"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => {
                        setSelectedTruckPlate(
                          plate
                        );

                        setExpandedTruckSessionId(null);
                      }}
                    >
                      <span className="live-transports-map__truck-icon">
                        <Truck size={22} />
                      </span>

                      <span className="live-transports-map__truck-info">
                        <strong>{plate}</strong>

                        <span className="live-transports-map__truck-driver">
                          <span className="live-transports-map__truck-driver-avatar">
                            <User size={14} />

                            {mockGps?.driverPhoto &&
                              session.driverName && (
                                <img
                                  src={mockGps.driverPhoto}
                                  alt=""
                                  loading="lazy"
                                  onError={(event) => {
                                    event.currentTarget.style.display =
                                      "none";
                                  }}
                                />
                              )}
                          </span>

                          <span className="live-transports-map__truck-driver-name">
                            {session.driverName ||
                              "Motorista não atribuído"}
                          </span>
                        </span>

                        <span className="live-transports-map__truck-location">
                          {mockGps ? (
                            <>
                              <span className="live-transports-map__truck-country-flag">
                                {getCountryFlag({ country: mockGps.country })}
                              </span>

                              <span className="live-transports-map__truck-location-text">
                                {[
                                  mockGps.road,
                                  mockGps.city,
                                  mockGps.postalCode,
                                  mockGps.country,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </span>
                            </>
                          ) : (
                            <>
                              <MapPin size={13} />

                              <span className="live-transports-map__truck-location-text">
                                Localização GPS indisponível
                              </span>
                            </>
                          )}
                        </span>

                        {mockGps && (
                          <span className="live-transports-map__truck-updated">
                            <Clock3 size={12} />

                            <span>
                              Última atualização:{" "}
                              <b>{mockGps.lastUpdatedRelative}</b>
                              {" · "}
                              {mockGps.lastUpdatedTime}
                            </span>
                          </span>
                        )}
                      </span>

                      <span className="live-transports-map__truck-side">
                        <span
                          className={[
                            "live-transports-map__truck-gps-status",
                            mockGps
                              ? "is-moving"
                              : "is-unavailable",
                          ].join(" ")}
                        >
                          {mockGps
                            ? mockGps.status
                            : "Sem GPS"}
                        </span>

                        {mockGps && (
                          <small>GPS simulado</small>
                        )}

                        <ChevronRight
                          size={16}
                          aria-hidden="true"
                        />
                      </span>
                    </button>
                  );
                }
              )}
            </div>
          ) : (
            <div className="map-view__sidebar-empty">
              <div className="map-view__sidebar-empty-icon">
                <Truck />
              </div>

              <strong>
                Nenhum camião encontrado
              </strong>

              <span>
                {searchTerm
                  ? "Experimenta outra matrícula ou nome."
                  : "Não existem camiões nas sessões atuais."}
              </span>
            </div>
          )
        ) : filteredSessions.length ? (
          <div className="live-transports-map__list">
            {filteredSessions.map(
              (session) => {
                const selected =
                  String(
                    session.id
                  ) ===
                  String(
                    selectedSessionId
                  );

                /*
                 * Pontos operacionais:
                 * utilizados para estados reais
                 * e identificação da próxima paragem.
                 */
                const operationalCardPoints =
                  session.timelinePoints?.length
                    ? session.timelinePoints
                    : session.operationalPoints?.length
                      ? session.operationalPoints
                      : session.points || [];


                /*
                 * Timeline completa e partilhada
                 * entre cartão e painel direito.
                 *
                 * Inclui os pontos da rota guardada
                 * e preserva os estados operacionais.
                 */
                const cardPoints =
                  getSessionTimelinePoints(
                    session
                  );

                const completedPointCount =
                  calculateCompletedPointCount(
                    cardPoints
                  );

                /*
                 * A próxima paragem continua a
                 * depender dos pontos operacionais.
                 */
                const nextPoint =
                  getNextOperationalPoint(
                    operationalCardPoints
                  );

                const currentPointNumber =
                  cardPoints.length
                    ? Math.min(
                        completedPointCount +
                          1,
                        cardPoints.length
                      )
                    : 0;

                const nextPointLocation =
                  getPointLocationMeta(
                    nextPoint
                  );

                const nextPointDateTime =
                  formatPointDateTime(
                    nextPoint,
                    locale
                  );

                const nextPointSchedule =
                  getPointScheduleHighlight(
                    nextPoint,
                    locale
                  );

                const nextPointRouteSummary = (() => {
                  const metrics =
                    session.routeMetrics || [];

                  if (
                    !nextPoint ||
                    !Array.isArray(metrics) ||
                    metrics.length === 0
                  ) {
                    return null;
                  }

                  const findMetric = (target) => {
                    if (!target) {
                      return null;
                    }

                    const targetLat =
                      toFiniteNumberOrNull(target.lat);

                    const targetLng =
                      toFiniteNumberOrNull(target.lng);

                    return metrics.find((metric) => {
                      if (
                        target.id != null &&
                        metric.id != null &&
                        String(target.id) ===
                          String(metric.id)
                      ) {
                        return true;
                      }

                      return (
                        targetLat !== null &&
                        targetLng !== null &&
                        Math.abs(
                          targetLat - Number(metric.lat)
                        ) < 0.0004 &&
                        Math.abs(
                          targetLng - Number(metric.lng)
                        ) < 0.0004
                      );
                    }) || null;
                  };

                  const lastFinalIndex =
                    cardPoints.reduce(
                      (last, point, index) => {
                        const status = String(
                          point?.status || ""
                        ).toUpperCase();

                        return FINAL_STATUSES.has(status)
                          ? index
                          : last;
                      },
                      -1
                    );

                  const originPoint =
                    cardPoints[
                      Math.max(0, lastFinalIndex)
                    ];

                  const originMetric =
                    findMetric(originPoint) ||
                    (lastFinalIndex < 0
                      ? metrics[0]
                      : null);

                  const destinationMetric =
                    findMetric(nextPoint);

                  if (
                    !originMetric ||
                    !destinationMetric
                  ) {
                    return null;
                  }

                  const km =
                    Number(destinationMetric.km) -
                    Number(originMetric.km);

                  const rawMinutes =
                    Number(destinationMetric.minutes) -
                    Number(originMetric.minutes);

                  if (
                    !Number.isFinite(km) ||
                    !Number.isFinite(rawMinutes) ||
                    km < 0 ||
                    rawMinutes < 0
                  ) {
                    return null;
                  }

                  const totalMinutes =
                    Math.round(rawMinutes);

                  const hours =
                    Math.floor(totalMinutes / 60);

                  const minutes =
                    totalMinutes % 60;

                  return {
                    distanceLabel:
                      `${Math.round(km)} km`,

                    durationLabel:
                      hours > 0
                        ? `${hours}h${
                            minutes > 0
                              ? ` ${minutes}m`
                              : ""
                          }`
                        : `${minutes}m`,
                  };
                })();

                const nextPointFlag =
                  getCountryFlag(
                    nextPoint
                  );

                const operationalStatus =
                  getOperationalStatusText(
                    nextPoint,
                    t
                  );

                return (
                  <button
                    key={
                      session.id
                    }
                    type="button"
                    className={[
                      "live-transport-card",

                      selected
                        ? "is-selected"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => {
                      const isAlreadySelected =
                        String(selectedSessionId) ===
                        String(session.id);

                      if (isAlreadySelected) {
                        focusSessionOnMap(session.id);
                        return;
                      }

                      // O useEffect foca a rota
                      // depois de o painel atualizar.
                      setSelectedSessionId(session.id);
                    }}

                  >
                    <div className="live-transport-card__top live-transport-card__top--reference live-transport-card__premium">
                      <div className="live-transport-card__premium-main">
                        <div className="live-transport-card__premium-heading">
                          <strong className="live-transport-card__premium-number">
                            {getSessionDisplayNumber(session)}
                          </strong>

                          {operationalStatus &&

                            !FINAL_STATUSES.has(
                              getSessionStatus(session)
                            ) && (
                              <span className="live-transport-card__compact-status">
                                <Route
                                  size={12}
                                  aria-hidden="true"
                                />

                                <span>
                                  {operationalStatus}
                                </span>
                              </span>
                            )}

                          {String(
                            nextPoint?.substatus || ""
                          ).toUpperCase() === "DELAYED" && (
                            <span className="live-transport-card__premium-delay">
                              Atrasado
                            </span>
                          )}
                        </div>

                        <div className="live-transport-card__premium-route">


                          {(() => {
                            const stops = [
                              ...(
                                session.timelinePoints?.length
                                  ? session.timelinePoints
                                  : cardPoints
                              ),
                            ].sort(
                              (a, b) =>
                                Number(a?.sequence || 0) -
                                Number(b?.sequence || 0)
                            );

                            const pickup =
                              stops.find(
                                (point) =>
                                  String(point?.type || "")
                                    .toUpperCase() === "PICKUP"
                              ) || stops[0];

                            const delivery =
                              [...stops].reverse().find(
                                (point) =>
                                  String(point?.type || "")
                                    .toUpperCase() === "DELIVERY"
                              ) || stops[stops.length - 1];

                            const prepareStop = (point) => {
                              const schedule =
                                getPointScheduleHighlight(
                                  point,
                                  locale
                                );

                              const companyName = String(
                                point?.companyName ||
                                point?.name ||
                                point?.location ||
                                point?.city ||
                                "—"
                              ).trim();

                              const city = String(
                                point?.city || ""
                              ).trim();

                              const company =
                                city &&
                                !companyName
                                  .toLocaleLowerCase()
                                  .includes(
                                    city.toLocaleLowerCase()
                                  )
                                  ? `${companyName}, ${city}`
                                  : companyName;


                              const date =
                                schedule.dateLabel === "—"
                                  ? "—"
                                  : `${schedule.dayLabel} ${schedule.monthLabel.toLowerCase()} ${schedule.yearLabel}`;

                              const time =
                                schedule.timeLabel
                                  .replace(
                                    /\s*[–—-]\s*/g,
                                    "–"
                                  );

                              return {
                                countryCode:
                                  getPointCountryCode(point),
                                company,
                                postalCode:
                                  point?.postalCode ||
                                  point?.postcode ||
                                  point?.zipCode ||
                                  "—",
                                date,
                                time,
                              };
                            };

                            const origin = prepareStop(pickup);
                            const destination = prepareStop(delivery);

                            const renderSchedule = (stop, label) => (
                              <div className="live-transport-card__side-schedule">
                                <small>{label}</small>
                                <strong>{stop.date}</strong>
                                <span>{stop.time}</span>
                              </div>
                            );

                            const renderCompany = (stop) => (
                              <div
                                className="live-transport-card__side-company"
                                title={`${stop.company} · ${stop.postalCode}`}
                              >
                                {stop.countryCode ? (
                                  <img
                                    src={`https://flagcdn.com/w40/${stop.countryCode.toLowerCase()}.png`}
                                    alt=""
                                    aria-hidden="true"
                                    loading="lazy"
                                  />
                                ) : null}

                                <strong className="live-transport-card__side-company-name">
                                  {stop.company}
                                </strong>

                                <small>{stop.postalCode}</small>
                              </div>
                            );

                            return (
                              <>
                                {renderSchedule(origin, "Carga")}
                                {renderCompany(origin)}

                                <span
                                  className="live-transport-card__premium-arrow"
                                  aria-hidden="true"
                                >
                                  →
                                </span>

                                {renderCompany(destination)}
                                {renderSchedule(destination, "Descarga")}
                              </>
                            );
                          })()}
                        </div>



                        <div className="live-transport-card__premium-progress">
                          {cardPoints.length > 0 && (
                            cardPoints.length <= 16 ? (
                              <div
                                className="live-transport-card__premium-timeline"
                                role="group"
                                aria-label={`${completedPointCount} de ${cardPoints.length} pontos concluídos`}
                                style={{
                                  "--premium-dot-size":
                                    cardPoints.length > 10
                                      ? "10px"
                                      : "13px",
                                }}
                              >
                                {(() => {
                                  const nextIndex =
                                    cardPoints.findIndex(
                                      (point) =>
                                        ![
                                          "COMPLETED",
                                          "CANCELLED",
                                        ].includes(
                                          String(
                                            point?.status || ""
                                          ).toUpperCase()
                                        )
                                    );

                                  return cardPoints.map(
                                    (point, index) => {
                                      const status =
                                        String(
                                          point?.status || ""
                                        ).toUpperCase();

                                      const completed =
                                        status === "COMPLETED";

                                      const cancelled =
                                        status === "CANCELLED";

                                      const previousCompleted =
                                        index > 0 &&
                                        String(
                                          cardPoints[index - 1]
                                            ?.status || ""
                                        ).toUpperCase() ===
                                          "COMPLETED";

                                      const isNext =
                                        index === nextIndex &&
                                        !completed &&
                                        !cancelled;

                                      const stateLabel =
                                        completed
                                          ? "Concluído"
                                          : cancelled
                                            ? "Cancelado"
                                            : isNext
                                              ? "Próximo"
                                              : "Pendente";

                                      return (
                                        <React.Fragment
                                          key={`${point?.id ?? "point"}-${index}`}
                                        >
                                          {index > 0 && (
                                            <span
                                              className={[
                                                "live-transport-card__premium-connector",
                                                previousCompleted &&
                                                completed
                                                  ? "is-completed"
                                                  : "",
                                              ]
                                                .filter(Boolean)
                                                .join(" ")}
                                              aria-hidden="true"
                                            />
                                          )}

                                          <span
                                            className={[
                                              "live-transport-card__premium-dot",
                                              completed
                                                ? "is-completed"
                                                : "",
                                              cancelled
                                                ? "is-cancelled"
                                                : "",
                                              isNext
                                                ? "is-next"
                                                : "",
                                            ]
                                              .filter(Boolean)
                                              .join(" ")}
                                            role="img"
                                            aria-label={`Ponto ${index + 1}: ${stateLabel}`}
                                            title={`${index + 1}. ${
                                              point?.name ||
                                              point?.city ||
                                              "Ponto"
                                            } — ${stateLabel}`}
                                          />
                                        </React.Fragment>
                                      );
                                    }
                                  );
                                })()}
                              </div>
                            ) : (
                              <div
                                className="live-transport-card__premium-progress-bar"
                                role="progressbar"
                                aria-valuemin={0}
                                aria-valuemax={cardPoints.length}
                                aria-valuenow={completedPointCount}
                                aria-label="Pontos concluídos"
                              >
                                <span
                                  style={{
                                    width: `${
                                      (completedPointCount /
                                        cardPoints.length) * 100
                                    }%`,
                                  }}
                                />
                              </div>
                            )
                          )}

                          <span className="live-transport-card__premium-count">
                            {completedPointCount}/{cardPoints.length}
                          </span>
                        </div>
                      </div>

                      <div className="live-transport-card__premium-next">
                        <ChevronRight
                          size={16}
                          className="live-transport-card__premium-chevron"
                          aria-hidden="true"
                        />

                        <small>Próximo ponto</small>

                        <div className="live-transport-card__premium-next-place">
                          <MapPin
                            size={16}
                            aria-hidden="true"
                          />

                          <strong
                            title={
                              nextPoint?.city ||
                              nextPoint?.name ||
                              ""
                            }
                          >
                            {nextPoint?.city ||
                              nextPoint?.name ||
                              "—"}
                          </strong>
                        </div>

                        <div
                          className={[
                            "live-transport-card__premium-next-schedule",
                            nextPointSchedule.emphasis ===
                            "time"
                              ? "is-time-primary"
                              : "is-date-primary",
                          ].join(" ")}
                        >
                          {nextPointSchedule.emphasis ===
                          "date" ? (
                            <>
                              <div className="live-transport-card__premium-next-primary is-date-card">
                                <CalendarDays
                                  size={19}
                                  aria-hidden="true"
                                />

                                <strong className="live-transport-card__premium-next-date-inline">
                                  {nextPointSchedule.dateLabel === "—"
                                    ? "—"
                                    : `${nextPointSchedule.dayLabel} ${nextPointSchedule.monthLabel.toLowerCase()} ${nextPointSchedule.yearLabel}`}
                                </strong>
                              </div>

                              <div className="live-transport-card__premium-next-secondary is-time-card">
                                <Clock3
                                  size={16}
                                  aria-hidden="true"
                                />

                                <span>
                                  {nextPointSchedule.timeLabel}
                                </span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="live-transport-card__premium-next-primary is-time-card">
                                <Clock3
                                  size={19}
                                  aria-hidden="true"
                                />

                                <strong>
                                  {nextPointSchedule.timeLabel}
                                </strong>
                              </div>

                              <div className="live-transport-card__premium-next-secondary">
                                <CalendarDays
                                  size={16}
                                  aria-hidden="true"
                                />

                                <span>
                                  {nextPointSchedule.dateLabel === "—"
                                    ? "—"
                                    : `${nextPointSchedule.dayLabel} ${nextPointSchedule.monthLabel.toLowerCase()} ${nextPointSchedule.yearLabel}`}
                                </span>
                              </div>
                            </>
                          )}
                        </div>

                        <div className="live-transport-card__premium-next-metrics">
                          <div className="live-transport-card__premium-next-metric">
                            <svg
                              className="live-transport-card__premium-road-icon"
                              viewBox="0 0 24 24"
                              fill="none"
                              aria-hidden="true"
                            >
                              <path
                                d="M9 2H15L22 22H2L9 2Z"
                                fill="currentColor"
                              />
                              <path
                                d="M11.65 5H12.35L12.6 8H11.4L11.65 5Z"
                                fill="#EAF6FF"
                              />
                              <path
                                d="M11.35 11H12.65L13 15H11L11.35 11Z"
                                fill="#EAF6FF"
                              />
                              <path
                                d="M10.85 18H13.15L13.5 20H10.5L10.85 18Z"
                                fill="#EAF6FF"
                              />
                            </svg>

                            <span>
                              {nextPointRouteSummary?.distanceLabel ||
                                "— km"}
                            </span>
                          </div>

                          <i aria-hidden="true" />

                          <div className="live-transport-card__premium-next-metric">
                            <Clock3
                              size={15}
                              aria-hidden="true"
                            />

                            <span>
                              {nextPointRouteSummary?.durationLabel ||
                                "—"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="live-transport-card__resources">
                      <div className="live-transport-card__resource">
                        <Truck
                          size={15}
                        />

                        <div className="live-transport-card__resource-copy">
                          <span className="live-transport-card__resource-label">
                            {t(
                              "map.live.truck",
                              {
                                defaultValue:
                                  "Camião",
                              }
                            )}
                          </span>

                          <span className="live-transport-card__resource-value">
                            {session.truckPlate ||
                              t(
                                "map.live.unassigned",
                                {
                                  defaultValue:
                                    "Não atribuído",
                                }
                              )}
                          </span>
                        </div>
                      </div>

                      <div className="live-transport-card__resource">
                        <Container
                          size={15}
                        />

                        <div className="live-transport-card__resource-copy">
                          <span className="live-transport-card__resource-label">
                            {t(
                              "map.live.trailer",
                              {
                                defaultValue:
                                  "Reboque",
                              }
                            )}
                          </span>

                          <span className="live-transport-card__resource-value">
                            {session.trailerPlate ||
                              t(
                                "map.live.unassigned",
                                {
                                  defaultValue:
                                    "Não atribuído",
                                }
                              )}
                          </span>
                        </div>
                      </div>

                      <div className="live-transport-card__resource">
                        <User
                          size={15}
                        />

                        <div className="live-transport-card__resource-copy">
                          <span className="live-transport-card__resource-label">
                            {t(
                              "map.live.driver",
                              {
                                defaultValue:
                                  "Motorista",
                              }
                            )}
                          </span>

                          <span className="live-transport-card__resource-value">
                            {session.driverName ||
                              t(
                                "map.live.unassigned",
                                {
                                  defaultValue:
                                    "Não atribuído",
                                }
                              )}
                          </span>
                        </div>
                      </div>

                      <div className="live-transport-card__resource">
                        <MapPin
                          size={15}
                        />

                        <div className="live-transport-card__resource-copy">
                          <span className="live-transport-card__resource-label">
                            {t(
                              "map.live.points",
                              {
                                defaultValue:
                                  "Pontos",
                              }
                            )}
                          </span>

                          <span className="live-transport-card__resource-value">
                            {cardPoints.length
                              ? t(
                                  "map.live.pointPosition",
                                  {
                                    current:
                                      currentPointNumber,

                                    total:
                                      cardPoints.length,

                                    defaultValue:
                                      "Ponto {{current}} de {{total}}",
                                  }
                                )
                              : t(
                                  "map.live.pointCount",
                                  {
                                    count:
                                      0,

                                    defaultValue:
                                      "{{count}} pontos",
                                  }
                                )}
                          </span>
                        </div>
                      </div>
                    </div>

                    {nextPoint && (
                      <div className="live-transport-card__next-point">
                        <div className="live-transport-card__next-heading">
                          <MapPin
                            size={15}
                          />

                          <span>
                            {t(
                              "map.live.nextPoint",
                              {
                                defaultValue:
                                  "Próximo ponto",
                              }
                            )}
                          </span>
                        </div>

                        <strong className="live-transport-card__next-name">
                          {nextPoint.name ||
                            t(
                              "map.live.withoutLocation",
                              {
                                defaultValue:
                                  "Localização não definida",
                              }
                            )}
                        </strong>

                        <div className="live-transport-card__next-meta">
                          {nextPointLocation && (
                            <span>
                              {nextPointFlag && (
                                <span
                                  className="live-transport-card__flag"
                                  aria-hidden="true"
                                >
                                  {nextPointFlag}
                                </span>
                              )}

                              <span>
                                {nextPointLocation}
                              </span>
                            </span>
                          )}

                          {nextPointDateTime && (
                            <span>
                              <Clock3
                                size={15}
                              />

                              <span>
                                {nextPointDateTime}
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="live-transport-card__progress-row">
                      <span>
                        {t(
                          "map.live.progress",
                          {
                            defaultValue:
                              "Progresso",
                          }
                        )}
                      </span>

                      <strong>
                        {session.progress}%
                      </strong>
                    </div>

                    <div className="live-transport-card__progress">
                      <span
                        style={{
                          width:
                            `${session.progress}%`,
                        }}
                      />
                    </div>

                    {!!cardPoints.length && (
                      <div className="live-transport-card__completed">
                        {t(
                          "map.live.completedPoints",
                          {
                            completed:
                              completedPointCount,

                            total:
                              cardPoints.length,

                            defaultValue:
                              "{{completed}} de {{total}} pontos concluídos",
                          }
                        )}
                      </div>
                    )}
                  </button>
                );
              }
            )}
          </div>
        ) : (() => {
          const emptyMessages = {
            all: {
              titleKey: "map.live.emptyTitle",
              title: "Sem transportes atuais",
              descriptionKey: "map.live.emptyDescription",
              description:
                "As sessões aparecerão aqui automaticamente.",
            },

            active: {
              titleKey: "map.live.noActiveTitle",
              title: "Sem transportes ativos",
              descriptionKey: "map.live.noActiveDescription",
              description:
                "Não existem sessões ativas neste momento.",
            },

            delayed: {
              titleKey: "map.live.noDelayedTitle",
              title: "Sem transportes atrasados",
              descriptionKey: "map.live.noDelayedDescription",
              description:
                "Nenhuma sessão está atrasada neste momento.",
            },

            planned: {
              titleKey: "map.live.noPlannedTitle",
              title: "Sem transportes planeados",
              descriptionKey: "map.live.noPlannedDescription",
              description:
                "Não existem sessões planeadas neste momento.",
            },
          };

          const emptyMessage =
            searchTerm.trim()
              ? {
                  titleKey: "map.live.noResultsTitle",
                  title: "Nenhum transporte encontrado",
                  descriptionKey:
                    "map.live.noResultsDescription",
                  description:
                    "Experimenta outro termo de pesquisa.",
                }
              : (
                  emptyMessages[sessionFilter] ||
                  emptyMessages.all
                );

          return (
            <div
              className={[
                "map-view__sidebar-empty",
                "live-transports-map__sessions-empty",
                `is-${sessionFilter}`,
              ].join(" ")}
            >
              <div className="map-view__sidebar-empty-icon">
                <Route aria-hidden="true" />
              </div>

              <strong>
                {t(
                  emptyMessage.titleKey,
                  {
                    defaultValue:
                      emptyMessage.title,
                  }
                )}
              </strong>

              <span>
                {t(
                  emptyMessage.descriptionKey,
                  {
                    defaultValue:
                      emptyMessage.description,
                  }
                )}
              </span>
            </div>
          );
        })()}
      </aside>

      <button
        type="button"
        className="map-view__sidebar-toggle"
        onClick={() =>
          setIsSidebarCollapsed(
            (current) => !current
          )
        }
        aria-label={
          isSidebarCollapsed
            ? t(
                "map.sidebar.show",
                {
                  defaultValue:
                    "Mostrar painel",
                }
              )
            : t(
                "map.sidebar.hide",
                {
                  defaultValue:
                    "Ocultar painel",
                }
              )
        }
        title={
          isSidebarCollapsed
            ? t(
                "map.sidebar.show",
                {
                  defaultValue:
                    "Mostrar painel",
                }
              )
            : t(
                "map.sidebar.hide",
                {
                  defaultValue:
                    "Ocultar painel",
                }
              )
        }
      >
        {isSidebarCollapsed ? (
          <ChevronRight
            size={18}
            aria-hidden="true"
          />
        ) : (
          <ChevronLeft
            size={18}
            aria-hidden="true"
          />
        )}
      </button>

      <section className="map-view__canvas">
        <div className="map-base live-transports-map__map">
          <div
            ref={mapContainerRef}
            className="map-base__canvas"
          />

          {loading &&
            !sessions.length && (
              <div className="live-transports-map__map-loading">
                <RefreshCw
                  size={24}
                  className="is-spinning"
                />

                <strong>
                  {t(
                    "map.live.loadingMap",
                    {
                      defaultValue:
                        "A preparar o mapa operacional...",
                    }
                  )}
                </strong>
              </div>
            )}

          {viewMode === "trucks" && selectedTruck && (
            <div
              className="
                live-transports-map__selection
                live-transports-map__truck-route-panel
              "
              style={{
                height:
                  matchedTruckPanelHeight !== null
                    ? `${matchedTruckPanelHeight}px`
                    : undefined,
              }}
            >
              <div className="live-transports-map__selection-header">
                <span
                  className="live-transports-map__selection-hero-icon"
                  aria-hidden="true"
                >
                  <img
                    src="/icons/truck-optimized.webp"
                    alt=""
                    width={34}
                    height={27}
                  />
                </span>

                <div className="live-transports-map__selection-heading">
                  <small>Rota do camião</small>

                  <strong className="live-transports-map__selection-number">
                    {selectedTruck.plate}
                  </strong>

                  <span className="live-transports-map__truck-route-meta">
                    <User size={15} />

                    {selectedTruck.session?.driverName ||
                      "Motorista não atribuído"}

                    {selectedTruckGpsMock && (
                      <>
                        <span className="live-transports-map__truck-route-separator" />

                        {getCountryFlag({
                          countryCode: "PT",
                        })}

                        A1, Leiria, Portugal
                      </>
                    )}
                  </span>
                </div>

                <div className="live-transports-map__selection-actions">
                  {selectedTruckGpsMock ? (
                    <span className="live-transports-map__truck-route-gps is-moving">
                      <span className="live-transports-map__truck-route-gps-dot" />
                      Em movimento
                      <small>MOCK</small>
                    </span>
                  ) : (
                    <span className="live-transports-map__truck-route-gps">
                      GPS indisponível
                    </span>
                  )}

                  <button
                    type="button"
                    className="live-transports-map__selection-close"
                    aria-label="Fechar rota do camião"
                    onClick={() => {
                      setSelectedTruckPlate(null);
                    }}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="live-transports-map__truck-route-summary">
                <span className="live-transports-map__truck-route-tag">
                  <Route size={19} />

                  {selectedTruck.sessionCount}

                  {selectedTruck.sessionCount === 1
                    ? " sessão"
                    : " sessões"}
                </span>

                <div className="live-transports-map__truck-route-progress">
                  <div>
                    <span>Progresso operacional</span>

                    <strong>
                      {selectedTruckProgress.percent}%
                    </strong>
                  </div>

                  <div className="live-transports-map__truck-route-progress-track">
                    <span
                      style={{
                        width: `${selectedTruckProgress.percent}%`,
                      }}
                    />
                  </div>

                  <small>
                    {selectedTruckProgress.completed} de{" "}
                    {selectedTruckProgress.total} pontos concluídos
                  </small>
                </div>
              </div>

              <div className="live-transports-map__truck-route-sessions">
                {selectedTruck.sessions.map(
                  (truckSession, sessionIndex) => {
                    const points =
                      getSessionTimelinePoints(truckSession);

                    const origin = points[0];

                    const destination =
                      points[points.length - 1];

                    const expanded =
                      expandedTruckSessionId === null
                        ? sessionIndex === 0
                        : String(expandedTruckSessionId) ===
                          String(truckSession.id);

                    return (
                      <div
                        key={truckSession.id}
                        className={[
                          "live-transports-map__truck-route-entry",
                          expanded ? "is-expanded" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {/* CABEÇALHO DA SESSÃO */}
                        <button
                          type="button"
                          className="live-transports-map__truck-route-entry-header"
                          aria-expanded={expanded}
                          onClick={() => {
                            setExpandedTruckSessionId(
                              expanded
                                ? "__none__"
                                : String(truckSession.id)
                            );
                          }}
                        >
                          <strong>
                            {getSessionDisplayNumber(
                              truckSession
                            )}
                          </strong>

                          <span className="live-transports-map__truck-route-entry-path">
                            {getCountryFlag(origin)}

                            {origin?.city ||
                              origin?.name ||
                              "Origem"}

                            <ChevronRight size={19} />

                            {getCountryFlag(destination)}

                            {destination?.city ||
                              destination?.name ||
                              "Destino"}
                          </span>

                          <span
                            className={`live-transports-map__truck-route-entry-status is-${String(
                              truckSession.status || "planned"
                            ).toLowerCase()}`}
                          >
                            {getStatusLabel(
                              truckSession.status
                            )}
                          </span>

                          <ChevronDown
                            size={20}
                            className="live-transports-map__truck-route-entry-chevron"
                          />
                        </button>

                        {/* PARAGENS DA SESSÃO EXPANDIDA */}
                        {expanded && (
                          <div className="live-transports-map__truck-route-entry-body">
                            {points.length === 0 && (
                              <div className="live-transports-map__truck-route-empty">
                                Sem pontos disponíveis.
                              </div>
                            )}

                            {points.map(
                              (point, pointIndex) => {
                                const type = String(
                                  point.type || ""
                                ).toUpperCase();

                                const pickup =
                                  type === "PICKUP";

                                const delivery =
                                  type === "DELIVERY";

                                const completed =
                                  String(
                                    point.status || ""
                                  ).toUpperCase() ===
                                  "COMPLETED";

                                const pointName =
                                  point.name ||
                                  point.city ||
                                  point.address ||
                                  "Ponto da rota";

                                const lat =
                                  toFiniteNumberOrNull(
                                    point.lat
                                  );

                                const lng =
                                  toFiniteNumberOrNull(
                                    point.lng
                                  );

                                const hasPosition =
                                  lat !== null &&
                                  lng !== null &&
                                  Math.abs(lat) <= 90 &&
                                  Math.abs(lng) <= 180;

                                const metric = (
                                  truckSession.routeMetrics || []
                                ).find((item) => {
                                  if (
                                    point.id != null &&
                                    item.id != null &&
                                    String(item.id) ===
                                      String(point.id)
                                  ) {
                                    return true;
                                  }

                                  return (
                                    hasPosition &&
                                    Math.abs(
                                      lat - Number(item.lat)
                                    ) < 0.0004 &&
                                    Math.abs(
                                      lng - Number(item.lng)
                                    ) < 0.0004
                                  );
                                });

                                const km =
                                  toFiniteNumberOrNull(
                                    metric?.km
                                  );

                                return (
                                  <div
                                    key={`${truckSession.id}-${point.id ?? pointIndex}`}
                                    className="live-transports-map__truck-route-stop"
                                  >
                                    {/* TIMELINE */}
                                    <div className="live-transports-map__truck-route-stop-rail">
                                      <span
                                        className={
                                          completed
                                            ? "is-completed"
                                            : ""
                                        }
                                      >
                                        {completed
                                          ? "✓"
                                          : pointIndex + 1}
                                      </span>
                                    </div>

                                    {/* PIN E LOCAL */}
                                    <button
                                      type="button"
                                      className="live-transports-map__truck-route-stop-location"
                                      disabled={!hasPosition}
                                      onClick={() => {
                                        if (
                                          !hasPosition ||
                                          !mapRef.current
                                        ) {
                                          return;
                                        }

                                        const map =
                                          mapRef.current;

                                        map.stop();

                                        map.easeTo({
                                          center: [lng, lat],
                                          zoom: Math.max(
                                            map.getZoom(),
                                            12
                                          ),
                                          duration: 450,
                                        });
                                      }}
                                    >
                                      <span className="live-transports-map__truck-route-stop-pin">
                                        {pickup || delivery ? (
                                          <img
                                            src={
                                              pickup
                                                ? pickupPinImage
                                                : deliveryPinImage
                                            }
                                            alt=""
                                            draggable={false}
                                            width={32}
                                            height={43}
                                          />
                                        ) : (
                                          <MapPin size={24} />
                                        )}

                                        {(pickup || delivery) && (
                                          <small
                                            className={
                                              pickup
                                                ? "is-pickup"
                                                : "is-delivery"
                                            }
                                          >
                                            <Box size={13} />

                                            {pickup
                                              ? "Carga"
                                              : "Descarga"}
                                          </small>
                                        )}
                                      </span>

                                      <span className="live-transports-map__truck-route-stop-place">
                                        <strong>
                                          {getCountryFlag(point)}

                                          <span>
                                            {pointName}
                                          </span>
                                        </strong>

                                        <small className="live-transports-map__truck-route-place-meta">
                                          <MapPin
                                            size={17}
                                            strokeWidth={2}
                                          />

                                          <span>
                                            {getPointLocationMeta(
                                              point
                                            ) ||
                                              "Localização não definida"}
                                          </span>
                                        </small>

                                        <small className="live-transports-map__truck-route-coordinates">
                                          <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            aria-hidden="true"
                                          >
                                            <circle
                                              cx="12"
                                              cy="12"
                                              r="7"
                                            />

                                            <circle
                                              cx="12"
                                              cy="12"
                                              r="2.5"
                                            />

                                            <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
                                          </svg>

                                          <span>
                                            {hasPosition
                                              ? `${lat.toFixed(4)}, ${lng.toFixed(4)}`
                                              : "Sem coordenadas"}
                                          </span>
                                        </small>
                                      </span>
                                    </button>

                                    {/* RECURSOS DA PARAGEM */}
                                    <div className="live-transports-map__truck-route-stop-assets">
                                      {/* CAMIÃO — APENAS VISUALIZAÇÃO */}
                                      <span className="is-truck">
                                        <span className="live-transports-map__truck-route-asset-icon">
                                          <img
                                            src="/icons/truck-optimized.webp"
                                            width={28}
                                            height={21}
                                            alt=""
                                            draggable={false}
                                          />
                                        </span>

                                        <span className="live-transports-map__truck-route-asset-copy">
                                          <small>Camião</small>

                                          <strong>
                                            {point.truckPlate ||
                                              truckSession.truckPlate ||
                                              "Não atribuído"}
                                          </strong>
                                        </span>
                                      </span>

                                      {/* REBOQUE — APENAS VISUALIZAÇÃO */}
                                      <span className="is-trailer">
                                        <span className="live-transports-map__truck-route-asset-icon">
                                          <img
                                            src="/icons/trailer-optimized.webp"
                                            width={30}
                                            height={22}
                                            alt=""
                                            draggable={false}
                                          />
                                        </span>

                                        <span className="live-transports-map__truck-route-asset-copy">
                                          <small>Reboque</small>

                                          <strong>
                                            {point.trailerPlate ||
                                              truckSession.trailerPlate ||
                                              "Não atribuído"}
                                          </strong>
                                        </span>
                                      </span>

                                      {/* MOTORISTAS — APENAS VISUALIZAÇÃO */}
                                      <span className="is-drivers">
                                        <span className="live-transports-map__truck-route-driver-row">
                                          <span className="live-transports-map__truck-route-driver-identity">
                                            <User size={22} />
                                            <b>1</b>
                                          </span>

                                          <strong>
                                            {point.driverName ||
                                              truckSession.driverName ||
                                              "Atribuir"}
                                          </strong>
                                        </span>

                                        <span className="live-transports-map__truck-route-driver-row">
                                          <span className="live-transports-map__truck-route-driver-identity">
                                            <User size={22} />
                                            <b>2</b>
                                          </span>

                                          <strong>
                                            {point.driver2Name ||
                                              truckSession.driver2Name ||
                                              "Atribuir"}
                                          </strong>
                                        </span>
                                      </span>
                                    </div>

                                    {/* DADOS OPERACIONAIS */}
                                    <div className="live-transports-map__truck-route-stop-details">
                                      <div className="live-transports-map__truck-route-stop-actions">
                                        <span>
                                          <FileText size={15} />
                                          Documentos
                                        </span>

                                        <strong
                                          className={
                                            completed
                                              ? "is-completed"
                                              : ""
                                          }
                                        >
                                          <Clock3 size={15} />

                                          {getStopStatusLabel(
                                            point
                                          )}
                                        </strong>
                                      </div>

                                      <div>
                                        <CalendarDays size={15} />

                                        <span>
                                          Programado
                                        </span>

                                        <strong>
                                          {formatPointDateTime(
                                            point,
                                            locale
                                          ) || "—"}
                                        </strong>
                                      </div>

                                      <div>
                                        <MapPin size={15} />

                                        <span>
                                          Chegada estimada (ETA)
                                        </span>

                                        <strong>—</strong>
                                      </div>

                                      <div>
                                        <Route size={15} />

                                        <span>
                                          Km até ao ponto
                                        </span>

                                        <strong>
                                          {km !== null
                                            ? `${Math.round(km)} km`
                                            : "— km"}
                                        </strong>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                            )}

                            {/* ABRIR PAINEL OPERACIONAL ORIGINAL */}
                            <button
                              type="button"
                              className="live-transports-map__truck-route-open-session"
                              onClick={() => {
                                setSelectedSessionId(
                                  truckSession.id
                                );

                                setViewMode(
                                  "sessions"
                                );
                              }}
                            >
                              Abrir sessão completa

                              <ChevronRight size={16} />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>

              <button
                type="button"
                className="live-transports-map__selection-focus"
                onClick={() => {
                  if (!isSidebarCollapsed) {
                    setIsSidebarCollapsed(
                      true
                    );
                  } else {
                    focusSessionOnMap(
                      null,
                      selectedTruck.plate
                    );
                  }
                }}
              >
                <Route size={16} />

                Centrar rotas no mapa

                <ChevronRight size={16} />
              </button>
            </div>
          )}

          {viewMode === "sessions" && selectedSession && (
            <div
              className={[
                "live-transports-map__selection",
                sessionPanelMode === "simple"
                  ? "is-simple"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              style={{
                "--session-panel-width":
                  sessionPanelMode === "simple"
                    ? "500px"
                    : "835px",
                right:
                  sessionPanelMode === "simple"
                    ? "24px"
                    : "8px",
              }}



            >

              <div className="live-transports-map__selection-header">
                <span
                  className="live-transports-map__selection-hero-icon"
                  aria-hidden="true"
                >
                  <Route
                    size={27}
                    strokeWidth={2.1}
                    style={{
                      color:
                        "var(--live-route-icon-blue, #60a5fa)",
                      stroke:
                        "var(--live-route-icon-blue, #60a5fa)",
                      fill: "none",
                    }}
                  />
                </span>

                <div className="live-transports-map__selection-heading">
                  <span className="live-transports-map__selection-number">
                    {`${
                      sessionPanelMode === "simple"
                        ? "Rota"
                        : "Rota da sessão"
                    } ${Number(
                      getSessionDisplayNumber(
                        selectedSession
                      ).replace(/^TS-/i, "")
                    )}`}
                  </span>
                </div>

                <div className="live-transports-map__selection-actions">
                  <span
                    className={`live-transport-card__status is-${String(
                      selectedSession.status || "active"
                    ).toLowerCase()}`}
                  >
                    {getStatusLabel(
                      selectedSession.status
                    )}
                  </span>

                  <button
                    type="button"
                    className="live-transports-map__selection-close"
                    onClick={() =>
                      setSelectedSessionId(null)
                    }
                    aria-label="Fechar detalhes da sessão"
                    title="Fechar detalhes"
                  >
                    <X
                      size={20}
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                  </button>
                </div>

                <div
                  className="live-transports-map__selection-view-toggle"
                  role="group"
                  aria-label="Visualização da sessão"
                >
                  <button
                    type="button"
                    className={
                      sessionPanelMode === "simple"
                        ? "is-active"
                        : ""
                    }
                    aria-pressed={
                      sessionPanelMode === "simple"
                    }
                    onClick={() =>
                      changeSessionPanelMode("simple")
                    }

                  >
                    <List
                      size={15}
                      aria-hidden="true"
                    />
                    Simplificado
                  </button>

                  <button
                    type="button"
                    className={
                      sessionPanelMode === "complete"
                        ? "is-active"
                        : ""
                    }
                    aria-pressed={
                      sessionPanelMode === "complete"
                    }
                    onClick={() =>
                      changeSessionPanelMode("complete")
                    }

                  >
                    <SlidersHorizontal
                      size={15}
                      aria-hidden="true"
                    />
                    Completo
                  </button>
                </div>
              </div>

              <div className="live-transports-map__selection-route live-transports-map__selection-route--summary">
                {(() => {
                  const points =
                    getSessionTimelinePoints(
                      selectedSession
                    );

                  const origin = points[0];
                  const destination =
                    points[points.length - 1];

                  return (
                    <>
                      <span>
                        {getCountryFlag(origin)}{" "}
                        {origin?.city ||
                          origin?.name ||
                          "Origem"}
                      </span>

                      <span className="live-transports-map__selection-arrow">
                        →
                      </span>

                      <span>
                        {getCountryFlag(destination)}{" "}
                        {destination?.city ||
                          destination?.name ||
                          "Destino"}
                      </span>
                    </>
                  );
                })()}
              </div>

              <div className="live-transports-map__selection-progress">
                <div>
                  <span>
                    {sessionPanelMode === "simple"
                      ? "Progresso"
                      : "Progresso operacional"}
                  </span>

                  <strong>
                    {selectedSession.progress || 0}%
                  </strong>
                </div>

                <div className="live-transports-map__selection-progress-track">
                  <span
                    style={{
                      width: `${Math.min(
                        100,
                        Math.max(
                          0,
                          Number(
                            selectedSession.progress
                          ) || 0
                        )
                      )}%`,
                    }}
                  />
                </div>

                <small className="live-transports-map__selection-progress-count">
                  {(() => {
                    const operationalPoints =
                      selectedSession.operationalPoints?.length
                        ? selectedSession.operationalPoints
                        : selectedSession.points || [];

                    return `${calculateCompletedPointCount(
                      operationalPoints
                    )} de ${operationalPoints.length} pontos`;
                  })()}
                </small>
              </div>

              {sessionPanelMode === "simple" && (
                <div className="live-transports-map__selection-simple-metrics">
                  {(() => {
                    const metrics =
                      selectedSession.routeMetrics || [];

                    const total =
                      selectedSession.routeTotals ||
                      metrics[metrics.length - 1] ||
                      getRouteTotalsFromSummary(
                        selectedSession.routeSnapshot?.route
                      ) ||
                      null;

                    const km =
                      toFiniteNumberOrNull(total?.km);

                    const minutes =
                      toFiniteNumberOrNull(total?.minutes);

                    return (
                      <>
                        <span title="Distância total da rota">
                          <Route
                            size={17}
                            aria-hidden="true"
                          />

                          <strong>
                            {km !== null
                              ? `${Math.round(km)} km`
                              : "— km"}
                          </strong>
                        </span>

                        <span title="Condução prevista">
                          <Clock3
                            size={17}
                            aria-hidden="true"
                          />

                          <strong>
                            {formatForecastMinutes(minutes)}
                          </strong>
                        </span>
                      </>
                    );
                  })()}
                </div>
              )}

              <div
                className="live-transports-map__selection-forecast-summary"
                hidden={sessionPanelMode === "simple"}
              >
                {(() => {
                  const metrics =
                    selectedSession.routeMetrics || [];

                  const total =
                    selectedSession.routeTotals ||
                    metrics[metrics.length - 1] ||
                    getRouteTotalsFromSummary(
                      selectedSession.routeSnapshot?.route
                    ) ||
                    null;

                  const forecast = {
                    ...(planningForecast || {}),
                    ...Object.fromEntries(
                      Object.entries(
                        selectedSession.forecast || {}
                      ).filter(
                        ([, value]) =>
                          value !== null &&
                          value !== undefined
                      )
                    ),
                  };

                  const points =
                    getSessionTimelinePoints(selectedSession);

                  const finalPoint =
                    points[points.length - 1];

                  const drivingMinutes =
                    toFiniteNumberOrNull(
                      total?.minutes
                    );

                  const operationMinutes =
                    points.reduce(
                      (sum, point) =>
                        sum +
                        getPlannedOperationMinutes(
                          point
                        ),
                      0
                    );

                  const minimumElapsedMinutes =
                    drivingMinutes !== null
                      ? drivingMinutes + operationMinutes
                      : null;

                  const elapsedMinutes =
                    forecast.totalElapsedMinutes ??
                    minimumElapsedMinutes;

                  const items = [
                    {
                      label: "Distância total",
                      value:
                        toFiniteNumberOrNull(total?.km) !== null
                          ? `${Math.round(Number(total.km)).toLocaleString(locale)} km`
                          : "—",
                    },
                    {
                      label: "Condução prevista",
                      value: formatForecastMinutes(
                        drivingMinutes
                      ),
                    },
                    {
                      label: "Pausas obrigatórias",
                      value: formatForecastMinutes(
                        forecast.mandatoryBreakMinutes
                      ),
                    },
                    {
                      label: "Descansos diários",
                      value: formatForecastMinutes(
                        forecast.dailyRestMinutes
                      ),
                    },
                    {
                      label: "Carga / Descarga",
                      value: formatForecastMinutes(
                        forecast.operationsMinutes ??
                        operationMinutes
                      ),
                    },
                    {
                      label: "Espera",
                      value: formatForecastMinutes(
                        forecast.waitingMinutes
                      ),
                    },
                    {
                      label:
                        forecast.isCompleteSchedule
                          ? "Duração total"
                          : "Duração mínima",
                      value: formatForecastMinutes(
                        elapsedMinutes
                      ),
                    },
                    {
                      label: "Entrega programada",
                      value: finalPoint
                        ? formatPointDateTime(
                            finalPoint,
                            locale
                          ) || "—"
                        : "—",
                    },
                  ];

                  return (
                    <>
                      <div className="live-transports-map__forecast-metrics">
                        {items.map((item) => (
                          <div
                            className="live-transports-map__forecast-metric"
                            key={item.label}
                          >
                            <span>{item.label}</span>
                            <strong>{item.value}</strong>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>

              {sessionPanelMode === "simple" && (
                <div className="live-transports-map__selection-simple-stops">

                  <DndContext
                    sensors={stopDndSensors}
                    autoScroll={false}
                    modifiers={[
                      restrictToVerticalAxis,
                      restrictToParentElement,
                    ]}
                    collisionDetection={liveStopCollisionDetection}
                    onDragStart={handleStopDragStart}
                    onDragCancel={() => setDraggingStopId(null)}
                    onDragEnd={handleStopDragEnd}
                  >

                    <SortableContext
                      items={getSessionTimelinePoints(selectedSession).map(
                        (point, index) =>
                          String(point?.id ?? `point-${index}`)
                      )}
                      strategy={verticalListSortingStrategy}
                    >
                      {getSessionTimelinePoints(selectedSession).map(
                        (point, index) => {
                          const id = String(
                            point?.id ?? `point-${index}`
                          );
                          const type = String(
                            point?.type || ""
                          ).toUpperCase();
                          const status = String(
                            point?.status || ""
                          ).toUpperCase();
                          const isPickup = type === "PICKUP";
                          const isDelivery = type === "DELIVERY";
                          const completed = status === "COMPLETED";

                          const name =
                            point?.companyName ||
                            point?.name ||
                            point?.location ||
                            point?.city ||
                            "Ponto";

                          const postalCode = String(
                            point?.postalCode ||
                            point?.postcode ||
                            point?.zipCode ||
                            point?.placePostalCode ||
                            ""
                          ).trim();



                          const schedule = getPointScheduleHighlight(
                            point,
                            locale
                          );
                          const programmedDate =
                            schedule.dateLabel === "—"
                              ? null
                              : schedule.dayLabel !== "—"
                                ? `${schedule.dayLabel} ${schedule.monthLabel.toLowerCase()} ${schedule.yearLabel}`
                                : schedule.dateLabel;
                          const programmedHour =
                            schedule.timeLabel === "—"
                              ? null
                              : schedule.timeLabel;

                          const forecastPoint = {
                            ...(planningForecast?.points?.[index] || {}),
                            ...(point?.forecast || {}),
                          };
                          const arrival = forecastPoint.estimatedArrivalAt;
                          const arrivalDate = arrival
                            ? new Date(arrival)
                            : null;
                          const hasEta =
                            index > 0 &&
                            arrivalDate &&
                            Number.isFinite(arrivalDate.getTime());
                          const timeZone =
                            forecastPoint.timeZone ||
                            getPlanningTimeZone(point);
                          const timeZoneOptions = timeZone
                            ? { timeZone }
                            : {};
                          const etaDate = hasEta
                            ? arrivalDate.toLocaleDateString(locale, {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                                ...timeZoneOptions,
                              })
                                .replace(/\bde\b/g, "")
                                .replace(/\./g, "")
                                .replace(/\s+/g, " ")
                                .trim()
                            : null;
                          const etaHour = hasEta
                            ? arrivalDate.toLocaleTimeString(locale, {
                                hour: "2-digit",
                                minute: "2-digit",
                                hourCycle: "h23",
                                ...timeZoneOptions,
                              })
                            : null;
                          const etaLocalDate = hasEta
                            ? new Intl.DateTimeFormat("sv-SE", {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                                ...timeZoneOptions,
                              }).format(arrivalDate)
                            : null;

                          // A data agendada vem exclusivamente do ponto.
                          // Nunca usar a ETA como data programada.
                          const plannedIsoDate = String(
                            point?.date || ""
                          ).slice(0, 10);

                          const monthsPt = [
                            "Jan", "Fev", "Mar", "Abr",
                            "Mai", "Jun", "Jul", "Ago",
                            "Set", "Out", "Nov", "Dez",
                          ];

                          const dateParts = plannedIsoDate.match(
                            /^(\d{4})-(\d{2})-(\d{2})$/
                          );

                          const monthName = dateParts
                            ? monthsPt[Number(dateParts[2]) - 1]
                            : null;

                          const shortDateLabel =
                            dateParts && monthName
                              ? `${Number(dateParts[3])} ${monthName}`
                              : programmedDate;

                          // ETA independente dos dados agendados.
                          const etaDateParts = String(
                            etaLocalDate || ""
                          ).match(/^(\d{4})-(\d{2})-(\d{2})$/);

                          const etaMonthName = etaDateParts
                            ? monthsPt[Number(etaDateParts[2]) - 1]
                            : null;

                          const etaShortDate =
                            etaDateParts && etaMonthName
                              ? `${Number(etaDateParts[3])} ${etaMonthName}`
                              : etaDate;

                          const hasSchedule = Boolean(
                            programmedDate || programmedHour
                          );

                          const scheduleTooltip = [
                            hasSchedule
                              ? `Agendado: ${programmedDate || "Sem data"} · ${programmedHour || "Sem hora"}`
                              : "Sem data/hora agendadas",
                            hasEta
                              ? `ETA estimada: ${etaDate} · ${etaHour}`
                              : null,
                          ].filter(Boolean).join(" | ");



                          return (
                            <LiveSortableStop
                              key={id}
                              id={id}
                              disabled={
                                !reorderablePoints ||
                                savingStopOrder ||
                                FINAL_STATUSES.has(status) ||
                                point?.isFrozen === true
                              }
                              dragInProgress={Boolean(draggingStopId)}
                              dragScale={stopDragScale}
                              className="live-transports-map__selection-simple-row"
                            >
                              <button
                                type="button"
                                className="live-transports-map__selection-simple-stop"
                                onClick={() => focusStopOnMap(point)}
                                title="Focar esta paragem no mapa"
                              >

                                <span
                                  className={[
                                    "live-transports-map__selection-simple-index",
                                    completed ? "is-completed" : "",
                                  ].filter(Boolean).join(" ")}
                                >
                                  {completed ? "✓" : index + 1}
                                </span>

                                <span className="live-transports-map__selection-simple-pin">
                                  {isPickup || isDelivery ? (
                                    <img
                                      src={isPickup ? pickupPinImage : deliveryPinImage}
                                      alt={isPickup ? "Carga" : "Descarga"}
                                      draggable={false}
                                    />
                                  ) : (
                                    <MapPin size={20} aria-hidden="true" />
                                  )}
                                </span>

                                <span className="live-transports-map__simple-place">
                                  <strong
                                    className="live-transports-map__simple-name"
                                    title={String(name)}
                                  >
                                    {name}
                                  </strong>

                                  {(postalCode || getPointCountryCode(point)) && (
                                    <small className="live-transports-map__simple-postal">
                                      {getCountryFlag(point)}
                                      {postalCode && <span>{postalCode}</span>}
                                    </small>
                                  )}
                                </span>



                                <span
                                  className="live-transports-map__simple-schedule"
                                  title={scheduleTooltip}
                                >
                                  {hasSchedule ? (
                                    <span className="live-transports-map__simple-planned">
                                      {programmedDate ? (
                                        <span className="live-transports-map__simple-date">
                                          <CalendarDays
                                            size={18}
                                            strokeWidth={2}
                                            aria-hidden="true"
                                          />
                                          {shortDateLabel}
                                        </span>
                                      ) : (
                                        <span className="live-transports-map__simple-unplanned">
                                          Sem data
                                        </span>
                                      )}

                                      {programmedHour ? (
                                        <span className="live-transports-map__simple-time">
                                          <Clock3
                                            size={18}
                                            strokeWidth={2}
                                            aria-hidden="true"
                                          />
                                          {programmedHour}
                                        </span>
                                      ) : (
                                        <span className="live-transports-map__simple-unplanned">
                                          Sem hora
                                        </span>
                                      )}
                                    </span>
                                  ) : (
                                    <span className="live-transports-map__simple-unplanned">
                                      Sem agendamento
                                    </span>
                                  )}

                                  {hasEta && (
                                    <span
                                      className="live-transports-map__simple-eta"
                                      title={`Chegada estimada: ${etaDate} · ${etaHour}`}
                                    >
                                      ETA · {etaShortDate} {etaHour}
                                    </span>
                                  )}
                                </span>




                                <ChevronRight size={15} aria-hidden="true" />
                              </button>
                            </LiveSortableStop>
                          );
                        }
                      )}
                    </SortableContext>
                  </DndContext>
                </div>
              )}


              <div
                className="live-transports-map__selection-stops"
                ref={stopsListRef}
                hidden={sessionPanelMode === "simple"}
              >
                {(() => {
                  const points =
                    getSessionTimelinePoints(
                      selectedSession
                    );

                  const canReorderTimeline =
                    Boolean(reorderablePoints);

                  const lastFinalIndex =
                    points.reduce(
                      (last, point, index) => {
                        const status = String(
                          point?.status || ""
                        )
                          .trim()
                          .toUpperCase();

                        return FINAL_STATUSES.has(status)
                          ? index
                          : last;
                      },
                      -1
                    );

                  const currentIndex =
                    points.findIndex(
                      (point, index) => {
                        const status = String(
                          point?.status || ""
                        )
                          .trim()
                          .toUpperCase();

                        const type = String(
                          point?.type || ""
                        ).toUpperCase();

                        const passage =
                          !status &&
                          ["VIA", "STRATEGICPOINT"]
                            .includes(type);

                        return (
                          index > lastFinalIndex &&
                          !FINAL_STATUSES.has(status) &&
                          !passage
                        );
                      }
                    );

                  return (
                    <DndContext
                      sensors={stopDndSensors}
                      autoScroll={false}
                      modifiers={[
                        restrictToVerticalAxis,
                        restrictToParentElement,
                      ]}
                      collisionDetection={liveStopCollisionDetection}
                      onDragStart={handleStopDragStart}
                      onDragCancel={() => setDraggingStopId(null)}
                      onDragEnd={handleStopDragEnd}
                    >

                      <SortableContext
                        items={points.map(
                          (point, index) =>
                            String(
                              point?.id ?? `point-${index}`
                            )
                        )}
                        strategy={verticalListSortingStrategy}
                      >
                      {points.map((point, index) => {
                        const status = String(
                          point?.status || ""
                        )
                          .trim()
                          .toUpperCase();

                        const type = String(
                          point?.type || ""
                        )
                          .trim()
                          .toUpperCase();

                        const isPickup = [
                          "PICKUP",
                          "PICK_UP",
                          "LOADING",
                        ].includes(type);

                        const isDelivery = [
                          "DELIVERY",
                          "DROPOFF",
                          "DROP_OFF",
                          "UNLOADING",
                        ].includes(type);

                        const completed =
                          status === "COMPLETED";

                        const cancelled =
                          status === "CANCELLED";

                        const canAssignVehicle =
                          !FINAL_STATUSES.has(status) &&
                          point?.isFrozen !== true &&
                          ["PICKUP", "DELIVERY"].includes(type) &&
                          /^[1-9]\d*$/.test(
                            String(point?.id ?? "")
                          );

                        const openVehiclePicker = (event, kind) => {
                          event.stopPropagation();

                          if (
                            !canAssignVehicle ||
                            savingVehicle
                          ) {
                            return;
                          }

                          const load =
                            selectedSession.loads?.find(
                              (item) =>
                                String(item.id) ===
                                String(point.loadId)
                            ) || null;

                          setVehiclePicker({
                            kind,
                            point,
                            sessionId: selectedSession.id,
                            load,
                          });
                        };

                        const delayed =
                          status === "DELAYED" ||
                          String(
                            point?.substatus || ""
                          )
                            .trim()
                            .toUpperCase() === "DELAYED";

                        const passage =
                          !status &&
                          ["VIA", "STRATEGICPOINT"]
                            .includes(type);

                        const current =
                          index === currentIndex;

                        const lat =
                          toFiniteNumberOrNull(
                            point?.lat
                          );

                        const lng =
                          toFiniteNumberOrNull(
                            point?.lng
                          );

                        const hasCoordinates =
                          lat !== null &&
                          lng !== null &&
                          Math.abs(lat) <= 90 &&
                          Math.abs(lng) <= 180;

                        const pointName =
                          String(point?.name || "").trim() ||
                          String(point?.address || "")
                            .split(",")[0]
                            .trim() ||
                          String(point?.city || "").trim() ||
                          "Ponto da rota";

                        const pointCountryCode =
                          getPointCountryCode(point);

                        const dateTime =
                          formatPointDateTime(
                            point,
                            locale
                          );

                        const routeMetrics =
                          selectedSession.routeMetrics || [];

                        const findRouteMetric =
                          (target) => {
                            if (!target) return null;

                            return (
                              routeMetrics.find(
                                (metric) => {
                                  if (
                                    target.id != null &&
                                    metric.id != null &&
                                    String(target.id) ===
                                      String(metric.id)
                                  ) {
                                    return true;
                                  }

                                  const targetLat =
                                    toFiniteNumberOrNull(target.lat);
                                  const targetLng =
                                    toFiniteNumberOrNull(target.lng);

                                  return (
                                    targetLat !== null &&
                                    targetLng !== null &&
                                    Math.abs(
                                      targetLat - Number(metric.lat)
                                    ) < 0.0004 &&
                                    Math.abs(
                                      targetLng - Number(metric.lng)
                                    ) < 0.0004
                                  );
                                }
                              ) || null
                            );
                          };

                        const originPoint =
                          points[
                            Math.max(0, lastFinalIndex)
                          ];

                        const originMetric =
                          findRouteMetric(originPoint);

                        const destinationMetric =
                          findRouteMetric(point);

                        const canCalculateRemaining =
                          !completed &&
                          !cancelled &&
                          index > 0 &&
                          originMetric &&
                          destinationMetric &&
                          destinationMetric.km >= originMetric.km &&
                          destinationMetric.minutes >= originMetric.minutes;

                        const remainingKm =
                          canCalculateRemaining
                            ? Math.max(
                                0,
                                destinationMetric.km - originMetric.km
                              )
                            : null;

                        const remainingMinutes =
                          canCalculateRemaining
                            ? Math.max(
                                0,
                                Math.round(
                                  destinationMetric.minutes -
                                  originMetric.minutes
                                )
                              )
                            : null;

                        const distanceLabel =
                          completed || cancelled
                            ? "0 km"
                            : remainingKm !== null
                              ? `${Math.round(remainingKm)} km`
                              : "— km";

                        const durationLabel =
                          completed || cancelled
                            ? "0 min"
                            : remainingMinutes !== null
                              ? (() => {
                                  const hours =
                                    Math.floor(remainingMinutes / 60);

                                  const minutes =
                                    remainingMinutes % 60;

                                  return hours > 0
                                    ? `${hours}h${minutes > 0
                                        ? ` ${minutes} min`
                                        : ""}`
                                    : `${minutes} min`;
                                })()
                              : "—";

                        const forecast = {
                          ...(planningForecast?.points?.[index] || {}),
                          ...(point?.forecast || {}),
                        };

                        const forecastZone =
                          forecast.timeZone || undefined;

                        const liveKm =
                          toFiniteNumberOrNull(
                            forecast.remainingKm
                          );

                        const liveMinutes =
                          toFiniteNumberOrNull(
                            forecast.remainingMinutes
                          );

                        const delayMinutes =
                          toFiniteNumberOrNull(
                            forecast.delayMinutes
                          );

                        const hasDelay =
                          !completed &&
                          !cancelled &&
                          delayMinutes !== null &&
                          delayMinutes > 0;

                        const realArrival =
                          formatForecastInstant(
                            point?.actualArrivalAt,
                            locale,
                            forecastZone
                          );

                        const estimatedArrival =
                          formatForecastInstant(
                            forecast.estimatedArrivalAt,
                            locale,
                            forecastZone
                          );

                        const liveDistanceLabel =
                          liveKm !== null && liveKm >= 0
                            ? `${Math.round(liveKm)} km`
                            : distanceLabel;

                        // Referência da previsão:
                        // - Sem pontos concluídos: início da primeira operação.
                        // - Com pontos concluídos: fim previsto do último.
                        const referenceIndex =
                          Math.max(0, lastFinalIndex);

                        const referenceForecast = {
                          ...(planningForecast?.points?.[referenceIndex] || {}),
                          ...(points[referenceIndex]?.forecast || {}),
                        };

                        const referenceAt =
                          lastFinalIndex >= 0
                            ? referenceForecast.estimatedCompletionAt
                            : referenceForecast.estimatedStartAt;

                        const referenceMs =
                          Date.parse(referenceAt || "");

                        const arrivalMs =
                          Date.parse(
                            forecast.estimatedArrivalAt || ""
                          );

                        // Acumula operações e deslocações anteriores
                        // até à chegada ao ponto selecionado.
                        const plannedUntilArrivalMinutes =
                          index > referenceIndex &&
                          Number.isFinite(referenceMs) &&
                          Number.isFinite(arrivalMs) &&
                          arrivalMs >= referenceMs
                            ? (
                                arrivalMs - referenceMs
                              ) / 60000
                            : null;

                        const liveDurationLabel =
                          completed || cancelled
                            ? "0 min"
                            : liveMinutes !== null &&
                                liveMinutes >= 0
                              ? formatForecastMinutes(
                                  liveMinutes
                                )
                              : plannedUntilArrivalMinutes !== null
                                ? formatForecastMinutes(
                                    plannedUntilArrivalMinutes
                                  )
                                : "—";

                        // Os campos fazem parte do layout fixo.
                        // Dados indisponíveis aparecem como "—".
                        const showDistance = true;
                        const showDuration = true;

                        const hasArrivalInformation =
                          !cancelled;

                        const hasTravelEstimate =
                          !cancelled;

                        return (

                          <LiveSortableStop
                            key={point?.id ?? `point-${index}`}
                            id={String(
                              point?.id ?? `point-${index}`
                            )}
                            disabled={
                              !canReorderTimeline ||
                              savingStopOrder ||
                              FINAL_STATUSES.has(status) ||
                              point?.isFrozen === true
                            }
                            dragInProgress={Boolean(
                              draggingStopId
                            )}
                            dragScale={stopDragScale}
                            className={[
                              "live-transports-map__selection-stop",
                              completed
                                ? "is-completed"
                                : "",
                              current
                                ? "is-current"
                                : "",
                              delayed
                                ? "is-delayed"
                                : "",
                              passage
                                ? "is-passage"
                                : "",
                              index === points.length - 1
                                ? "is-last"
                                : "",
                              !canReorderTimeline ||
                              FINAL_STATUSES.has(status) ||
                              point?.isFrozen === true
                                ? "is-reorder-locked"
                                : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          >
                            <span className="live-transports-map__selection-stop-index">
                              {completed ? "✓" : index + 1}
                            </span>

                            <div
                              className="live-transports-map__selection-stop-card"
                              role="group"
                              tabIndex={hasCoordinates ? 0 : -1}
                              title={
                                hasCoordinates
                                  ? `Ver ${pointName} no mapa`
                                  : undefined
                              }
                              onKeyDown={(event) => {
                                if (
                                  event.target !== event.currentTarget ||
                                  !hasCoordinates ||
                                  !["Enter", " "].includes(event.key)
                                ) {
                                  return;
                                }

                                event.preventDefault();

                                const map = mapRef.current;

                                if (!map) return;

                                map.stop();
                                map.easeTo({
                                  center: [lng, lat],
                                  zoom: Math.max(map.getZoom(), 12),
                                  duration: 650,
                                });
                              }}
                              onClick={() => {
                                const map = mapRef.current;

                                if (!map || !hasCoordinates) {
                                  return;
                                }

                                map.stop();

                                map.easeTo({
                                  center: [lng, lat],
                                  zoom: Math.max(map.getZoom(), 12),
                                  duration: 650,
                                });
                              }}
                            >
                              <span className="live-transports-map__selection-stop-name">
                                <span
                                  className={[
                                    "live-transports-map__selection-stop-direction",
                                    isPickup
                                      ? "is-pickup"
                                      : isDelivery
                                        ? "is-delivery"
                                        : "is-waypoint",
                                  ].join(" ")}
                                  aria-hidden="true"
                                >
                                  {isPickup || isDelivery ? (
                                    <img
                                      src={
                                        isPickup
                                          ? pickupPinImage
                                          : deliveryPinImage
                                      }
                                      alt=""
                                      aria-hidden="true"
                                      draggable={false}
                                      loading="eager"
                                      decoding="async"
                                      width={34}
                                      height={48}
                                      style={{
                                        display: "block",
                                        width: "34px",
                                        height: "48px",
                                        objectFit: "contain",
                                        flexShrink: 0,
                                        pointerEvents: "none",
                                        userSelect: "none",
                                      }}
                                    />
                                  ) : (
                                    <MapPin
                                      size={18}
                                      strokeWidth={1.8}
                                    />
                                  )}
                                </span>
                                <span className="live-transports-map__selection-stop-location-details">
                                  <strong title={pointName}>
                                    <span className="live-transports-map__selection-stop-title">
                                      {pointCountryCode ? (
                                        <img
                                          className="live-transports-map__selection-stop-flag"
                                          src={`https://flagcdn.com/w40/${pointCountryCode.toLowerCase()}.png`}
                                          alt=""
                                          aria-hidden="true"
                                          width={16}
                                          height={16}
                                        />
                                      ) : null}

                                      <span className="live-transports-map__selection-stop-title-text">
                                        {pointName}
                                      </span>
                                    </span>
                                  </strong>

                                  <small className="live-transports-map__selection-stop-place-meta">
                                    <MapPin
                                      size={16}
                                      strokeWidth={2}
                                      aria-hidden="true"
                                    />
                                    <span>
                                      {getPointLocationMeta(point)}
                                    </span>
                                  </small>

                                  {hasCoordinates && (
                                    <small className="live-transports-map__selection-stop-coordinates">
                                      <svg
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-hidden="true"
                                      >
                                        <circle cx="12" cy="12" r="7" />
                                        <circle cx="12" cy="12" r="2.5" />
                                        <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
                                      </svg>
                                      <span>
                                        {lat.toFixed(4)}, {lng.toFixed(4)}
                                      </span>
                                    </small>
                                  )}
                                </span>

                                {(isPickup || isDelivery) && (
                                  <span
                                    className={[
                                      "live-transports-map__selection-stop-type",
                                      isPickup
                                        ? "is-pickup"
                                        : "is-delivery",
                                    ].join(" ")}
                                  >
                                    <Box
                                      size={13}
                                      strokeWidth={2}
                                      aria-hidden="true"
                                    />

                                    <span>
                                      {isPickup
                                        ? "Carga"
                                        : "Descarga"}
                                    </span>
                                  </span>
                                )}

                              </span>

                              <span className="live-transports-map__selection-stop-assets">
                                <button
                                  type="button"
                                  className={[
                                    "live-transports-map__selection-stop-asset",
                                    "is-vehicle-button",
                                    point?.truckPlate ||
                                    selectedSession.truckPlate
                                      ? "is-assigned is-truck"
                                      : "",
                                  ]
                                    .filter(Boolean)
                                    .join(" ")}
                                  disabled={
                                    !canAssignVehicle ||
                                    savingVehicle
                                  }
                                  onClick={(event) =>
                                    openVehiclePicker(event, "truck")
                                  }
                                  title="Selecionar camião"
                                  aria-label="Selecionar camião"
                                >
                                  <span className="live-transports-map__selection-stop-asset-icon">
                                    <img
                                      src="/icons/truck-optimized.webp"
                                      className="live-transports-map__selection-truck-image"
                                      width={24}
                                      height={18}
                                      alt=""
                                      aria-hidden="true"
                                      draggable={false}
                                      loading="eager"
                                      decoding="async"
                                    />
                                  </span>

                                  <span className="live-transports-map__selection-stop-asset-copy">
                                    <small>Camião</small>
                                    <strong>
                                      {point?.truckPlate ||
                                        selectedSession.truckPlate ||
                                        "Não atribuído"}
                                    </strong>
                                  </span>
                                </button>

                                <button
                                  type="button"
                                  className={[
                                    "live-transports-map__selection-stop-asset",
                                    "is-vehicle-button",
                                    point?.trailerPlate ||
                                    selectedSession.trailerPlate
                                      ? "is-assigned is-trailer"
                                      : "",
                                  ]
                                    .filter(Boolean)
                                    .join(" ")}
                                  disabled={
                                    !canAssignVehicle ||
                                    savingVehicle
                                  }
                                  onClick={(event) =>
                                    openVehiclePicker(event, "trailer")
                                  }
                                  title="Selecionar reboque"
                                  aria-label="Selecionar reboque"
                                >
                                  <span className="live-transports-map__selection-stop-asset-icon">
                                    <img
                                      src="/icons/trailer-optimized.webp"
                                      className="live-transports-map__selection-trailer-image"
                                      width={28}
                                      height={20}
                                      alt=""
                                      aria-hidden="true"
                                      draggable={false}
                                      loading="eager"
                                      decoding="async"
                                    />
                                  </span>

                                  <span className="live-transports-map__selection-stop-asset-copy">
                                    <small>Reboque</small>
                                    <strong>
                                      {point?.trailerPlate ||
                                        selectedSession.trailerPlate ||
                                        "Não atribuído"}
                                    </strong>
                                  </span>
                                </button>

                                <span className="live-transports-map__selection-stop-asset is-driver-pair">
                                  {[1, 2].map((slot) => {
                                    const driverName =
                                      slot === 1
                                        ? point?.driverName
                                        : point?.driver2Name;

                                    const driverId =
                                      slot === 1
                                        ? point?.driverId
                                        : point?.driver2Id;

                                    const isAssigned =
                                      Boolean(driverName || driverId);

                                    const isLocked =
                                      completed ||
                                      cancelled ||
                                      point?.isFrozen === true ||
                                      !(isPickup || isDelivery) ||
                                      !/^[1-9]\d*$/.test(
                                        String(point?.id ?? "")
                                      );

                                    return (
                                      <button
                                        key={slot}
                                        type="button"
                                        className={[
                                          "live-transports-map__selection-driver-slot",
                                          isAssigned ? "is-assigned" : "",
                                        ]
                                          .filter(Boolean)
                                          .join(" ")}
                                        disabled={
                                          isLocked || savingDriver
                                        }
                                        title={`Motorista ${slot}: ${
                                          driverName || "Não atribuído"
                                        }`}
                                        aria-label={`Motorista ${slot}: ${
                                          driverName ||
                                          (isAssigned
                                            ? "Atribuído"
                                            : "Não atribuído")
                                        }`}
                                        onClick={(event) => {
                                          event.stopPropagation();

                                          if (isLocked || savingDriver) {
                                            return;
                                          }

                                          const load =
                                            selectedSession.loads?.find(
                                              (item) =>
                                                String(item.id) ===
                                                String(point.loadId)
                                            ) || null;

                                          setDriverPicker({
                                            point,
                                            slot,
                                            sessionId:
                                              selectedSession.id,
                                            load,
                                          });
                                        }}
                                      >
                                        <User
                                          size={16}
                                          strokeWidth={2}
                                          className="live-transports-map__selection-driver-slot-icon"
                                          aria-hidden="true"
                                        />

                                        <span
                                          className="live-transports-map__selection-driver-slot-number"
                                          aria-hidden="true"
                                        >
                                          {slot}
                                        </span>

                                        <span className="live-transports-map__selection-driver-slot-copy">
                                          <strong>
                                            {driverName ||
                                              (isAssigned
                                                ? "Atribuído"
                                                : "Atribuir")}
                                          </strong>
                                        </span>

                                        <ChevronDown
                                          size={11}
                                          aria-hidden="true"
                                        />
                                      </button>
                                    );
                                  })}
                                </span>

                              </span>

                              <div className="live-transports-map__selection-stop-status">
                                <div
                                  className="live-transports-map__selection-stop-documents"
                                  role={
                                    isPickup || isDelivery
                                      ? "button"
                                      : undefined
                                  }
                                  tabIndex={
                                    isPickup || isDelivery
                                      ? 0
                                      : -1
                                  }
                                  aria-label="Ver fotografias e documentos CMR"
                                  onClick={(event) => {
                                    event.stopPropagation();

                                    if (isPickup || isDelivery) {
                                      void openStopPhotos(point);
                                    }
                                  }}
                                  onKeyDown={(event) => {
                                    if (
                                      event.key !== "Enter" &&
                                      event.key !== " "
                                    ) {
                                      return;
                                    }

                                    event.preventDefault();
                                    event.stopPropagation();

                                    if (isPickup || isDelivery) {
                                      void openStopPhotos(point);
                                    }
                                  }}
                                  style={{
                                    cursor:
                                      isPickup || isDelivery
                                        ? "pointer"
                                        : "default",
                                  }}
                                >
                                  <FileText
                                    size={17}
                                    strokeWidth={1.9}
                                    aria-hidden="true"
                                  />

                                  <span>Docs</span>

                                </div>

                                <span
                                  className={[
                                    "live-transports-map__selection-stop-state",
                                    completed
                                      ? "is-completed"
                                      : cancelled
                                        ? "is-cancelled"
                                        : delayed
                                          ? "is-delayed"
                                          : passage
                                            ? "is-passage"
                                            : "is-pending",
                                  ].join(" ")}
                                  role={
                                    isPickup || isDelivery
                                      ? "button"
                                      : undefined
                                  }
                                  tabIndex={
                                    isPickup || isDelivery
                                      ? 0
                                      : -1
                                  }
                                  aria-label="Consultar ou alterar o estado da paragem"
                                  onClick={(event) => {
                                    event.stopPropagation();

                                    if (isPickup || isDelivery) {
                                      void openStopStatus(point);
                                    }
                                  }}
                                  onKeyDown={(event) => {
                                    if (
                                      event.key !== "Enter" &&
                                      event.key !== " "
                                    ) {
                                      return;
                                    }

                                    event.preventDefault();
                                    event.stopPropagation();

                                    if (isPickup || isDelivery) {
                                      void openStopStatus(point);
                                    }
                                  }}
                                  style={{
                                    cursor:
                                      isPickup || isDelivery
                                        ? "pointer"
                                        : "default",
                                  }}
                                >
                                  <span
                                    className="live-transports-map__selection-stop-state-icon"
                                    aria-hidden="true"
                                  >
                                    {completed ? (
                                      "✓"
                                    ) : cancelled ? (
                                      <X size={13} strokeWidth={2.8} />
                                    ) : delayed ? (
                                      <AlertCircle size={13} strokeWidth={2.4} />
                                    ) : passage ? (
                                      <MapPin size={13} strokeWidth={2.4} />
                                    ) : (
                                      <Clock3 size={13} strokeWidth={2.4} />
                                    )}
                                  </span>

                                  <span className="live-transports-map__selection-stop-state-label">
                                    {getStopStatusLabel(point)}
                                  </span>
                                </span>

                                <div className="live-transports-map__selection-stop-facts is-forecast">
                                  <div className="live-transports-map__forecast-line">
                                    <span>
                                      <CalendarDays size={15} />
                                      Programado
                                    </span>

                                    <strong>
                                      {dateTime || "—"}
                                    </strong>
                                  </div>

                                  {hasArrivalInformation && (
                                    <div className="live-transports-map__forecast-line">
                                      <span>
                                        <MapPin size={15} />

                                        {completed
                                          ? "Chegada real"
                                          : "ETA"}

                                      </span>

                                      <strong>
                                        {completed
                                          ? realArrival
                                          : estimatedArrival}
                                      </strong>
                                    </div>
                                  )}

                                  {hasTravelEstimate && (
                                    <div
                                      className="live-transports-map__forecast-remaining"
                                      title={
                                        liveKm === null ||
                                        liveMinutes === null
                                          ? "Valores da rota entre paragens; não são GPS em tempo real."
                                          : "Previsão atual recebida do serviço de forecast."
                                      }
                                    >
                                      {showDistance && (
                                        <span>
                                          <Route size={15} />

                                          <small>
                                            Km restantes
                                          </small>

                                          <strong>
                                            {liveDistanceLabel}
                                          </strong>
                                        </span>
                                      )}

                                      {showDuration && (
                                        <span>
                                          <Clock3 size={15} />

                                          <small>
                                            Até chegar
                                          </small>

                                          <strong>
                                            {liveDurationLabel}
                                          </strong>
                                        </span>
                                      )}
                                    </div>
                                  )}

                                  {!completed &&
                                    !cancelled &&
                                    !hasArrivalInformation &&
                                    !hasTravelEstimate && (
                                      <div className="live-transports-map__forecast-unavailable">
                                        Previsão ainda indisponível
                                      </div>
                                    )}
                                </div>

                                {hasDelay && (
                                  <div
                                    className="live-transports-map__selection-stop-delay-alert"
                                    role="status"
                                  >
                                    <AlertTriangle
                                      size={17}
                                      strokeWidth={2.4}
                                      aria-hidden="true"
                                    />

                                    <strong>
                                      Atraso estimado ·{" "}
                                      {formatForecastMinutes(
                                        delayMinutes
                                      )}
                                    </strong>
                                  </div>
                                )}
                              </div>

                              <ChevronRight
                                size={18}
                                className="live-transports-map__selection-stop-chevron"
                                aria-hidden="true"
                              />

                            </div>
                          </LiveSortableStop>
                        );
                      })}
                      </SortableContext>
                    </DndContext>
                  );
                })()}
              </div>

              <button
                type="button"
                className="live-transports-map__selection-focus"
                onClick={() => {
                  if (!isSidebarCollapsed) {
                    setIsSidebarCollapsed(true);
                    return;
                  }

                  focusSessionOnMap(
                    selectedSession.id
                  );
                }}
              >
                <Route size={16} />
                Centrar rota no mapa
                <ChevronRight size={16} />
              </button>
            </div>
          )}

        </div>
      </section>
    </div>
      )}

      {driverPicker && (
        <DriverSelectorModal
          isOpen={true}
          driverSlot={driverPicker.slot}
          onClose={() => {
            if (!savingDriver) {
              setDriverPicker(null);
            }
          }}
          setIsDriverUpdating={() => {}}
          selectedType={driverPicker.point.type}
          selectedPickupDelivery={driverPicker.point}
          currentLoad={driverPicker.load}
          title={`Selecionar Motorista ${driverPicker.slot}`}
          allPickups={(
            selectedSession?.timelinePoints?.length
              ? selectedSession.timelinePoints
              : selectedSession?.operationalPoints || []
          ).filter(
            (item) =>
              String(item.type).toUpperCase() === "PICKUP" &&
              item.loadId != null &&
              String(item.loadId) ===
                String(driverPicker.point.loadId)
          )}
          allDeliveries={(
            selectedSession?.timelinePoints?.length
              ? selectedSession.timelinePoints
              : selectedSession?.operationalPoints || []
          ).filter(
            (item) =>
              String(item.type).toUpperCase() === "DELIVERY" &&
              item.loadId != null &&
              String(item.loadId) ===
                String(driverPicker.point.loadId)
          )}
          onSelect={async (
            driver,
            _selectedType,
            _alsoAssignToDeparture,
            slotFromModal = driverPicker.slot
          ) => {
            if (!driver?.id || savingDriver) return;

            setSavingDriver(true);

            try {
              const pointId = driverPicker.point.id;

              const response = await api.get(
                `/pickupdeliveries/by-id/${pointId}`
              );

              const freshPoint =
                response.data?.pickupDelivery ??
                response.data?.pd ??
                response.data?.row ??
                response.data;

              if (
                !freshPoint ||
                String(freshPoint.id) !== String(pointId)
              ) {
                throw new Error(
                  "Não foi possível confirmar a paragem."
                );
              }

              const freshSessionId =
                getTransportSessionId(freshPoint);

              if (
                freshSessionId != null &&
                String(freshSessionId) !==
                  String(driverPicker.sessionId)
              ) {
                throw new Error(
                  "A paragem já não pertence a esta sessão."
                );
              }

              if (
                FINAL_STATUSES.has(
                  String(freshPoint.status || "").toUpperCase()
                ) ||
                freshPoint.isFrozen === true
              ) {
                throw new Error(
                  "Não é possível alterar motoristas numa paragem bloqueada."
                );
              }

              const type = String(
                freshPoint.type || ""
              ).toUpperCase();

              if (!["PICKUP", "DELIVERY"].includes(type)) {
                throw new Error(
                  "Este ponto não permite esta atribuição."
                );
              }

              const driverSlot =
                Number(slotFromModal) === 2 ? 2 : 1;

              await api.put(
                `/pickupdeliveries/${pointId}/assign-driver`,
                {
                  driverId: driver.id,
                  type: type.toLowerCase(),
                  driverSlot,
                }
              );

              await refreshSessions({ silent: true });

              setDriverPicker(null);
            } catch (error) {
              console.error(
                "[LiveTransportsMap] Driver assignment:",
                error
              );

              window.alert(
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "Não foi possível atribuir o motorista."
              );
            } finally {
              setSavingDriver(false);
            }
          }}
        />
      )}

      {vehiclePicker?.kind === "truck" && (
        <TruckSelectorModal
          isOpen={true}
          onClose={() => {
            if (!savingVehicle) {
              setVehiclePicker(null);
            }
          }}
          onSelect={handleVehicleSelect}
          selectedType={vehiclePicker.point.type}
          selectedPickupDelivery={vehiclePicker.point}
          currentLoad={vehiclePicker.load}
          title="Selecionar camião"
          allPickups={(
            selectedSession?.timelinePoints?.length
              ? selectedSession.timelinePoints
              : selectedSession?.operationalPoints || []
          ).filter(
            (item) =>
              String(item.type).toUpperCase() === "PICKUP" &&
              item.loadId != null &&
              String(item.loadId) ===
                String(vehiclePicker.point.loadId)
          )}
          allDeliveries={(
            selectedSession?.timelinePoints?.length
              ? selectedSession.timelinePoints
              : selectedSession?.operationalPoints || []
          ).filter(
            (item) =>
              String(item.type).toUpperCase() === "DELIVERY" &&
              item.loadId != null &&
              String(item.loadId) ===
                String(vehiclePicker.point.loadId)
          )}
        />
      )}

      {vehiclePicker?.kind === "trailer" && (
        <TrailerSelectorModal
          isOpen={true}
          onClose={() => {
            if (!savingVehicle) {
              setVehiclePicker(null);
            }
          }}
          onSelect={handleVehicleSelect}
          selectedPickupDelivery={vehiclePicker.point}
          currentLoad={vehiclePicker.load}
          title="Selecionar reboque"
          selectedType={String(
            vehiclePicker.point.type || ""
          ).toUpperCase() === "DELIVERY"
            ? "delivery"
            : "pickup"}
          mode="single"
        />
      )}

      <ModalPortal>
        {selectedStopForCmr != null && (
          <CMRLoadModal
            isOpen={true}
            pickupDeliveryId={selectedStopForCmr}
            onClose={async () => {
              setSelectedStopForCmr(null);

              try {
                await refreshSessions({
                  silent: true,
                });
              } catch (error) {
                console.error(
                  "[LiveTransportsMap] CMR refresh failed:",
                  error
                );
              }
            }}
          />
        )}

        {selectedStopForStatus && (
          <StatusSelectorModal
            isOpen={true}
            currentStatus={selectedStopForStatus.status}
            type={selectedStopForStatus.type}
            locationName={
              selectedStopForStatus.place?.location ||
              selectedStopForStatus.location
            }
            onClose={() => {
              if (!savingStopStatus) {
                setSelectedStopForStatus(null);
              }
            }}
            onSelect={async (newStatus) => {
              if (savingStopStatus) return;

              setSavingStopStatus(true);

              try {
                const freshPoint =
                  await getFreshOperationalStop(
                    selectedStopForStatus
                  );

                if (
                  freshPoint.isFrozen === true ||
                  freshPoint.isStatusLocked === true ||
                  freshPoint.isLocked === true
                ) {
                  throw new Error(
                    "O estado desta paragem está bloqueado."
                  );
                }

                const pointId = freshPoint.id;

                const type = String(
                  freshPoint.type || ""
                ).toUpperCase();

                const nextStatus = String(
                  newStatus || ""
                )
                  .trim()
                  .toUpperCase();

                if (!nextStatus) {
                  throw new Error(
                    "Seleciona um estado válido."
                  );
                }

                if (
                  type !== "STRATEGICPOINT" &&
                  nextStatus !== "PENDING"
                ) {
                  const hasTruck =
                    freshPoint.truckId != null ||
                    Boolean(
                      String(
                        freshPoint.truckPlate || ""
                      ).trim()
                    );

                  const hasDriver =
                    freshPoint.driverId != null ||
                    Boolean(
                      String(
                        freshPoint.driverName || ""
                      ).trim()
                    );

                  if (!hasTruck || !hasDriver) {
                    throw new Error(
                      "É necessário ter camião e motorista atribuídos."
                    );
                  }
                }

                /*
                 * A última descarga tem um fluxo especial:
                 * estado → CMR → inventário → finalização.
                 *
                 * Não concluir apenas com PUT /status.
                 */
                if (
                  type === "DELIVERY" &&
                  ["COMPLETED", "CANCELLED"].includes(
                    nextStatus
                  )
                ) {
                  const operationalPoints =
                    selectedSession?.timelinePoints?.length
                      ? selectedSession.timelinePoints
                      : selectedSession?.operationalPoints || [];

                  const deliveriesOfLoad =
                    operationalPoints
                      .filter(
                        (item) =>
                          String(
                            item.type || ""
                          ).toUpperCase() === "DELIVERY" &&
                          String(item.loadId) ===
                            String(freshPoint.loadId)
                      )
                      .sort(
                        (a, b) =>
                          Number(a.sequence || 0) -
                          Number(b.sequence || 0)
                      );

                  const lastDelivery =
                    deliveriesOfLoad[
                      deliveriesOfLoad.length - 1
                    ];

                  if (
                    !lastDelivery ||
                    String(lastDelivery.id) ===
                      String(pointId)
                  ) {
                    throw new Error(
                      "A última descarga exige o fluxo completo de CMR e inventário. Conclui esta operação no painel de pontos da sessão."
                    );
                  }
                }

                await api.put(
                  `/pickupdeliveries/${pointId}/status`,
                  {
                    status: nextStatus,
                  }
                );

                setSelectedStopForStatus(null);

                await refreshSessions({
                  silent: true,
                });
              } catch (error) {
                console.error(
                  "[LiveTransportsMap] Status update failed:",
                  error
                );

                window.alert(
                  error?.response?.data?.message ||
                  error?.response?.data?.error ||
                  error?.message ||
                  "Não foi possível atualizar o estado."
                );
              } finally {
                setSavingStopStatus(false);
              }
            }}
          />
        )}
      </ModalPortal>

      {scheduleConflict && (
        <MapReorderScheduleConflict
          data={scheduleConflict}
          saving={savingStopOrder}
          onCancel={() => {
            if (savingStopOrder) return;
            setScheduleConflict(null);
          }}
          onSave={saveResolvedStopOrder}
        />
      )}
    </>
  );
};

export default LiveTransportsMap;